// src/admin/pages/AdminAnalytics.js
import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
  FaShoppingCart,
  FaUsers,
  FaDollarSign,
  FaChartLine,
  FaBox,
  FaSearch,
} from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getAdminAnalytics } from "../../services/adminAnalytics.api";
import { FaCalendarAlt } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { FaExchangeAlt } from "react-icons/fa";
import { ChartBarIcon as ChartSolid } from "@heroicons/react/24/solid";
import {
  getAdminAnalyticsCards,
  searchProductOverallSales,
} from "../../services/adminAnalyticsCards.api";

// Chart.js imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
);



function AdminAnalytics() {
  const modalRef = React.useRef(null);
  const pageSafeClass =
  "w-full max-w-full min-w-0";

const cardSafeClass =
  "w-full max-w-full min-w-0 overflow-visible";

const contentSafeClass =
  "w-full max-w-full min-w-0 overflow-x-clip";

  const skipNextCardsAutoLoadRef = React.useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const { lang } = useContext(AdminLanguageContext);

  const navigate = useNavigate();

  const location = useLocation();
const reportsStateAppliedRef = React.useRef(false);

  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);

  const [showSimpleCalendar, setShowSimpleCalendar] = useState(false);

const [compareStart, setCompareStart] = useState(null);
const [compareEnd, setCompareEnd] = useState(null);

const [compareOffset, setCompareOffset] = useState(0);

const [currency, setCurrency] = useState("USD");// LBP | USD
const [exchangeRate, setExchangeRate] = useState(89000); // default
const [showCurrencyModal, setShowCurrencyModal] = useState(false);
const [tempRate, setTempRate] = useState("");

  const [showCompareDropdown, setShowCompareDropdown] = useState(false);
const [compareType, setCompareType] = useState("none");

const [appliedRange, setAppliedRange] = useState(null);
// { start: Date, end: Date }

const [dateView, setDateView] = useState("main");
// main | last | quarters

const [showCustomCompareModal, setShowCustomCompareModal] = useState(false);

const [activeTooltip, setActiveTooltip] = useState(null);

const [activeDeviceTooltip, setActiveDeviceTooltip] = useState(null);

// ✅ MAIN RANGE (used everywhere in calendar + UI)
const [rangeStart, setRangeStart] = useState(null);
const [rangeEnd, setRangeEnd] = useState(null);

// ✅ FINAL APPLIED RANGE (for button label)
const [mainRange, setMainRange] = useState(null);

// ✅ COMPARE RANGE (second button)
const [compareRange, setCompareRange] = useState(null);

  const [metric, setMetric] = useState("sales"); 
// sales | orders | customers | sessions


const [dayOffset, setDayOffset] = useState(0);
const [yearOffset, setYearOffset] = useState(0);

const [showDateModal, setShowDateModal] = useState(false);
const [dateTab, setDateTab] = useState("today"); 
// today | last | quarters | custom

const [productPublicId, setProductPublicId] = useState("");
const [productResult, setProductResult] = useState(null);

const [showExport, setShowExport] = useState(false);
const [exportType, setExportType] = useState("today");
const [exportFrom, setExportFrom] = useState("");
const [exportTo, setExportTo] = useState("");
const [exportData, setExportData] = useState(null);

const [activeQuickRange, setActiveQuickRange] = useState(null);
// "today" | "yesterday" | "last" | "quarter" | "custom"

const [showCompareCalendar, setShowCompareCalendar] = useState(false);

// MAIN CALENDAR (top-left button)
const [mainStart, setMainStart] = useState(null);
const [mainEnd, setMainEnd] = useState(null);

const [cardsData, setCardsData] = useState(null);
const [showProductSalesSearch, setShowProductSalesSearch] = useState(false);
const [productSalesSearchQuery, setProductSalesSearchQuery] = useState("");
const [productSalesSearchResults, setProductSalesSearchResults] = useState([]);
const [productSalesSearchLoading, setProductSalesSearchLoading] = useState(false);
const [productSalesSearchError, setProductSalesSearchError] = useState("");

// COMPARE CALENDAR (custom compare modal)
const [compareStartDate, setCompareStartDate] = useState(null);
const [compareEndDate, setCompareEndDate] = useState(null);

const [selectedDate, setSelectedDate] = useState(new Date());

// ✅ Keeps calendar visible month stable while selecting dates
const [calendarBaseDate, setCalendarBaseDate] = useState(new Date());

const [calendarOffset, setCalendarOffset] = useState(0);

const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";

const canAccess = isAdmin || perms.can_view_analytics;


const resetDateModal = () => {
  setDateView("main");
  setDateTab("today");

  setActiveQuickRange(null);

  setRangeStart(null);
  setRangeEnd(null);

  setMainStart(null);        // ✅ ADD
  setMainEnd(null);          // ✅ ADD
  setMainRange(null);        // ✅ ADD

  setCompareRange(null);     // ✅ ADD
  setCompareStartDate(null); // ✅ ADD
  setCompareEndDate(null);   // ✅ ADD

  const today = new Date();

setSelectedDate(today);
setCalendarBaseDate(today);
setCalendarOffset(0);
};

const applyRange = (start, end) => {
  setMainStart(start);
  setMainEnd(end);

  setMainRange({ start, end });

  setSelectedDate(end);

  // ✅ Only reset visible month when applying quick ranges,
  // not while clicking inside calendar days.
  setCalendarBaseDate(end);
  setCalendarOffset(0);
};

const resetToToday = () => {
  const today = new Date();

  setDateView("main");
  setDateTab("today");
  setActiveQuickRange(null);

  setRangeStart(null);
  setRangeEnd(null);

  setMainStart(today);
  setMainEnd(today);
  setMainRange(null);

  setSelectedDate(today);
setCalendarBaseDate(today);
setCalendarOffset(0);

  loadCards(today, today);
};

const applyCompareRange = (start, end) => {
  setCompareStartDate(start);
  setCompareEndDate(end);
};

const getDailyTitle = () => {
  const d = new Date();
  d.setDate(d.getDate() - dayOffset);

  if (dayOffset === 0) {
    return lang === "AR" ? "اليوم" : "Today";
  }

  if (dayOffset === 1) {
    return lang === "AR" ? "أمس" : "Yesterday";
  }

  const weekday = d.toLocaleDateString(
    lang === "AR" ? "ar" : "en",
    { weekday: "long" }
  );

  const date = d.toLocaleDateString(
    lang === "AR" ? "ar" : "en",
    { day: "numeric", month: "short" }
  );

  return `${weekday} (${date})`;
};

const handleAnalyticsFullPageExport = async (format) => {
  const page = document.getElementById("admin-analytics-page");
  if (!page) return;

  // ✅ Export only the main analytics content.
  // This automatically excludes AdminSidebar and AdminHeader.
  const mainContent = page.querySelector("main");
  if (!mainContent) return;

  const clonedPage = mainContent.cloneNode(true);

  // ✅ Preserve Chart.js canvas charts
    // ✅ Preserve Chart.js canvas charts with stable dimensions
  const originalCanvases = mainContent.querySelectorAll("canvas");
  const clonedCanvases = clonedPage.querySelectorAll("canvas");

  originalCanvases.forEach((canvas, index) => {
    try {
      const rect = canvas.getBoundingClientRect();

      const image = document.createElement("img");
      image.src = canvas.toDataURL("image/png", 1);

            image.style.display = "block";
      image.style.width = `${Math.max(rect.width, 80)}px`;
      image.style.maxWidth = "100%";
      image.style.height = `${Math.max(rect.height, 80)}px`;
      image.style.objectFit = "contain";
      image.style.objectPosition = "center";
      image.style.boxSizing = "border-box";

      if (clonedCanvases[index]) {
        clonedCanvases[index].replaceWith(image);
      }
    } catch (err) {
      console.error("Could not copy chart for export", err);
    }
  });

  // ✅ Remove export modal/buttons from exported page
  clonedPage.querySelectorAll(".analytics-export-no-print").forEach((el) => {
    el.remove();
  });

    // ✅ Remove only real controls, but keep card-title buttons as plain text
  clonedPage.querySelectorAll("button").forEach((btn) => {
    if (btn.closest(".analytics-export-no-print")) {
      btn.remove();
      return;
    }

    // Keep clickable report/card titles visible in export
    const text = btn.textContent?.trim();

    if (text) {
      const span = document.createElement("span");
      span.textContent = text;
      span.className = btn.className;
      span.style.cursor = "default";
      span.style.color = "#111827";
      span.style.fontWeight = "600";
      span.style.textDecoration = "none";
      span.style.display = "inline-block";

      btn.replaceWith(span);
    } else {
      btn.remove();
    }
  });

  clonedPage.querySelectorAll("input, select, textarea").forEach((el) => {
    el.remove();
  });

    const EXPORT_WIDTH = 1440;

  // ✅ Make cloned page desktop-sized so xl:grid layouts stay stable
  clonedPage.classList.add("analytics-export-page");
  clonedPage.style.width = `${EXPORT_WIDTH}px`;
  clonedPage.style.maxWidth = `${EXPORT_WIDTH}px`;
  clonedPage.style.minWidth = `${EXPORT_WIDTH}px`;
  clonedPage.style.minHeight = "auto";
  clonedPage.style.overflow = "visible";
  clonedPage.style.background = "#f1f1f1";
  clonedPage.style.padding = "24px";
  clonedPage.style.boxSizing = "border-box";

  // ✅ Prevent cloned Tailwind overflow/fixed-height issues
  clonedPage.querySelectorAll("*").forEach((el) => {
    el.style.boxSizing = "border-box";
  });


    // ✅ Hide hover tooltips in export because they are absolute and can mess layout
  clonedPage.querySelectorAll(
    ".pointer-events-none, [class*='group-hover'], #chart-tooltip"
  ).forEach((el) => {
    el.remove();
  });

  // ✅ Keep cards stable, but do not destroy their internal layout
  clonedPage.querySelectorAll(".bg-white.border.border-gray-200.rounded-xl").forEach((card) => {
    card.style.overflow = "hidden";
    card.style.breakInside = "avoid";
    card.style.pageBreakInside = "avoid";
  });

  // ✅ Make chart image wrappers stable
  clonedPage.querySelectorAll("img").forEach((img) => {
    img.style.maxWidth = "100%";
    img.style.objectFit = "contain";
  });

  // ✅ Keep chart containers inside cards
  clonedPage.querySelectorAll("img").forEach((img) => {
    img.style.maxWidth = "100%";
    img.style.objectFit = "contain";
  });

  if (format === "pdf") {
    const wrapper = document.createElement("div");

    wrapper.id = "analytics-pdf-wrapper";
    wrapper.style.position = "fixed";
    wrapper.style.left = "0";
    wrapper.style.top = "0";
        wrapper.style.width = `${EXPORT_WIDTH}px`;
    wrapper.style.background = "#f1f1f1";
    wrapper.style.zIndex = "-1";
    wrapper.style.opacity = "1";
    wrapper.style.pointerEvents = "none";

    wrapper.appendChild(clonedPage);
    document.body.appendChild(wrapper);

    try {
       const canvas = await html2canvas(wrapper, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f1f1f1",
        windowWidth: EXPORT_WIDTH,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `analytics-${formatLocalDate(mainStart || new Date())}-to-${formatLocalDate(
          mainEnd || mainStart || new Date()
        )}.pdf`
      );
    } catch (err) {
      console.error("Analytics PDF export failed", err);
    } finally {
      document.body.removeChild(wrapper);
      setShowExport(false);
    }

    return;
  }

  // ✅ Print only
    const printWindow = window.open("", "", `width=${EXPORT_WIDTH},height=900`);
  if (!printWindow) return;

  printWindow.document.write(`
    <html dir="${lang === "AR" ? "rtl" : "ltr"}">
      <head>
        <title>${TEXT.title} - Print</title>
        ${document.head.innerHTML}

                <style>
          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          body {
            padding: 0;
          }

          #print-area {
            width: 1440px !important;
            max-width: 1440px !important;
            min-width: 1440px !important;
            margin: 0 auto !important;
            overflow: visible !important;
            background: #f1f1f1 !important;
            box-sizing: border-box !important;
          }

          #print-area,
          #print-area * {
            box-sizing: border-box !important;
          }

          #print-area .analytics-export-no-print {
            display: none !important;
          }

                    #print-area .pointer-events-none,
          #print-area [class*="group-hover"],
          #chart-tooltip {
            display: none !important;
          }

          #print-area .bg-white.border.border-gray-200.rounded-xl {
            overflow: hidden !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }

          #print-area img {
            max-width: 100% !important;
            object-fit: contain !important;
          }

          #print-area img {
            max-width: 100% !important;
            object-fit: contain !important;
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 8mm;
            }

            body {
              zoom: 0.58;
              background: #ffffff !important;
            }

            #print-area {
              background: #f1f1f1 !important;
            }
          }
        </style>
      </head>

      <body>
        <div id="print-area">
          ${clonedPage.outerHTML}
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();

  setShowExport(false);

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

const formatLocalDate = (date) => {
  if (!date) return undefined;

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
};

const loadCards = async (
  start = mainStart,
  end = mainEnd,
  compare = compareRange
) => {
  try {
    const res = await getAdminAnalyticsCards({
      from: start ? formatLocalDate(start) : undefined,
      to: end ? formatLocalDate(end) : undefined,
      compareFrom: compare?.start ? formatLocalDate(compare.start) : undefined,
      compareTo: compare?.end ? formatLocalDate(compare.end) : undefined,
    });

    setCardsData(res);
  } catch (e) {
    console.error(e);
  }
};
  const TEXT = {
  EN: {
    value: "Value",
monthLabel: "Month",
cohortLabel: "Cohort",
atLabel: "at",
aovShort: "AOV",
sessionsLower: "sessions",
saveBtn: "Save",
todayLabel: "Today",
reset: "Reset",
yesterdayLabel: "Yesterday",
lastLabel: "Last",
quartersLabel: "Quarters",
customRangeLabel: "Custom range",
sessionsShort: "Sessions",
addedToCart: "Added to cart",
completed: "Completed",

cancelBtn: "Cancel",
applyBtn: "Apply",

setExchangeRate: "Set exchange rate",
usdToLbp: "1 USD = ? LBP",
exchangeHelper: "This rate will be used to display LBP values in analytics",

sessionsBySocialReferrerTitle: "Sessions by social referrer",
salesByReferrerTitle: "Total sales by referrer",
performanceByChannelTitle: "Performance by referring channel",
sessionsByDevice: "Sessions by device type",
sessionsByLocation: "Sessions by location",
salesBySocial: "Total sales by social referrer",
cohortAnalysis: "Customer cohort analysis",
landingPage: "Sessions by landing page",
sessionsByReferrer: "Sessions by referrer",
salesByPOS: "Total sales by POS location",
sellThrough: "Products by sell-through rate",
posStaffSales: "POS staff sales total",
noData: "No data for this date range",
discounts: "Discounts",
totalSalesBreakdown: "Total sales breakdown",
totalSalesFormula: "Total sales",
netSalesFormula: "net sales",
additionalFeesFormula: "extra fees",
shippingChargeFormula: "shipping charge",
shippingChargesFormula: "shipping charges",
deliveryChargesFormula: "delivery charges",
averageOrderValueFormula: "Average order value",
grossSalesFormula: "gross sales",
discountFormula: "discount",
conversionRateFormula: "Conversion rate",
sessionsCheckoutFormula: "sessions that complete checkout",
sessionsLabel: "Sessions",
reachedCheckout: "Reached checkout",
sessionsByDeviceDesc: "Sessions on your online store, broken down by the user's device type",
sessionsByLocationDesc: "Sessions on your online store, broken down by geographic location",
salesBySocialDesc: "Total sales from social sources, broken down by name",
cohortDesc: "Returning purchase rate, with customers grouped by month of the first purchase",
cohort: "Cohort",
months: "Months",
month: "Month",
cohortTooltipSuffix: "cohort",
cohortReturnedDesc: "Customers who returned to purchase from you in",
landingPageDesc: "Sessions in your online store, broken down by the page the user first landed on",
sessionsBySocialReferrer: "Sessions by social referrer",
sessionsBySocialReferrerDesc: "Sessions in your online store from social sources, broken down by name",
salesByReferrer: "Total sales by referrer",
salesByReferrerDesc: "Total sales, broken down by the name of the site that led to the order",
performanceByReferringChannel: "Performance by referring channel",
performanceByReferringChannelDesc: "Sales and sessions metrics from your online store, broken down by referring channel and traffic type",
totalSalesLastNonDirectClick: "Total sales last non direct click",
sessionsByReferrerDesc: "Sessions in your online store, broken down by name of the site that led to the session",
salesByPOSDesc: "Total sales, broken down by physical store location",
testProductDefault: "Test Product, Default Title, None",
sellThroughDesc: "Products, broken down by sell-through rate (how quickly they're sold)",
sellThroughRateFormula: "Sell-through rate",
inventoryUnitsSold: "inventory units sold",
endingInventoryUnits: "ending inventory units",
posStaffSalesDesc: "Number of sales made by the staff member of your retail location",
returns: "Returns",
netSales: "Net sales",
shippingCharges: "Shipping charges",
returnFees: "Return fees",
taxes: "Taxes",
    totalSalesOverTime: "Total sales over time",
totalSalesDesc: "Amount spent (shipping, discounts, extra fees, etc...)",
    grossSales: "Gross sales",
returningRate: "Returning customer rate",
ordersFulfilled: "Orders fulfilled",
grossSalesTooltip: "Gross sales over time",
grossSalesDesc: "Rough sales revenue before discounts and returns",
returningTooltip: "Returning customer rate over time",
returningDesc: "Percentage of returning customers",
fulfilledTooltip: "Orders fulfilled over time",
fulfilledDesc: "Orders marked as fulfilled",
ordersTooltip: "Orders over time",
ordersDesc: "Total orders across all channels",
    loading: "Loading analytics...",
    title: "Analytics Dashboard",
    totalSales: "Total Sales",
    totalDiscounts: "Total Discounts",
    compareNone: "No comparison",
compareYesterday: "Yesterday | Previous period",
comparePrevYear: "Previous year",
comparePrevYearMatch: "Previous year (match day of week)",
compareCustom: "Custom",
    totalOrders: "Total Orders",
    customers: "Customers",
    products: "Products",
    sales: "Sales",
    orders: "Orders",
    sessions: "Sessions",
    total: "Total",
    prev: "Prev",
    next: "Next",
    last24h: "Last 24 hours",
last7d: "Last 7 days",
last30d: "Last 30 days",
last90d: "Last 90 days",
lastYear: "Last year",
exchangePlaceholder: "Ex: 89000",
    salesByChannel: "Total sales by sales channel",
salesByChannelDesc: "Total sales broken down by sales channel",
avgOrder: "Average order value over time",
avgOrderDesc: "Average order value, factoring on discount",
salesByProduct: "Total sales by product",
salesByProductDesc: "Total sales, broken down by product",
sessionsOverTime: "Sessions over time",
sessionsDesc: "Number of users sessions in your online store",
conversion: "Conversion rate over time",
conversionDesc: "Percentage of online store sessions that result in a sale",
conversionBreakdown: "Conversion rate breakdown",
    topProducts: "Top Selling Products",
    unitsSold: "Units Sold",
    topSellingProductsDesc: "Products ranked by quantity sold, ignoring price and revenue",
quantity: "Quantity",
    productLookup: "Product Sales Lookup",
    enterPublicId: "Enter product public_id",
    check: "Check",
    notFound: "Product not found",
    dayComparison: "Day Comparison",
    today: "Today",
    yesterday: "Yesterday",
    weekComparison: "Week Comparison",
    thisWeek: "This week",
    lastWeek: "Last week",
    monthComparison: "Month Comparison",
    thisMonth: "This month",
    lastMonth: "Last month",
    sellThroughRate: "Sell-through rate",
    yearComparison: "Year Comparison",
    thisYear: "This year",
    lastYear: "Last year",
    export: "Export",
chooseExportFormat: "Choose export format",
pdf: "PDF",
print: "Print",
exportFullPageDescription: "Export or print the full analytics page with all visible data.",
    exportTitle: "Export Analytics",
    generate: "Generate",
    cancel: "Cancel",
    reportTitle: "Analytics Report",
    from: "From",
    to: "To",
    metric: "Metric",
    print: "Print",
    exportToday: "Today",
exportWeek: "This Week",
exportMonth: "This Month",
exportYear: "This Year",
exportRange: "Custom Range",

    days: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"],
  },

  AR: {
    sessionsByDevice: "الجلسات حسب نوع الجهاز",
sessionsByLocation: "الجلسات حسب الموقع",
salesBySocial: "المبيعات حسب المصدر الاجتماعي",
totalSalesBreakdown: "تفصيل إجمالي المبيعات",
saveBtn: "حفظ",
totalSalesFormula: "إجمالي المبيعات",
netSalesFormula: "صافي المبيعات",
additionalFeesFormula: "الرسوم الإضافية",
shippingChargeFormula: "رسوم الشحن",
shippingChargesFormula: "رسوم الشحن",
deliveryChargesFormula: "رسوم التوصيل",
exchangePlaceholder: "مثال: 89000",
todayLabel: "اليوم",
yesterdayLabel: "أمس",
lastLabel: "آخر",
last24h: "آخر 24 ساعة",
last7d: "آخر 7 أيام",
last30d: "آخر 30 يوم",
last90d: "آخر 90 يوم",
lastYear: "آخر سنة",
quartersLabel: "الأرباع",
customRangeLabel: "نطاق مخصص",
averageOrderValueFormula: "متوسط قيمة الطلب",
grossSalesFormula: "إجمالي المبيعات",
discountFormula: "الخصم",
conversionRateFormula: "معدل التحويل",
sessionsCheckoutFormula: "الجلسات التي أكملت الدفع",
sessionsLabel: "الجلسات",
value: "القيمة",
monthLabel: "الشهر",
cohortLabel: "المجموعة",
sessionsShort: "الجلسات",
addedToCart: "أضيفت إلى السلة",
cancelBtn: "إلغاء",
applyBtn: "تطبيق",
setExchangeRate: "تحديد سعر الصرف",
usdToLbp: "1 دولار = كم ليرة",
exchangeHelper: "سيتم استخدام هذا السعر لعرض القيم بالليرة اللبنانية",
sessionsBySocialReferrerTitle: "الجلسات حسب المصدر الاجتماعي",
salesByReferrerTitle: "إجمالي المبيعات حسب المصدر",
performanceByChannelTitle: "الأداء حسب قناة الإحالة",
reachedCheckout: "وصلت إلى الدفع",
completed: "مكتملة",
sessionsByDeviceDesc: "الجلسات في متجرك الإلكتروني مقسّمة حسب نوع جهاز المستخدم",
sessionsByLocationDesc: "الجلسات في متجرك الإلكتروني مقسّمة حسب الموقع الجغرافي",
salesBySocialDesc: "إجمالي المبيعات من المصادر الاجتماعية مقسّم حسب الاسم",
cohortDesc: "معدل العودة للشراء مع تجميع العملاء حسب شهر أول عملية شراء",
cohort: "المجموعة",
months: "الأشهر",
month: "الشهر",
cohortTooltipSuffix: "مجموعة",
cohortReturnedDesc: "العملاء الذين عادوا للشراء منك في",
landingPageDesc: "الجلسات في متجرك الإلكتروني مقسّمة حسب أول صفحة وصل إليها المستخدم",
sessionsBySocialReferrer: "الجلسات حسب المصدر الاجتماعي",
sessionsBySocialReferrerDesc: "الجلسات في متجرك الإلكتروني من المصادر الاجتماعية مقسّمة حسب الاسم",
salesByReferrer: "إجمالي المبيعات حسب المصدر",
salesByReferrerDesc: "إجمالي المبيعات مقسّم حسب اسم الموقع الذي أدى إلى الطلب",
performanceByReferringChannel: "الأداء حسب قناة الإحالة",
performanceByReferringChannelDesc: "مقاييس المبيعات والجلسات من متجرك الإلكتروني مقسّمة حسب قناة الإحالة ونوع الزيارات",
totalSalesLastNonDirectClick: "إجمالي المبيعات لآخر نقرة غير مباشرة",
reset: "إعادة تعيين",
sessionsByReferrerDesc: "الجلسات في متجرك الإلكتروني مقسّمة حسب اسم الموقع الذي أدى إلى الجلسة",
salesByPOSDesc: "إجمالي المبيعات مقسّم حسب موقع المتجر الفعلي",
testProductDefault: "منتج تجريبي، العنوان الافتراضي، لا شيء",
sellThroughDesc: "المنتجات مقسّمة حسب معدل البيع",
sellThroughRateFormula: "معدل البيع",
inventoryUnitsSold: "وحدات المخزون المباعة",
endingInventoryUnits: "وحدات المخزون المتبقية",
posStaffSalesDesc: "عدد المبيعات التي قام بها موظف نقطة البيع في موقعك",
cohortAnalysis: "تحليل مجموعات العملاء",
landingPage: "الجلسات حسب صفحة الدخول",
sessionsByReferrer: "الجلسات حسب المصدر",
salesByPOS: "المبيعات حسب نقطة البيع",
sellThrough: "المنتجات حسب معدل البيع",
posStaffSales: "إجمالي مبيعات موظفي نقاط البيع",
    noData: "لا توجد بيانات لهذا النطاق الزمني",
    discounts: "الخصومات",
returns: "المرتجعات",
netSales: "صافي المبيعات",
shippingCharges: "رسوم الشحن",
returnFees: "رسوم المرتجعات",
taxes: "الضرائب",
    totalSalesOverTime: "إجمالي المبيعات عبر الوقت",
totalSalesDesc: "إجمالي المبلغ المدفوع شامل الشحن والخصومات والرسوم",
    grossSales: "إجمالي الإيرادات",
returningRate: "نسبة العملاء العائدين",
ordersFulfilled: "الطلبات المنجزة",
grossSalesTooltip: "إجمالي المبيعات عبر الوقت",
grossSalesDesc: "إجمالي الإيرادات قبل الخصومات والمرتجعات",
returningTooltip: "نسبة العملاء العائدين عبر الوقت",
returningDesc: "نسبة العملاء الذين عادوا للشراء",
fulfilledTooltip: "الطلبات المنجزة عبر الوقت",
fulfilledDesc: "الطلبات التي تم تنفيذها",
ordersTooltip: "الطلبات عبر الوقت",
ordersDesc: "عدد الطلبات عبر جميع القنوات",
    loading: "جاري تحميل التحليلات...",
    title: "لوحة التحليلات",
    totalSales: "إجمالي المبيعات",
    totalDiscounts: "إجمالي الخصومات",
    totalOrders: "إجمالي الطلبات",
    customers: "العملاء",
    products: "المنتجات",
    atLabel: "في",
aovShort: "متوسط الطلب",
sessionsLower: "جلسات",
    sales: "المبيعات",
    orders: "الطلبات",
    sessions: "الجلسات",
    compareNone: "بدون مقارنة",
compareYesterday: "أمس | الفارة السابقة",
comparePrevYear: "السنة الماضية",
comparePrevYearMatch: "السنة الماضية (نفس يوم الأسبوع)",
compareCustom: "مخصص",
sessionsOverTime: "الجلسات عبر الوقت",
total: "الإجمالي",
prev: "السابق",
next: "التالي",
salesByChannel: "إجمالي المبيعات حسب القناة",
salesByChannelDesc: "إجمالي المبيعات مقسّم حسب قناة البيع",
avgOrder: "متوسط قيمة الطلب عبر الوقت",
avgOrderDesc: "متوسط الطلب بعد احتساب الخصم",
salesByProduct: "إجمالي المبيعات حسب المنتج",
salesByProductDesc: "إجمالي المبيعات حسب كل منتج",
sessionsOverTime: "الجلسات عبر الوقت",
sessionsDesc: "عدد جلسات المستخدمين في المتجر",
conversion: "معدل التحويل عبر الوقت",
conversionDesc: "نسبة الجلسات التي انتهت بعملية شراء",
conversionBreakdown: "تفصيل معدل التحويل",
    topProducts: "المنتجات الأكثر مبيعًا",
    unitsSold: "الوحدات المباعة",
    topSellingProductsDesc: "المنتجات مرتبة حسب الكمية المباعة بدون احتساب السعر أو الإيرادات",
quantity: "الكمية",
    sessionsOverTime: "الجلسات عبر الوقت",
    productLookup: "بحث مبيعات المنتج",
    enterPublicId: "أدخل public_id للمنتج",
    check: "تحقق",
    notFound: "المنتج غير موجود",
    dayComparison: "مقارنة اليوم",
    today: "اليوم",
    yesterday: "أمس",
    weekComparison: "مقارنة الأسبوع",
    thisWeek: "هذا الأسبوع",
    lastWeek: "الأسبوع الماضي",
    monthComparison: "مقارنة الشهر",
    thisMonth: "هذا الشهر",
    lastMonth: "الشهر الماضي",
    yearComparison: "مقارنة السنة",
    thisYear: "هذه السنة",
    lastYear: "السنة الماضية",
    export: "تصدير",
chooseExportFormat: "اختر صيغة التصدير",
pdf: "PDF",
print: "طباعة",
exportFullPageDescription: "تصدير أو طباعة صفحة التحليلات كاملة مع كل البيانات الظاهرة.",
    exportTitle: "تصدير التحليلات",
    generate: "إنشاء",
    cancel: "إلغاء",
    reportTitle: "تقرير التحليلات",
    from: "من",
    to: "إلى",
    metric: "المؤشر",
    print: "طباعة",
    exportToday: "اليوم",
exportWeek: "هذا الأسبوع",
exportMonth: "هذا الشهر",
exportYear: "هذه السنة",
exportRange: "نطاق مخصص",
sellThroughRate: "معدل البيع",

    days: ["الأحد","الإثنين","الثلاثاء","الأربعاء","الخميس","الجمعة","السبت"],
  },
}[lang];

const buildSparkline = (key) => {
  const normalize = (raw = []) => {
    if (!raw.length) {
      return {
        labels:
          lang === "AR"
            ? ["البداية", "الوسط", "النهاية"]
            : ["start", "middle", "end"],
        values: [0, 0, 0],
      };
    }

    let values = raw.map((t) => Number(t[key] || 0));
    let labels = raw.map((t) =>
      new Date(t.date).toLocaleDateString(lang === "AR" ? "ar" : "en", {
        day: "numeric",
        month: "short",
      })
    );

    // one-day selected: create visible mini movement
    if (values.length === 1) {
      const v = values[0];
      labels = ["start", labels[0], "end"];
      values = v > 0 ? [0, v, 0] : [0, 0, 0];
    }

    return { labels, values };
  };

  const main = normalize(cardsData?.trend || []);
  const compare = normalize(cardsData?.comparison?.trend || []);

  const allValues = [
    ...main.values,
    ...(cardsData?.comparison?.trend ? compare.values : []),
  ];

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  return {
    labels: main.labels,
    values: main.values,
    compareValues: cardsData?.comparison?.trend ? compare.values : null,
    min: minValue === maxValue ? minValue - 1 : undefined,
    max: minValue === maxValue ? maxValue + 1 : undefined,
  };
};

const getLastQuarters = () => {
  const now = new Date();

  const currentQuarter = Math.floor(now.getMonth() / 3); // 0–3
  const currentYear = now.getFullYear();

  const quarters = [];

  for (let i = 0; i < 4; i++) {
    let qIndex = currentQuarter - i;
    let year = currentYear;

    if (qIndex < 0) {
      qIndex += 4;
      year -= 1;
    }

    const startMonth = qIndex * 3;
    const endMonth = startMonth + 2;

    const start = new Date(year, startMonth, 1);
    const end = new Date(year, endMonth + 1, 0); // last day of month

    quarters.push({
      label: `Q${qIndex + 1} ${year}`,
      start,
      end,
    });
  }

  return quarters;
};

const CohortTable = () => {
  const [activeRow, setActiveRow] = React.useState(null);
  const [hoverCell, setHoverCell] = React.useState(null);

  if (!customerCohorts.length) {
    return (
      <div className="h-[240px] flex items-center justify-center text-gray-400 text-sm">
        {TEXT.noData}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {customerCohorts.map((cohort, rowIndex) => {
        const visibleCells = cohort.cells?.length || 0;

        return (
          <div
            key={cohort.cohort}
            className={`
              grid grid-cols-[120px_1fr] items-center
              ${activeRow !== null && activeRow !== rowIndex ? "opacity-30" : ""}
            `}
          >
            {/* LEFT LABEL */}
            <span
              onMouseEnter={() => setActiveRow(rowIndex)}
              onMouseLeave={() => setActiveRow(null)}
              className="text-[12px] text-gray-700 cursor-pointer"
            >
              {cohort.label}
            </span>

            {/* CELLS */}
            <div className="grid grid-cols-8 gap-1 w-full">
              {(cohort.cells || []).map((cell, colIndex) => {
                const key = `${rowIndex}-${colIndex}`;
                const isHover = hoverCell === key;
                const hasData = Number(cohort.customers || 0) > 0;
                const rate = Number(cell.retentionRate || 0);

                return (
                  <div
                    key={key}
                    onMouseEnter={() => setHoverCell(key)}
                    onMouseLeave={() => setHoverCell(null)}
                    className="relative group cursor-pointer"
                  >
                    <div
                      className={`
                        w-full h-8 flex items-center justify-center
                        text-[11px] font-semibold
                        rounded-md border transition
                        ${isHover ? "border-yellow-400 scale-[1.02]" : "border-transparent"}
                      `}
                      style={getCohortCellStyle(rate, hasData)}
                    >
                      {hasData ? `${rate.toFixed(0)}%` : "—"}
                    </div>

                    {isHover && (
                      <div className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 w-[260px] bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-[12px]">
                        <div className="font-semibold text-gray-900 mb-1">
                          {TEXT.monthLabel} {cell.monthIndex + 1},{" "}
                          {cohort.label} {TEXT.cohortTooltipSuffix}
                        </div>

                        <div className="text-gray-600 mb-2 leading-relaxed">
                          {TEXT.cohortReturnedDesc} {cell.targetLabel}
                        </div>

                        <div className="space-y-1 text-[11px]">
                          <div className="flex justify-between gap-3">
                            <span className="text-gray-500">
                              {lang === "AR" ? "حجم المجموعة" : "Cohort size"}
                            </span>
                            <span className="font-semibold text-gray-900">
                              {cohort.customers}
                            </span>
                          </div>

                          <div className="flex justify-between gap-3">
                            <span className="text-gray-500">
                              {lang === "AR" ? "العملاء العائدون" : "Returning customers"}
                            </span>
                            <span className="font-semibold text-gray-900">
                              {cell.customers}
                            </span>
                          </div>

                          <div className="flex justify-between gap-3">
                            <span className="text-blue-700">
                              {lang === "AR" ? "معدل العودة" : "Retention rate"}
                            </span>
                            <span className="font-semibold text-blue-900">
                              {hasData ? `${rate.toFixed(1)}%` : "—"}
                            </span>
                          </div>
                        </div>

                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-white rotate-45 -mt-[4px]" />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* EMPTY RIGHT SPACE */}
              {Array.from({ length: 8 - visibleCells }).map((_, i) => (
                <div key={`empty-${cohort.cohort}-${i}`} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CohortHelpTooltip = () => (
  <div className="relative group">
    <button
      type="button"
      className="
        w-5 h-5
        rounded-full
        border border-gray-300
        bg-white
        text-gray-600
        text-[12px]
        font-semibold
        flex items-center justify-center
        hover:bg-gray-100
        hover:text-gray-900
        transition
      "
    >
      ?
    </button>

    <div
      className="
        pointer-events-none
        opacity-0 group-hover:opacity-100
        translate-y-1 group-hover:translate-y-0
        transition-all duration-200
        absolute
        right-0
        bottom-full mb-2
        w-[min(420px,calc(100vw-32px))]
        bg-white
        border border-gray-200
        rounded-xl
        shadow-[0_12px_35px_rgba(0,0,0,0.14)]
        p-4
        z-[999]
        text-left
      "
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <p className="text-[14px] font-semibold text-gray-900 mb-2">
        {lang === "AR"
          ? "كيف تقرأ تحليل مجموعات العملاء؟"
          : "How to read customer cohort analysis"}
      </p>

      <div className="space-y-2 text-[12px] text-gray-700 leading-relaxed">
        <p>
          {lang === "AR"
            ? "كل صف يمثل مجموعة عملاء قاموا بأول عملية شراء لهم في نفس الشهر. على سبيل المثال، صف Jan 2026 يعني العملاء الذين اشتروا لأول مرة في يناير 2026."
            : "Each row is a cohort: a group of customers whose first valid purchase happened in the same month. For example, the Jan 2026 row means customers who bought for the first time in January 2026."}
        </p>

        <p>
          {lang === "AR"
            ? "الأعمدة تمثل الأشهر التالية بعد أول شراء. الخلية الأولى عادةً تمثل شهر أول شراء، والخلايا التالية توضّح كم من نفس العملاء عادوا واشتروا مرة أخرى في الأشهر اللاحقة."
            : "The columns show the following months after the first purchase. The first cell represents the first-purchase month, and the next cells show how many customers from that same group came back and purchased again in later months."}
        </p>

        <p>
          {lang === "AR"
            ? "النسبة داخل كل خلية هي معدل الرجوع: العملاء العائدون في ذلك الشهر ÷ حجم المجموعة الأساسية. لذلك إذا كان حجم المجموعة 20 عميلاً و4 عادوا للشراء في الشهر التالي، ستكون النسبة 20%."
            : "The percentage inside each cell is the retention rate: returning customers in that month divided by the original cohort size. So if the cohort has 20 customers and 4 bought again the next month, the cell shows 20%."}
        </p>

        <div className="pt-2 border-t border-gray-100 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-[3px] bg-[#0EA5E9]" />
            <span>
              {lang === "AR"
                ? "الأزرق الداكن يعني رجوع قوي للعملاء."
                : "Dark blue means strong customer retention."}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-[3px] bg-[#E0F2FE] border border-sky-100" />
            <span>
              {lang === "AR"
                ? "الأزرق الفاتح يعني رجوع منخفض أو طبيعي."
                : "Light blue means low or normal retention."}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-[3px] bg-[#F1F2F4] border border-gray-200" />
            <span>
              {lang === "AR"
                ? "الرمادي يعني عدم وجود بيانات كافية أو عدم وجود عملاء في هذه المجموعة."
                : "Gray means there is not enough data or no customers in that cohort."}
            </span>
          </div>
        </div>

        <p className="pt-2 border-t border-gray-100">
          {lang === "AR"
            ? "ما يمكنك تحليله من هذا الكارد: هل العملاء يعودون بعد أول شراء؟ أي شهر جذب عملاء بجودة أعلى؟ هل الحملات أو الخصومات جلبت عملاء يشترون مرة واحدة فقط؟ وهل تجربة المنتج/الشحن/الخدمة تشجع العملاء على تكرار الشراء؟"
            : "What you can analyze from this card: whether customers come back after their first purchase, which acquisition month brought higher-quality customers, whether campaigns or discounts attracted one-time buyers only, and whether your product, shipping, or service experience is encouraging repeat purchases."}
        </p>

        <p className="text-[11px] text-gray-500">
          {lang === "AR"
            ? "ملاحظة: هذه البطاقة مفيدة أكثر عندما يكون لديك عدة أشهر من الطلبات، لأنها تقيس سلوك الرجوع مع الوقت وليس فقط مبيعات يوم واحد."
            : "Note: this card becomes more useful after you have multiple months of orders, because it measures repeat behavior over time, not only one-day sales."}
        </p>
      </div>

      <div className="absolute right-3 top-full w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45 -mt-[6px]" />
    </div>
  </div>
);

const TooltipBlock = ({ title, subtitle, equation }) => (
  <div
    className={`
      hidden sm:block
      pointer-events-none
      opacity-0 group-hover:opacity-100
      translate-y-1 group-hover:translate-y-0
      transition-all duration-200
      absolute
      bottom-full mb-2
      w-[min(320px,calc(100vw-32px))]
      bg-white
      border border-gray-200
      rounded-xl
      shadow-lg
      p-3
      z-[999]
      ${lang === "AR" ? "right-0 translate-x-0" : "left-1/2 -translate-x-1/2"}
    `}
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <p className="text-[15px] font-semibold text-gray-900 mb-1">
      {title}
    </p>

    {subtitle && (
      <p className="text-[13px] text-gray-700 leading-relaxed mb-2">
        {subtitle}
      </p>
    )}

    {equation && (
      <p className="text-[12px] font-mono text-green-800 tracking-wide">
        {equation}
      </p>
    )}

    <div
  className={`
    absolute top-full
    w-3 h-3 bg-white rotate-45 -mt-[6px]
    ${lang === "AR" ? "right-4" : "left-1/2 -translate-x-1/2"}
  `}
/>
  </div>
);

const getMainButtonLabel = () => {
  if (!mainRange) {
    return lang === "AR" ? "اليوم" : "Today";
  }

  const { start, end } = mainRange;

  const today = new Date();
  today.setHours(0,0,0,0);

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const normalize = (d) => {
    const x = new Date(d);
    x.setHours(0,0,0,0);
    return x;
  };

  const s = normalize(start);
  const e = normalize(end);

  const sameDay = s.getTime() === e.getTime();

  if (sameDay) {
    if (s.getTime() === today.getTime()) {
      return lang === "AR" ? "اليوم" : "Today";
    }

    if (s.getTime() === yesterday.getTime()) {
      return lang === "AR" ? "أمس" : "Yesterday";
    }

    // ✅ SAME DAY (not today/yesterday)
    return s.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).toUpperCase(); // 7 JUL, 2026
  }

  // ✅ RANGE
  return `${formatCompareDate(start).toUpperCase()} → ${formatCompareDate(end).toUpperCase()}`;
};

const getAppliedLabel = () => {
  if (!appliedRange) {
    return lang === "AR" ? "اليوم" : "Today";
  }

  const start = appliedRange.start;
  const end = appliedRange.end;

  const format = (d) =>
    d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "short",
    });

  // same day
  if (start.toDateString() === end.toDateString()) {
    return format(start);
  }

  return `${format(start)} → ${format(end)}`;
};

const Calendar = ({
  monthOffset,
  selectedDate,
  calendarBaseDate,
  setSelectedDate,
  rangeStart,
  rangeEnd,
  setRangeStart,
  setRangeEnd,
  setCalendarOffset,
  activeQuickRange,
  setActiveQuickRange,
  isCompareCalendar = false,
  compact = false,
}) => {

  const isCustomMode = activeQuickRange === "custom";

 const safeSelected = selectedDate || new Date();
const safeBaseDate = calendarBaseDate || safeSelected || new Date();

const base = new Date(
  safeBaseDate.getFullYear(),
  safeBaseDate.getMonth() + monthOffset,
  1
);

  const year = base.getFullYear();
  const month = base.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
  <div className={compact ? "w-[220px]" : "w-[260px]"}>

      {/* DAYS */}
      <div className="grid grid-cols-7 text-xs text-gray-500 mb-1">
        {(lang === "AR"
          ? ["ح","ن","ث","ر","خ","ج","س"]
          : ["Su","Mo","Tu","We","Th","Fr","Sa"]
        ).map(d => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>

      {/* GRID */}
      <div className="grid grid-cols-7 text-sm">
        {days.map((d, i) => {
          const currentDate = d ? new Date(year, month, d) : null;

const todayDate = new Date();
todayDate.setHours(0,0,0,0);

const isFuture =
  currentDate &&
  currentDate > todayDate;

const isToday =
  d &&
  d === todayDate.getDate() &&
  month === todayDate.getMonth() &&
  year === todayDate.getFullYear();

const isSelected =
  d &&
  !isFuture &&
  d === safeSelected.getDate() &&
  month === safeSelected.getMonth()&&
  year === safeSelected.getFullYear();

const start = rangeStart && rangeEnd && rangeStart > rangeEnd ? rangeEnd : rangeStart;
const end = rangeStart && rangeEnd && rangeStart > rangeEnd ? rangeStart : rangeEnd;

const isRangeStart =
  currentDate &&
  rangeStart &&
  currentDate.toDateString() === rangeStart.toDateString();

const isRangeEnd =
  currentDate &&
  rangeEnd &&
  currentDate.toDateString() === rangeEnd.toDateString();

const isInRange =
  currentDate &&
  start &&
  end &&
  currentDate > start &&
  currentDate < end;

          return (
            <button
  type="button"
              key={i}
           onClick={() => {
  if (!d || isFuture) return;

  const clicked = new Date(year, month, d);

  // reset quick ranges except custom
if (activeQuickRange && activeQuickRange !== "custom") {
  setActiveQuickRange(null);
}

// =========================
// ✅ COMPARE CALENDAR MODE
// one click = same-day compare
// second click = range compare
// third click after range = restart selection
// =========================
if (isCompareCalendar) {
  const isSingleCompareDay =
    rangeStart &&
    rangeEnd &&
    rangeStart.toDateString() === rangeEnd.toDateString();

  // First click OR after a completed range:
  // set both labels to the same date so Apply works for one-day compare.
  if (!rangeStart || !rangeEnd || !isSingleCompareDay) {
    setRangeStart(clicked);
    setRangeEnd(clicked);
    setSelectedDate(clicked);
    return;
  }

  // Second click after a single selected day:
  // convert it into a real range.
  if (clicked < rangeStart) {
    setRangeStart(clicked);
    setRangeEnd(rangeStart);
  } else {
    setRangeStart(rangeStart);
    setRangeEnd(clicked);
  }

  setSelectedDate(clicked);
  return;
}

// =========================
// ✅ CUSTOM RANGE MODE
// =========================
if (isCustomMode) {
    // first click
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(clicked);
      setRangeEnd(null);
      setSelectedDate(clicked);
      return;
    }

    // second click
    if (!rangeEnd) {
      if (clicked < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(clicked);
      } else {
        setRangeEnd(clicked);
      }
      return;
    }
  }

 // =========================
// ✅ DEFAULT (single day mode)
// =========================
setRangeStart(clicked);
setRangeEnd(clicked);
setSelectedDate(clicked);

// ✅ Important:
// Compare calendar should only update compare range,
// not the main analytics range.
if (isCompareCalendar) {
  return;
}

applyRange(clicked, clicked);
}}
              className={`
  ${compact ? "h-7 w-7 text-[12px]" : "h-8 w-8 text-[13px]"}
  flex items-center justify-center
  rounded-md
  transition
  ${
    isFuture
      ? "text-gray-300 cursor-not-allowed"
      : "hover:bg-gray-200"
  }
  ${isInRange ? "bg-gray-200" : ""}
  ${isRangeStart || isRangeEnd ? "bg-black text-white" : ""}
`}
            >
              <div className="relative flex items-center justify-center w-full h-full">
  <span className="leading-none">{d}</span>

  {isToday && !isSelected && !isRangeStart && !isRangeEnd && (
    <span className="absolute bottom-1 w-1 h-1 bg-black rounded-full"></span>
  )}
</div>
            </button>
          );
        })}
      </div>

    </div>
  );
};

  const load = async (w = weekOffset, m = monthOffset) => {
    setLoading(true);
    try {
    const res = await getAdminAnalytics({
  weekOffset: w,
  monthOffset: m,
  yearOffset,
  dayOffset,
  productPublicId: productPublicId || undefined,

  // ✅ ADD THIS
  from: mainStart ? formatLocalDate(mainStart) : undefined,
to: mainEnd ? formatLocalDate(mainEnd) : undefined,
});
      setData(res);
      setProductResult(res.productSales || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => {
  if (skipNextCardsAutoLoadRef.current) {
    skipNextCardsAutoLoadRef.current = false;
    return;
  }

  const start = mainStart || new Date();
  const end = mainEnd || start;

  let nextCompare = compareRange;

  if (compareType !== "custom") {
    nextCompare = buildCompareRange(compareType, start, end);
    setCompareRange(nextCompare);
  }

  loadCards(start, end, nextCompare);
}, [mainStart, mainEnd, compareType]);

 useEffect(() => {
  const handleClickOutside = (e) => {
    // ONLY close if clicking outside cards
    if (!e.target.closest(".analytics-card")) {
      setActiveTooltip(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  useEffect(() => {
  if (mainStart && mainEnd) {
    load();
  }
}, [mainStart, mainEnd]);

  useEffect(() => {
  const handleClickOutside = (e) => {
    if (!showCompareCalendar) return; // ✅ only when open

   if (modalRef.current && !modalRef.current.contains(e.target)) {
  setShowCompareCalendar(false); // ✅ ONLY CLOSE
}
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, [showCompareCalendar]);

  useEffect(() => {
  load(weekOffset, monthOffset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
                  }, [weekOffset, monthOffset, yearOffset, dayOffset]);

  const resolveValues = (block) => {
  if (!block) return [];
  return block[metric] || [];
};

useEffect(() => {
  const handleClick = (e) => {
    if (!e.target.closest(".compare-dropdown")) {
      setShowCompareDropdown(false);
    }
  };

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, []);

const isSameDay =
  mainStart &&
  mainEnd &&
  mainStart.toDateString() === mainEnd.toDateString();

const metricLabel = {
  sales: TEXT.sales,
  orders: TEXT.orders,
  customers: TEXT.customers,
  sessions: TEXT.sessions,
}[metric];


const calculateTotal = (values = []) => {
  return values.reduce((sum, v) => sum + (Number(v) || 0), 0);
};

const formatShortDate = (d) =>
  d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
    day: "numeric",
    month: "short",
  });

  const formatCompareDate = (d) =>
  d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const diffDaysInclusive = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);

  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);

  return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
};

const getSameWeekdayPreviousYear = (date) => {
  const current = new Date(date);
  const previous = new Date(date);

  previous.setFullYear(previous.getFullYear() - 1);

  const currentDay = current.getDay();
  const previousDay = previous.getDay();

  const diff = currentDay - previousDay;
  previous.setDate(previous.getDate() + diff);

  return previous;
};

const buildCompareRange = (type, start, end) => {
  if (!type || type === "none" || !start || !end) return null;

  const days = diffDaysInclusive(start, end);

  if (type === "yesterday") {
    return {
      start: addDays(start, -days),
      end: addDays(end, -days),
    };
  }

  if (type === "prevYear") {
    const s = new Date(start);
    const e = new Date(end);

    s.setFullYear(s.getFullYear() - 1);
    e.setFullYear(e.getFullYear() - 1);

    return { start: s, end: e };
  }

  if (type === "prevYearMatch") {
    const compareEnd = getSameWeekdayPreviousYear(end);
    const compareStart = addDays(compareEnd, -(days - 1));

    return {
      start: compareStart,
      end: compareEnd,
    };
  }

  return null;
};

useEffect(() => {
  const state = location.state;

  if (!state?.fromReports || reportsStateAppliedRef.current) return;

  reportsStateAppliedRef.current = true;

  const nextMainStart = state.mainStart
    ? parseChartDate(state.mainStart)
    : new Date();

  const nextMainEnd = state.mainEnd
    ? parseChartDate(state.mainEnd)
    : nextMainStart;

  const nextCompareRange =
    state.compareStart && state.compareEnd
      ? {
          start: parseChartDate(state.compareStart),
          end: parseChartDate(state.compareEnd),
        }
      : null;

  setMainStart(nextMainStart);
  setMainEnd(nextMainEnd);
  setMainRange({
    start: nextMainStart,
    end: nextMainEnd,
  });

  setRangeStart(nextMainStart);
  setRangeEnd(nextMainEnd);
  setSelectedDate(nextMainEnd);
  setCalendarBaseDate(nextMainEnd);
  setCalendarOffset(0);

  setCompareRange(nextCompareRange);
  setCompareType(state.compareType || (nextCompareRange ? "custom" : "none"));

  setCurrency(state.currency || "USD");

  if (state.exchangeRate) {
    setExchangeRate(Number(state.exchangeRate) || 89000);
  }

  loadCards(nextMainStart, nextMainEnd, nextCompareRange);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.state]);

const getDelta = (current, previous, options = {}) => {
  current = Number(current || 0);
  previous = Number(previous || 0);

  const {
    hideZeroComparison = false,
    lowSampleThreshold = 3,
  } = options;

  // =========================
  // ✅ BOTH ZERO
  // Shopify-style:
  // show neutral state instead of fake 0%
  // =========================
  if (current === 0 && previous === 0) {
    return {
      percent: null,
      direction: "same",
      hideComparison: true,
    };
  }

  // =========================
  // ✅ PREVIOUS ZERO
  // Shopify-style:
  // avoid divide-by-zero fake %
  // =========================
  if (previous === 0) {
  return {
    percent: null,
    direction: current > 0 ? "up" : "same",
    hideComparison: false,
    fromZero: current > 0,
  };
}

  // =========================
  // ✅ LOW SAMPLE SIZE
  // Important for rates like returning customers
  // Example:
  // 1/1 vs 1/1 = mathematically 0%
  // but statistically meaningless
  // =========================
  if (
    hideZeroComparison &&
    current <= lowSampleThreshold &&
    previous <= lowSampleThreshold
  ) {
    return {
      percent: null,
      direction: "same",
      hideComparison: true,
    };
  }

  const percent = ((current - previous) / previous) * 100;

  // Shopify-style:
  // tiny/no meaningful change
  if (Math.abs(percent) < 0.05) {
    return {
      percent: null,
      direction: "same",
      hideComparison: true,
    };
  }

  return {
    percent: Number(Math.abs(percent).toFixed(1)),
    direction: percent > 0 ? "up" : percent < 0 ? "down" : "same",
    hideComparison: false,
  };
};

const getCompareLabel = () => {
  if (!cardsData?.comparison) return "";

  const start = new Date(cardsData.comparison.from);
  const end = new Date(cardsData.comparison.to);

  if (start.toDateString() === end.toDateString()) {
    return formatCompareDate(start);
  }

  return `${formatCompareDate(start)} → ${formatCompareDate(end)}`;
};

const getCompareButtonLabel = () => {
  if (!compareRange) {
    return lang === "AR" ? "بدون مقارنة" : "No comparison";
  }

  const { start, end } = compareRange;

  if (start.toDateString() === end.toDateString()) {
    return formatCompareDate(start).toUpperCase();
  }

  return `${formatCompareDate(start).toUpperCase()} → ${formatCompareDate(end).toUpperCase()}`;
};

const formatTotal = (total) => {
  if (metric === "sales") {
    return `$${total.toFixed(2)}`;
  }
  return total;
};

// ===============================
// ✅ TOTAL SALES OVER TIME HELPERS
// ===============================
const roundUpToNiceStep = (value) => {
  const n = Number(value || 0);

  if (n <= 0) return 1;

  // round up to nearest number ending with 0 or 5
  return Math.ceil(n / 5) * 5;
};

const formatMoneyAxis = (value) => {
  const n = Number(value || 0);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  if (currency === "LBP") {
    const amount = Math.round(n * exchangeRate);

    if (isMobile) {
      if (amount >= 1000000) return `${Math.round(amount / 1000000)}M`;
      if (amount >= 1000) return `${Math.round(amount / 1000)}K`;
      return `${amount}`;
    }

    return `${amount.toLocaleString()} L.L`;
  }

  if (isMobile && n >= 1000) {
    return `$${Math.round(n / 1000)}K`;
  }

  return `$${n.toLocaleString()}`;
};

const formatChartMoney = (value) => {
  const n = Number(value || 0);

  if (currency === "LBP") {
    return `${Math.round(n * exchangeRate).toLocaleString()} L.L`;
  }

  return `$${n.toFixed(2)}`;
};

const parseChartDate = (value) => {
  if (!value) return new Date();

  // Supports:
  // "2026-05-06"
  // "2026-05-06 21:00:00"
  const safe = String(value).includes(" ")
    ? String(value).replace(" ", "T")
    : `${value}T00:00:00`;

  return new Date(safe);
};

const formatHourLabel = (hour) => {
  const h = Number(hour || 0);
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? "AM" : "PM";

  return `${displayHour} ${suffix}`;
};

const formatChartDate = (value) => {
  return parseChartDate(value).toLocaleDateString(
    lang === "AR" ? "ar" : "en",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  );
};

const formatChartDateTime = (point) => {
  const d = parseChartDate(point?.date);
  const hour =
    point?.hour !== undefined
      ? Number(point.hour || 0)
      : d.getHours();

  return `${d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
  day: "numeric",
  month: "short",
  year: "numeric",
})} ${TEXT.atLabel} ${formatHourLabel(hour)}`;
};

const getRangeDaysForChart = () => {
  const start = mainStart || new Date();
  const end = mainEnd || start;

  return diffDaysInclusive(start, end);
};

const getComparisonRangeDaysForChart = () => {
  if (!cardsData?.comparison?.from || !cardsData?.comparison?.to) {
    return 0;
  }

  return diffDaysInclusive(
    parseChartDate(cardsData.comparison.from),
    parseChartDate(cardsData.comparison.to)
  );
};

const normalizeComparisonDailyTrend = (
  trend = [],
  from,
  to,
  granularity,
  valueKeys = []
) => {
  if (granularity !== "day" || !from || !to) {
    return trend || [];
  }

  const map = new Map(
    (trend || []).map((point) => [
      formatLocalDate(parseChartDate(point.date)),
      point,
    ])
  );

  const result = [];
  const cursor = parseChartDate(from);
  const last = parseChartDate(to);

  cursor.setHours(0, 0, 0, 0);
  last.setHours(0, 0, 0, 0);

  while (cursor <= last) {
    const key = formatLocalDate(cursor);

    if (map.has(key)) {
      result.push(map.get(key));
    } else {
      const emptyPoint = { date: key };

      valueKeys.forEach((keyName) => {
        emptyPoint[keyName] = 0;
      });

      result.push(emptyPoint);
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
};

const normalizeComparisonTrendToMainLength = (
  trend = [],
  from,
  to,
  granularity,
  valueKeys = [],
  targetLength = 0
) => {
  const normalized = normalizeComparisonDailyTrend(
    trend,
    from,
    to,
    granularity,
    valueKeys
  );

  // Only daily comparison ranges need this protection.
  if (granularity !== "day" || !targetLength) {
    return normalized;
  }

  // If backend/frontend already returned the correct length, keep it.
  if (normalized.length === targetLength) {
    return normalized;
  }

  // If somehow longer, trim safely to match the main chart.
  if (normalized.length > targetLength) {
    return normalized.slice(0, targetLength);
  }

  // If custom comparison lost the last day, pad missing days with 0 values.
  const result = [...normalized];

  const cursor =
    result.length > 0
      ? parseChartDate(result[result.length - 1].date)
      : parseChartDate(from);

  cursor.setHours(0, 0, 0, 0);

  if (result.length > 0) {
    cursor.setDate(cursor.getDate() + 1);
  }

  while (result.length < targetLength) {
    const key = formatLocalDate(cursor);
    const emptyPoint = { date: key };

    valueKeys.forEach((keyName) => {
      emptyPoint[keyName] = 0;
    });

    result.push(emptyPoint);
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
};

const getMonthKey = (point) => {
  const d = parseChartDate(point?.date);
  return `${d.getFullYear()}-${d.getMonth()}`;
};

const getYearKey = (point) => {
  const d = parseChartDate(point?.date);
  return `${d.getFullYear()}`;
};

const isFirstPointOfMonth = (point, index, trend) => {
  if (index === 0) return true;

  return getMonthKey(point) !== getMonthKey(trend[index - 1]);
};

const isFirstPointOfYear = (point, index, trend) => {
  if (index === 0) return true;

  return getYearKey(point) !== getYearKey(trend[index - 1]);
};

const shouldShowAxisLabel = (
  point,
  index,
  trend,
  granularity,
  chartSize = "small"
) => {
  if (!point || !trend?.length) return false;

  // ✅ One-day hourly charts:
  // show 8 labels: 12 AM, 3 AM, 6 AM, 9 AM, 12 PM, 3 PM, 6 PM, 9 PM
  if (granularity === "hour") {
    const hour = Number(point.hour || 0);
    return hour % 3 === 0;
  }

  const rangeDays = getRangeDaysForChart();
  const isFirst = index === 0;
  const isLast = index === trend.length - 1;

  if (isFirst) return true;

  // ✅ More than 1 year:
  // show years only, but do NOT force the last label
  // because it can duplicate/overlap the last year label.
  if (rangeDays > 365) {
    return isFirstPointOfYear(point, index, trend);
  }

  // ✅ Greater than 30 days:
  // show month labels only, but do NOT force the last label
  // because last day often repeats the same month label.
  if (rangeDays > 30) {
    if (!isFirstPointOfMonth(point, index, trend)) return false;

    // ✅ Small cards have less width:
    // for long ranges, show every other month only.
    if (chartSize === "small" && rangeDays > 180) {
      const date = parseChartDate(point.date);
      return isFirst || date.getMonth() % 2 === 0;
    }

    return true;
  }

  // ✅ Small charts: AOV + Sessions
  if (chartSize === "small") {
    if (rangeDays <= 7) return true;

    if (rangeDays <= 30) {
      if (isLast) return true;
      if (index >= trend.length - 3) return false;
      return index % 4 === 0;
    }
  }

  // ✅ Large chart: Total Sales
  if (rangeDays <= 15) return true;

  if (rangeDays <= 30) {
    if (isLast) return true;
    if (index >= trend.length - 2) return false;
    return index % 2 === 0;
  }

  return false;
};

const formatAxisLabel = (
  point,
  index,
  trend,
  granularity,
  chartSize = "small"
) => {
  if (!shouldShowAxisLabel(point, index, trend, granularity, chartSize)) {
    return "";
  }

  if (granularity === "hour") {
    return formatHourLabel(point.hour);
  }

  const rangeDays = getRangeDaysForChart();
  const date = parseChartDate(point.date);

  // ✅ More than 1 year: show years
  if (rangeDays > 365) {
    return date.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      year: "numeric",
    });
  }

  // ✅ More than 30 days:
  // small charts use shorter labels to avoid messy overlap.
  if (rangeDays > 30) {
    return date.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      month: "short",
      ...(chartSize === "large" ? { year: "numeric" } : {}),
    });
  }

  // ✅ 30 days or less: show days
  return date.toLocaleDateString(lang === "AR" ? "ar" : "en", {
    day: "numeric",
    month: "short",
  });
};

const mainChartTrend = cardsData?.trend || [];
const mainChartGranularity = cardsData?.trendGranularity || "day";

const mainChartLabels = mainChartTrend.map((point, index) =>
  formatAxisLabel(
    point,
    index,
    mainChartTrend,
    mainChartGranularity,
    "large"
  )
);

const mainChartSales = mainChartTrend.map((point) =>
  Number(point.sales || 0)
);

const compareChartGranularity =
  cardsData?.comparison?.trendGranularity || mainChartGranularity;

const compareChartTrend = normalizeComparisonTrendToMainLength(
  cardsData?.comparison?.trend || [],
  cardsData?.comparison?.from,
  cardsData?.comparison?.to,
  compareChartGranularity,
  ["sales", "orders", "customers", "fulfilledOrders"],
  mainChartTrend.length
);

const compareChartSales = compareChartTrend.map((point) =>
  Number(point.sales || 0)
);

const mainChartRangeDays = getRangeDaysForChart();
const compareChartRangeDays = getComparisonRangeDaysForChart();

// ✅ Show comparison line only when the chart shape makes sense.
// - hourly can compare only with hourly
// - daily can compare only with daily
// - multi-day ranges must have the same number of days
const canOverlayCompareChart =
  Boolean(cardsData?.comparison) &&
  compareChartTrend.length > 0 &&
  compareChartGranularity === mainChartGranularity &&
  (
    // one-day hourly vs one-day hourly
    (mainChartGranularity === "hour" &&
      mainChartRangeDays === 1 &&
      compareChartRangeDays === 1) ||

    // date range daily vs date range daily with same number of days
    (mainChartGranularity === "day" &&
      mainChartRangeDays > 1 &&
      compareChartRangeDays === mainChartRangeDays)
  );

const alignedCompareSales = canOverlayCompareChart
  ? mainChartLabels.map((_, index) => compareChartSales[index] ?? null)
  : [];

const hasCompareChart = canOverlayCompareChart;

const mainChartTotalSales = Number(cardsData?.grossSales || 0);
const compareChartTotalSales = Number(cardsData?.comparison?.grossSales || 0);

const totalSalesDelta = getDelta(
  mainChartTotalSales,
  compareChartTotalSales
);

const mainChartMaxSales = Math.max(
  ...mainChartSales,
  ...compareChartSales,
  mainChartTotalSales,
  0
);

// ✅ 6 y-axis rows: 0 + 5 jumps
const mainChartStep = roundUpToNiceStep(mainChartMaxSales / 5);
const mainChartSuggestedMax = mainChartStep * 5;

const averageOrderValueTrend = cardsData?.averageOrderValueTrend || [];
const averageOrderValueGranularity =
  cardsData?.averageOrderValueTrendGranularity || "day";

const averageOrderValueLabels = averageOrderValueTrend.map((point, index) =>
  formatAxisLabel(
    point,
    index,
    averageOrderValueTrend,
    averageOrderValueGranularity,
    "small"
  )
);

const averageOrderValueValues = averageOrderValueTrend.map((point) =>
  Number(point.averageOrderValue || 0)
);

const compareAverageOrderValueGranularity =
  cardsData?.comparison?.averageOrderValueTrendGranularity ||
  averageOrderValueGranularity;

const compareAverageOrderValueTrend = normalizeComparisonTrendToMainLength(
  cardsData?.comparison?.averageOrderValueTrend || [],
  cardsData?.comparison?.from,
  cardsData?.comparison?.to,
  compareAverageOrderValueGranularity,
  ["averageOrderValue", "orders"],
  averageOrderValueTrend.length
);

const compareAverageOrderValueValues = compareAverageOrderValueTrend.map(
  (point) => Number(point.averageOrderValue || 0)
);

const canOverlayAverageOrderValueCompare =
  Boolean(cardsData?.comparison) &&
  compareAverageOrderValueTrend.length > 0 &&
  compareAverageOrderValueGranularity === averageOrderValueGranularity &&
  (
    (averageOrderValueGranularity === "hour" &&
      mainChartRangeDays === 1 &&
      compareChartRangeDays === 1) ||
    (averageOrderValueGranularity === "day" &&
      mainChartRangeDays > 1 &&
      compareChartRangeDays === mainChartRangeDays)
  );

const alignedCompareAverageOrderValue = canOverlayAverageOrderValueCompare
  ? averageOrderValueLabels.map(
      (_, index) => compareAverageOrderValueValues[index] ?? null
    )
  : [];

const averageOrderValue = Number(cardsData?.averageOrderValue || 0);
const compareAverageOrderValue = Number(
  cardsData?.comparison?.averageOrderValue || 0
);

const averageOrderValueDelta = getDelta(
  averageOrderValue,
  compareAverageOrderValue
);

const averageOrderValueMax = Math.max(
  ...averageOrderValueValues,
  ...compareAverageOrderValueValues,
  averageOrderValue,
  0
);

// ✅ 3 y-axis rows: 0, middle, max
// jump = biggest number / 2, rounded up to closest 0 or 5
const averageOrderValueStep = roundUpToNiceStep(averageOrderValueMax / 2);
const averageOrderValueSuggestedMax = averageOrderValueStep * 2;

const sessionsTrend = cardsData?.sessionsTrend || [];
const sessionsGranularity = cardsData?.sessionsTrendGranularity || "day";

const sessionsLabels = sessionsTrend.map((point, index) =>
  formatAxisLabel(
    point,
    index,
    sessionsTrend,
    sessionsGranularity,
    "small"
  )
);

const sessionsValues = sessionsTrend.map((point) =>
  Number(point.sessions || 0)
);

const compareSessionsGranularity =
  cardsData?.comparison?.sessionsTrendGranularity || sessionsGranularity;

const compareSessionsTrend = normalizeComparisonTrendToMainLength(
  cardsData?.comparison?.sessionsTrend || [],
  cardsData?.comparison?.from,
  cardsData?.comparison?.to,
  compareSessionsGranularity,
  ["sessions"],
  sessionsTrend.length
);

const compareSessionsValues = compareSessionsTrend.map((point) =>
  Number(point.sessions || 0)
);

const canOverlaySessionsCompare =
  Boolean(cardsData?.comparison) &&
  compareSessionsTrend.length > 0 &&
  compareSessionsGranularity === sessionsGranularity &&
  (
    (sessionsGranularity === "hour" &&
      mainChartRangeDays === 1 &&
      compareChartRangeDays === 1) ||
    (sessionsGranularity === "day" &&
      mainChartRangeDays > 1 &&
      compareChartRangeDays === mainChartRangeDays)
  );

const alignedCompareSessions = canOverlaySessionsCompare
  ? sessionsLabels.map((_, index) => compareSessionsValues[index] ?? null)
  : [];

const sessionsTotal = Number(cardsData?.sessions || 0);
const compareSessionsTotal = Number(cardsData?.comparison?.sessions || 0);

const sessionsDelta = getDelta(
  sessionsTotal,
  compareSessionsTotal
);

const sessionsMax = Math.max(
  ...sessionsValues,
  ...compareSessionsValues,
  sessionsTotal,
  0
);

// ✅ 3 y-axis rows: 0, middle, max
const sessionsStep = roundUpToNiceStep(sessionsMax / 2);
const sessionsSuggestedMax = sessionsStep * 2;

const conversionRateTrend = cardsData?.conversionRateTrend || [];
const conversionRateGranularity =
  cardsData?.conversionRateTrendGranularity || "day";

const conversionRateLabels = conversionRateTrend.map((point, index) =>
  formatAxisLabel(
    point,
    index,
    conversionRateTrend,
    conversionRateGranularity,
    "small"
  )
);

const conversionRateValues = conversionRateTrend.map((point) =>
  Number(point.conversionRate || 0)
);

const compareConversionRateGranularity =
  cardsData?.comparison?.conversionRateTrendGranularity ||
  conversionRateGranularity;

const compareConversionRateTrend = normalizeComparisonTrendToMainLength(
  cardsData?.comparison?.conversionRateTrend || [],
  cardsData?.comparison?.from,
  cardsData?.comparison?.to,
  compareConversionRateGranularity,
  ["conversionRate", "activeSessions", "convertedSessions"],
  conversionRateTrend.length
);

const compareConversionRateValues = compareConversionRateTrend.map((point) =>
  Number(point.conversionRate || 0)
);

const canOverlayConversionRateCompare =
  Boolean(cardsData?.comparison) &&
  compareConversionRateTrend.length > 0 &&
  compareConversionRateGranularity === conversionRateGranularity &&
  (
    (conversionRateGranularity === "hour" &&
      mainChartRangeDays === 1 &&
      compareChartRangeDays === 1) ||
    (conversionRateGranularity === "day" &&
      mainChartRangeDays > 1 &&
      compareChartRangeDays === mainChartRangeDays)
  );

const alignedCompareConversionRate = canOverlayConversionRateCompare
  ? conversionRateLabels.map(
      (_, index) => compareConversionRateValues[index] ?? null
    )
  : [];

const conversionRate = Number(cardsData?.conversionRate || 0);
const compareConversionRate = Number(
  cardsData?.comparison?.conversionRate || 0
);

const conversionRateDelta = getDelta(
  conversionRate,
  compareConversionRate
);

const conversionRateMax = Math.max(
  ...conversionRateValues,
  ...compareConversionRateValues,
  conversionRate,
  0
);

const conversionRateStep =
  conversionRateMax <= 0 ? 50 : roundUpToNiceStep(conversionRateMax / 2);

const conversionRateSuggestedMax =
  conversionRateMax <= 0
    ? 100
    : Math.min(100, Math.max(2, conversionRateStep * 2));

const mainChartFooterLabel = (() => {
  const start = mainStart || new Date();
  const end = mainEnd || start;

  const format = (d) =>
    d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  if (start.toDateString() === end.toDateString()) {
    return format(start);
  }

  return `${format(start)} → ${format(end)}`;
})();

const customerCohorts = cardsData?.customerCohorts || [];

const getCohortCellStyle = (rate, hasData) => {
  if (!hasData) {
    return {
      backgroundColor: "#F1F2F4",
      color: "#8A8A8A",
    };
  }

  const safeRate = Math.max(0, Math.min(Number(rate || 0), 100));

  // ✅ Shopify-style two-level heatmap:
  // light blue = low/normal retention
  // dark blue = strong retention
  if (safeRate >= 50) {
    return {
      backgroundColor: "#0EA5E9",
      color: "#FFFFFF",
    };
  }

  return {
    backgroundColor: "#E0F2FE",
    color: "#075985",
  };
};

const salesBreakdown = cardsData?.salesBreakdown || {
  grossSales: 0,
  discounts: 0,
  returns: 0,
  netSales: 0,
  shippingCharges: 0,
  additionalFees: 0,
  totalSales: 0,
};

const compareSalesBreakdown = cardsData?.comparison?.salesBreakdown || null;

const conversionBreakdown = cardsData?.conversionBreakdown || {
  sessions: 0,
  addedToCart: 0,
  reachedCheckout: 0,
  completed: 0,
  sessionsRate: 0,
  addedToCartRate: 0,
  reachedCheckoutRate: 0,
  completedRate: 0,
};

const compareConversionBreakdown =
  cardsData?.comparison?.conversionBreakdown || null;

  const sessionsByDeviceRaw = cardsData?.sessionsByDevice || [];
const compareSessionsByDeviceRaw =
  cardsData?.comparison?.sessionsByDevice || [];

const deviceLabelMap = {
  mobile: lang === "AR" ? "الهاتف" : "Mobile",
  desktop: lang === "AR" ? "الكمبيوتر" : "Desktop",
  tablet: lang === "AR" ? "التابلت" : "Tablet",
  unknown: lang === "AR" ? "غير معروف" : "Unknown",
};

const deviceOrder = ["mobile", "desktop", "tablet", "unknown"];

const deviceColorMap = {
  mobile: "#06B6D4",
  desktop: "#8B5CF6",
  tablet: "#F59E0B",
  unknown: "#9CA3AF",
};

const sessionsByDeviceMap = new Map(
  sessionsByDeviceRaw.map((item) => [item.device, item])
);

const compareSessionsByDeviceMap = new Map(
  compareSessionsByDeviceRaw.map((item) => [item.device, item])
);

const sessionsByDevice = deviceOrder.map((device) => {
  const item = sessionsByDeviceMap.get(device);
  const compareItem = compareSessionsByDeviceMap.get(device);

  return {
    device,
    label: deviceLabelMap[device] || device,
    sessions: Number(item?.sessions || 0),
    percent: Number(item?.percent || 0),
    compareSessions: Number(compareItem?.sessions || 0),
    comparePercent: Number(compareItem?.percent || 0),
  };
});

const sessionsByDeviceTotal = sessionsByDevice.reduce(
  (sum, item) => sum + Number(item.sessions || 0),
  0
);

const compareSessionsByDeviceTotal = sessionsByDevice.reduce(
  (sum, item) => sum + Number(item.compareSessions || 0),
  0
);

const sessionsByDeviceMax = Math.max(
  ...sessionsByDevice.map((item) => Number(item.sessions || 0)),
  0
);

const getDeviceBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (sessionsByDeviceMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / sessionsByDeviceMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const sessionsByLocationRaw = cardsData?.sessionsByLocation || [];
const compareSessionsByLocationRaw =
  cardsData?.comparison?.sessionsByLocation || [];

const compareSessionsByLocationMap = new Map(
  compareSessionsByLocationRaw.map((item) => [item.country, item])
);

const sessionsByLocation = sessionsByLocationRaw.map((item) => {
  const compareItem = compareSessionsByLocationMap.get(item.country);

  return {
    ...item,
    label:
      item.country === "Unknown"
        ? lang === "AR"
          ? "غير معروف"
          : "Unknown"
        : item.country,
    sessions: Number(item.sessions || 0),
    percent: Number(item.percent || 0),
    compareSessions: Number(compareItem?.sessions || 0),
    comparePercent: Number(compareItem?.percent || 0),
    cities: Array.isArray(item.cities) ? item.cities : [],
  };
});

const sessionsByLocationTotal = sessionsByLocation.reduce(
  (sum, item) => sum + Number(item.sessions || 0),
  0
);

const sessionsByLocationMax = Math.max(
  ...sessionsByLocation.map((item) => Number(item.sessions || 0)),
  0
);

const getLocationBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (sessionsByLocationMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / sessionsByLocationMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const locationTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  country: lang === "AR" ? "الدولة" : "Country",
  sessions: lang === "AR" ? "الجلسات" : "Sessions",
  percent: lang === "AR" ? "النسبة" : "Percent",
  cities: lang === "AR" ? "المدن" : "Cities",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const locationColorPalette = [
  "#06B6D4",
  "#8B5CF6",
  "#F59E0B",
  "#22C55E",
  "#9CA3AF",
];

const sessionsByLocationWithColors = sessionsByLocation.map((item, index) => ({
  ...item,
  color: locationColorPalette[index] || "#9CA3AF",
}));

const sessionsByLandingPageRaw = cardsData?.sessionsByLandingPage || [];
const compareSessionsByLandingPageRaw =
  cardsData?.comparison?.sessionsByLandingPage || [];

const compareSessionsByLandingPageMap = new Map(
  compareSessionsByLandingPageRaw.map((item) => [item.landingPage, item])
);

const sessionsByLandingPage = sessionsByLandingPageRaw.map((item) => {
  const compareItem = compareSessionsByLandingPageMap.get(item.landingPage);

  return {
    ...item,
    label: item.label || item.landingPage || (lang === "AR" ? "غير معروف" : "Unknown"),
    landingPage: item.landingPage || "",
    sessions: Number(item.sessions || 0),
    percent: Number(item.percent || 0),
    compareSessions: Number(compareItem?.sessions || 0),
    comparePercent: Number(compareItem?.percent || 0),
  };
});

const sessionsByLandingPageTotal = sessionsByLandingPage.reduce(
  (sum, item) => sum + Number(item.sessions || 0),
  0
);

const sessionsByLandingPageMax = Math.max(
  ...sessionsByLandingPage.map((item) => Number(item.sessions || 0)),
  0
);

const getLandingPageBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (sessionsByLandingPageMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / sessionsByLandingPageMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const landingPageTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  page: lang === "AR" ? "صفحة الدخول" : "Landing page",
  url: lang === "AR" ? "الرابط" : "URL",
  sessions: lang === "AR" ? "الجلسات" : "Sessions",
  percent: lang === "AR" ? "النسبة" : "Percent",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const channelLabelMap = {
  website: lang === "AR" ? "الموقع الإلكتروني" : "Website",
  whatsapp: lang === "AR" ? "واتساب" : "WhatsApp",
  store: lang === "AR" ? "المتجر" : "Store",
  unknown: lang === "AR" ? "غير معروف" : "Unknown",
};

const channelOrder = ["website", "whatsapp", "store"];

const salesByChannelRaw = cardsData?.salesByChannel || [];

const salesByChannelMap = new Map(
  salesByChannelRaw.map((item) => [item.channel, item])
);

const compareSalesByChannelRaw =
  cardsData?.comparison?.salesByChannel || [];

const compareSalesByChannelMap = new Map(
  compareSalesByChannelRaw.map((item) => [item.channel, item])
);

const salesByChannel = channelOrder.map((channel) => {
  const item = salesByChannelMap.get(channel);
  const compareItem = compareSalesByChannelMap.get(channel);

  return {
    channel,
    label: channelLabelMap[channel] || channel,
    orders: Number(item?.orders || 0),
    totalSales: Number(item?.totalSales || 0),

    compareOrders: Number(compareItem?.orders || 0),
    compareTotalSales: Number(compareItem?.totalSales || 0),
  };
});

const salesByChannelMax = Math.max(
  ...salesByChannel.map((item) => item.totalSales),
  0
);

const salesByChannelTotal = salesByChannel.reduce(
  (sum, item) => sum + Number(item.totalSales || 0),
  0
);

const formatReferrerLabel = (value) => {
  const raw = String(value || "").trim();

  if (!raw || raw === "direct" || raw === "unknown") {
    return lang === "AR" ? "مباشر / غير معروف" : "Direct / Unknown";
  }

  const labelMap = {
    instagram: "Instagram",
    facebook: "Facebook",
    google: "Google",
    tiktok: "TikTok",
    whatsapp: "WhatsApp",
    youtube: "YouTube",
  };

  return labelMap[raw.toLowerCase()] || raw;
};

const salesByReferrerRaw = cardsData?.salesByReferrer || [];
const compareSalesByReferrerRaw =
  cardsData?.comparison?.salesByReferrer || [];

const compareSalesByReferrerMap = new Map(
  compareSalesByReferrerRaw.map((item) => [item.referrer, item])
);

const salesByReferrer = salesByReferrerRaw.map((item) => {
  const compareItem = compareSalesByReferrerMap.get(item.referrer);

  return {
    referrer: item.referrer,
    label: formatReferrerLabel(item.referrer),
    orders: Number(item.orders || 0),
    totalSales: Number(item.totalSales || 0),
    compareOrders: Number(compareItem?.orders || 0),
    compareTotalSales: Number(compareItem?.totalSales || 0),
  };
});

const salesByReferrerTotal = salesByReferrer.reduce(
  (sum, item) => sum + Number(item.totalSales || 0),
  0
);

const salesByReferrerMax = Math.max(
  ...salesByReferrer.map((item) => Number(item.totalSales || 0)),
  0
);

const getSalesReferrerBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (salesByReferrerMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / salesByReferrerMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const salesReferrerTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  referrer: lang === "AR" ? "المصدر" : "Referrer",
  totalOrders: lang === "AR" ? "إجمالي الطلبات" : "Total orders",
  amount: lang === "AR" ? "المبلغ" : "Amount",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const formatSocialReferrerLabel = (value) => {
  const raw = String(value || "").trim().toLowerCase();

  const labelMap = {
    instagram: "Instagram",
    facebook: "Facebook",
    tiktok: "TikTok",
    youtube: "YouTube",
    whatsapp: "WhatsApp",
    pinterest: "Pinterest",
    snapchat: "Snapchat",
    unknown: lang === "AR" ? "غير معروف" : "Unknown",
  };

  return labelMap[raw] || value || (lang === "AR" ? "غير معروف" : "Unknown");
};

const sessionsBySocialReferrerRaw =
  cardsData?.sessionsBySocialReferrer || [];

const compareSessionsBySocialReferrerRaw =
  cardsData?.comparison?.sessionsBySocialReferrer || [];

const compareSessionsBySocialReferrerMap = new Map(
  compareSessionsBySocialReferrerRaw.map((item) => [item.referrer, item])
);

const sessionsBySocialReferrer = sessionsBySocialReferrerRaw.map((item) => {
  const compareItem = compareSessionsBySocialReferrerMap.get(item.referrer);

  return {
    referrer: item.referrer,
    label: formatSocialReferrerLabel(item.referrer),
    sessions: Number(item.sessions || 0),
    percent: Number(item.percent || 0),
    compareSessions: Number(compareItem?.sessions || 0),
    comparePercent: Number(compareItem?.percent || 0),
  };
});

const sessionsBySocialReferrerTotal = sessionsBySocialReferrer.reduce(
  (sum, item) => sum + Number(item.sessions || 0),
  0
);

const sessionsBySocialReferrerMax = Math.max(
  ...sessionsBySocialReferrer.map((item) => Number(item.sessions || 0)),
  0
);

const getSocialReferrerBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (sessionsBySocialReferrerMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / sessionsBySocialReferrerMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const socialReferrerTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  referrer: lang === "AR" ? "المصدر الاجتماعي" : "Social referrer",
  sessions: lang === "AR" ? "الجلسات" : "Sessions",
  percent: lang === "AR" ? "النسبة" : "Percent",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const salesBySocialReferrerRaw =
  cardsData?.salesBySocialReferrer || [];

const compareSalesBySocialReferrerRaw =
  cardsData?.comparison?.salesBySocialReferrer || [];

const compareSalesBySocialReferrerMap = new Map(
  compareSalesBySocialReferrerRaw.map((item) => [item.referrer, item])
);

const salesBySocialReferrer = salesBySocialReferrerRaw.map((item) => {
  const compareItem = compareSalesBySocialReferrerMap.get(item.referrer);

  return {
    referrer: item.referrer,
    label: formatSocialReferrerLabel(item.referrer),
    orders: Number(item.orders || 0),
    totalSales: Number(item.totalSales || 0),
    compareOrders: Number(compareItem?.orders || 0),
    compareTotalSales: Number(compareItem?.totalSales || 0),
  };
});

const salesBySocialReferrerTotal = salesBySocialReferrer.reduce(
  (sum, item) => sum + Number(item.totalSales || 0),
  0
);

const salesBySocialReferrerMax = Math.max(
  ...salesBySocialReferrer.map((item) => Number(item.totalSales || 0)),
  0
);

const getSalesSocialReferrerBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (salesBySocialReferrerMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / salesBySocialReferrerMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const salesSocialReferrerTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  referrer: lang === "AR" ? "المصدر الاجتماعي" : "Social referrer",
  totalOrders: lang === "AR" ? "إجمالي الطلبات" : "Total orders",
  amount: lang === "AR" ? "المبلغ" : "Amount",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const sessionsByReferrerRaw =
  cardsData?.sessionsByReferrer || [];

const compareSessionsByReferrerRaw =
  cardsData?.comparison?.sessionsByReferrer || [];

const compareSessionsByReferrerMap = new Map(
  compareSessionsByReferrerRaw.map((item) => [item.referrer, item])
);

const sessionsByReferrer = sessionsByReferrerRaw.map((item) => {
  const compareItem = compareSessionsByReferrerMap.get(item.referrer);

  return {
    referrer: item.referrer,
    label: formatReferrerLabel(item.referrer),
    sessions: Number(item.sessions || 0),
    percent: Number(item.percent || 0),
    compareSessions: Number(compareItem?.sessions || 0),
    comparePercent: Number(compareItem?.percent || 0),
  };
});

const sessionsByReferrerTotal = sessionsByReferrer.reduce(
  (sum, item) => sum + Number(item.sessions || 0),
  0
);

const sessionsByReferrerMax = Math.max(
  ...sessionsByReferrer.map((item) => Number(item.sessions || 0)),
  0
);

const getSessionsReferrerBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (sessionsByReferrerMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / sessionsByReferrerMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const sessionsReferrerTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  referrer: lang === "AR" ? "المصدر" : "Referrer",
  sessions: lang === "AR" ? "الجلسات" : "Sessions",
  percent: lang === "AR" ? "النسبة" : "Percent",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const formatReferringChannelLabel = (value) => {
  const raw = String(value || "").trim().toLowerCase();

  const labelMap = {
    social: lang === "AR" ? "اجتماعي" : "Social",
    search: lang === "AR" ? "بحث" : "Search",
    direct: lang === "AR" ? "مباشر / غير معروف" : "Direct / Unknown",
    referral: lang === "AR" ? "إحالة" : "Referral",
    email: lang === "AR" ? "البريد الإلكتروني" : "Email",
    paid: lang === "AR" ? "مدفوع" : "Paid",
  };

  return labelMap[raw] || value || (lang === "AR" ? "غير معروف" : "Unknown");
};

const performanceByReferringChannelRaw =
  cardsData?.performanceByReferringChannel || [];

const comparePerformanceByReferringChannelRaw =
  cardsData?.comparison?.performanceByReferringChannel || [];

const comparePerformanceByReferringChannelMap = new Map(
  comparePerformanceByReferringChannelRaw.map((item) => [item.channel, item])
);

const performanceByReferringChannel = performanceByReferringChannelRaw.map(
  (item) => {
    const compareItem = comparePerformanceByReferringChannelMap.get(
      item.channel
    );

    return {
      channel: item.channel,
      label: formatReferringChannelLabel(item.channel),
      sessions: Number(item.sessions || 0),
      orders: Number(item.orders || 0),
      totalSales: Number(item.totalSales || 0),
      conversionRate: Number(item.conversionRate || 0),
      averageOrderValue: Number(item.averageOrderValue || 0),

      compareSessions: Number(compareItem?.sessions || 0),
      compareOrders: Number(compareItem?.orders || 0),
      compareTotalSales: Number(compareItem?.totalSales || 0),
      compareConversionRate: Number(compareItem?.conversionRate || 0),
      compareAverageOrderValue: Number(compareItem?.averageOrderValue || 0),
    };
  }
);

const performanceByReferringChannelTotal =
  performanceByReferringChannel.reduce(
    (sum, item) => sum + Number(item.sessions || 0) + Number(item.orders || 0),
    0
  );

const performanceByReferringChannelMax = Math.max(
  ...performanceByReferringChannel.map((item) => Number(item.totalSales || 0)),
  0
);

const getPerformanceChannelBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (performanceByReferringChannelMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / performanceByReferringChannelMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const performanceChannelTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  channel: lang === "AR" ? "القناة" : "Channel",
  sessions: lang === "AR" ? "الجلسات" : "Sessions",
  orders: lang === "AR" ? "الطلبات" : "Orders",
  conversionRate: lang === "AR" ? "معدل التحويل" : "Conversion rate",
  averageOrderValue: lang === "AR" ? "متوسط الطلب" : "Average order value",
  sales: lang === "AR" ? "المبيعات" : "Sales",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const getSalesChannelBarWidth = (value) => {
  const numericValue = Number(value || 0);

  // If all channels are 0, still show a very small visible line.
  if (salesByChannelMax <= 0) return "4%";

  // If this specific channel is 0, keep it smaller than real values.
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / salesByChannelMax) * 100;

  // Keep room for the price after the bar.
  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const salesChannelTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  source: lang === "AR" ? "المصدر" : "Source",
  totalOrders: lang === "AR" ? "إجمالي الطلبات" : "Total orders",
  amount: lang === "AR" ? "المبلغ" : "Amount",
};

const formatSalesChannelDateLabel = (start, end) => {
  const safeStart = start || new Date().toISOString().slice(0, 10);
  const safeEnd = end || safeStart;

  const formatter = new Intl.DateTimeFormat(
    lang === "AR" ? "ar" : "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  );

  const startLabel = formatter.format(new Date(`${safeStart}T00:00:00`));
  const endLabel = formatter.format(new Date(`${safeEnd}T00:00:00`));

  return safeStart === safeEnd
    ? startLabel
    : `${startLabel} - ${endLabel}`;
};

const salesChannelDateLabel = formatSalesChannelDateLabel(
  mainStart ? formatLocalDate(mainStart) : undefined,
  mainEnd ? formatLocalDate(mainEnd) : undefined
);

const salesByProductRaw = cardsData?.salesByProduct || [];
const compareSalesByProductRaw = cardsData?.comparison?.salesByProduct || [];

const getProductKey = (item) => {
  return String(item.productId || item.publicId || item.name_en || item.name_ar || "");
};

const compareSalesByProductMap = new Map(
  compareSalesByProductRaw.map((item) => [getProductKey(item), item])
);

const salesByProduct = salesByProductRaw.map((item) => {
  const compareItem = compareSalesByProductMap.get(getProductKey(item));

  return {
    ...item,
    label: lang === "AR" ? item.name_ar : item.name_en,
    compareOrders: Number(compareItem?.orders || 0),
    compareUnitsSold: Number(compareItem?.unitsSold || 0),
    compareTotalSales: Number(compareItem?.totalSales || 0),
  };
});

const visibleSalesByProduct = salesByProduct.slice(0, 4);

const salesByProductMax = Math.max(
  ...salesByProduct.map((item) => Number(item.totalSales || 0)),
  0
);

const salesByProductTotal = salesByProduct.reduce(
  (sum, item) => sum + Number(item.totalSales || 0),
  0
);

const getSalesProductBarWidth = (value) => {
  const numericValue = Number(value || 0);

  if (salesByProductMax <= 0) return "4%";
  if (numericValue === 0) return "4%";

  const rawPercent = (numericValue / salesByProductMax) * 100;

  return `${Math.min(Math.max(rawPercent, 12), 72)}%`;
};

const salesProductTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  product: lang === "AR" ? "المنتج" : "Product",
  totalOrders: lang === "AR" ? "إجمالي الطلبات" : "Total orders",
  unitsSold: lang === "AR" ? "الوحدات المباعة" : "Units sold",
  amount: lang === "AR" ? "المبلغ" : "Amount",
};

const topSellingProductsRaw = cardsData?.topSellingProducts || [];
const compareTopSellingProductsRaw =
  cardsData?.comparison?.topSellingProducts || [];

const compareTopSellingProductsMap = new Map(
  compareTopSellingProductsRaw.map((item) => [getProductKey(item), item])
);

const topSellingProducts = topSellingProductsRaw.map((item) => {
  const compareItem = compareTopSellingProductsMap.get(getProductKey(item));

  return {
    ...item,
    label: lang === "AR" ? item.name_ar : item.name_en,
    image: item.image || "/images/placeholder-product.jpg",
    compareOrders: Number(compareItem?.orders || 0),
    compareUnitsSold: Number(compareItem?.unitsSold || 0),
  };
});

const topSellingProductsTotal = topSellingProducts.reduce(
  (sum, item) => sum + Number(item.unitsSold || 0),
  0
);

const topSellingTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  product: lang === "AR" ? "المنتج" : "Product",
  totalOrders: lang === "AR" ? "إجمالي الطلبات" : "Total orders",
  unitsSold: lang === "AR" ? "الوحدات المباعة" : "Units sold",
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const productsBySellThroughRateRaw =
  cardsData?.productsBySellThroughRate || [];

const compareProductsBySellThroughRateRaw =
  cardsData?.comparison?.productsBySellThroughRate || [];

const compareProductsBySellThroughRateMap = new Map(
  compareProductsBySellThroughRateRaw.map((item) => [
    getProductKey(item),
    item,
  ])
);

const productsBySellThroughRate = productsBySellThroughRateRaw.map((item) => {
  const compareItem = compareProductsBySellThroughRateMap.get(
    getProductKey(item)
  );

  return {
    ...item,
    label: lang === "AR" ? item.name_ar : item.name_en,
    image: item.image || "/images/placeholder-product.jpg",
    unitsSold: Number(item.unitsSold || 0),
    endingInventory: Number(item.endingInventory || 0),
    sellThroughRate: Number(item.sellThroughRate || 0),

    compareUnitsSold: Number(compareItem?.unitsSold || 0),
    compareEndingInventory: Number(compareItem?.endingInventory || 0),
    compareSellThroughRate: Number(compareItem?.sellThroughRate || 0),
  };
});

const productsBySellThroughRateTotal = productsBySellThroughRate.reduce(
  (sum, item) => sum + Number(item.unitsSold || 0),
  0
);

const sellThroughTooltipText = {
  date: lang === "AR" ? "التاريخ" : "Date",
  product: lang === "AR" ? "المنتج" : "Product",
  sellThroughRate: TEXT.sellThroughRate,
  unitsSold: TEXT.inventoryUnitsSold,
  endingInventory: TEXT.endingInventoryUnits,
  comparison: lang === "AR" ? "المقارنة" : "Comparison",
};

const SalesChannelDelta = ({ current, previous }) => {
  if (!cardsData?.comparison) return null;

  const delta = getDelta(current, previous);

  return (
    <span className="inline-flex items-center gap-[3px] text-[10px] font-semibold whitespace-nowrap">
      {delta.fromZero ? (
        <svg
          viewBox="0 0 20 20"
          className="w-[11px] h-[11px] text-[#5aa36b]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 16V4" />
          <path d="M5 9l5-5 5 5" />
        </svg>
      ) : delta.hideComparison || delta.percent === null ? (
        <span className="text-gray-400">-</span>
      ) : delta.direction === "up" ? (
        <>
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>

          <span className="text-gray-700">{delta.percent}%</span>
        </>
      ) : delta.direction === "down" ? (
        <>
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#dc6868]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 4v12" />
            <path d="M5 11l5 5 5-5" />
          </svg>

          <span className="text-gray-700">{delta.percent}%</span>
        </>
      ) : (
        <span className="text-gray-400">-</span>
      )}
    </span>
  );
};

const BreakdownDelta = ({ current, previous }) => {
  if (!cardsData?.comparison || !compareSalesBreakdown) return null;

  const delta = getDelta(current, previous);

  return (
    <div className="inline-flex items-center gap-1.5 text-[10px] whitespace-nowrap">
      <span className="text-gray-400">
        {formatChartMoney(previous)}
      </span>

      {delta.fromZero ? (
        <span className="inline-flex items-center font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>
        </span>
      ) : delta.hideComparison || delta.percent === null ? (
        <span className="text-gray-400 font-semibold">-</span>
      ) : delta.direction === "up" ? (
        <span className="inline-flex items-center gap-[2px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>

          <span className="text-gray-700">{delta.percent}%</span>
        </span>
      ) : delta.direction === "down" ? (
        <span className="inline-flex items-center gap-[2px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#dc6868]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 4v12" />
            <path d="M5 11l5 5 5-5" />
          </svg>

          <span className="text-gray-700">{delta.percent}%</span>
        </span>
      ) : (
        <span className="text-gray-400 font-semibold">-</span>
      )}
    </div>
  );
};

const FunnelDelta = ({ current, previous }) => {
  if (!cardsData?.comparison || !compareConversionBreakdown) return null;

  const delta = getDelta(current, previous);

  return (
    <span className="inline-flex items-center justify-center h-4 text-[10px] font-semibold whitespace-nowrap">
      {delta.fromZero ? (
        <svg
          viewBox="0 0 20 20"
          className="w-[11px] h-[11px] text-[#5aa36b]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 16V4" />
          <path d="M5 9l5-5 5 5" />
        </svg>
      ) : delta.hideComparison || delta.percent === null ? (
        <span className="text-gray-400">-</span>
      ) : delta.direction === "up" ? (
        <span className="inline-flex items-center gap-[2px]">
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>
          <span className="text-gray-700">{delta.percent}%</span>
        </span>
      ) : delta.direction === "down" ? (
        <span className="inline-flex items-center gap-[2px]">
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#dc6868]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 4v12" />
            <path d="M5 11l5 5 5-5" />
          </svg>
          <span className="text-gray-700">{delta.percent}%</span>
        </span>
      ) : (
        <span className="text-gray-400">-</span>
      )}
    </span>
  );
};

const DeviceDelta = ({ current, previous }) => {
  if (!cardsData?.comparison) return null;

  const delta = getDelta(current, previous);

  return (
    <span className="inline-flex items-center gap-[3px] text-[10px] font-semibold whitespace-nowrap">
      {delta.fromZero ? (
        <svg
          viewBox="0 0 20 20"
          className="w-[11px] h-[11px] text-[#5aa36b]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 16V4" />
          <path d="M5 9l5-5 5 5" />
        </svg>
      ) : delta.hideComparison || delta.percent === null ? (
        <span className="text-gray-400">-</span>
      ) : delta.direction === "up" ? (
        <>
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>

          <span className="text-gray-700">{delta.percent}%</span>
        </>
      ) : delta.direction === "down" ? (
        <>
          <svg
            viewBox="0 0 20 20"
            className="w-[11px] h-[11px] text-[#dc6868]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 4v12" />
            <path d="M5 11l5 5 5-5" />
          </svg>

          <span className="text-gray-700">{delta.percent}%</span>
        </>
      ) : (
        <span className="text-gray-400">-</span>
      )}
    </span>
  );
};

const DeviceDonutChart = ({
  items = [],
  total = 0,
  valueKey = "sessions",
  percentKey = "percent",
  size = 150,
  stroke = 26,
}) => {
  const safeTotal = Number(total || 0);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  let accumulatedPercent = 0;

  return (
    <div className="relative flex items-center justify-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F3F4F6"
          strokeWidth={stroke}
        />

        {safeTotal > 0 &&
          items.map((item) => {
            const percent = Math.max(0, Number(item[percentKey] || 0));
            const dashLength = (percent / 100) * circumference;
            const dashOffset =
              circumference - (accumulatedPercent / 100) * circumference;

            accumulatedPercent += percent;

            if (percent <= 0) return null;

            return (
              <circle
                key={`${item.device}-${valueKey}`}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={deviceColorMap[item.device] || "#9CA3AF"}
                strokeWidth={stroke}
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset}
                strokeLinecap="butt"
                className="transition-all duration-300"
              />
            );
          })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <p className="text-[20px] font-semibold text-gray-900 leading-none">
          {safeTotal}
        </p>
        <p className="text-[10px] text-gray-500 mt-1">
          {TEXT.sessionsShort}
        </p>
      </div>
    </div>
  );
};

const LocationDonutChart = ({
  items = [],
  total = 0,
  percentKey = "percent",
  size = 150,
  stroke = 26,
}) => {
  const safeTotal = Number(total || 0);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  let accumulatedPercent = 0;

  return (
    <div className="relative flex items-center justify-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F3F4F6"
          strokeWidth={stroke}
        />

        {safeTotal > 0 &&
          items.map((item) => {
            const percent = Math.max(0, Number(item[percentKey] || 0));
            const dashLength = (percent / 100) * circumference;
            const dashOffset =
              circumference - (accumulatedPercent / 100) * circumference;

            accumulatedPercent += percent;

            if (percent <= 0) return null;

            return (
              <circle
                key={item.country}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={item.color || "#9CA3AF"}
                strokeWidth={stroke}
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset}
                strokeLinecap="butt"
                className="transition-all duration-300"
              />
            );
          })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <p className="text-[20px] font-semibold text-gray-900 leading-none">
          {safeTotal}
        </p>
        <p className="text-[10px] text-gray-500 mt-1">
          {TEXT.sessionsShort}
        </p>
      </div>
    </div>
  );
};

const ProgressRing = ({
  value = 0,
  color = "#06B6D4",
  size = 44,
  stroke = 4,
}) => {
  const safeValue = Math.max(0, Math.min(Number(value || 0), 100));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safeValue / 100) * circumference;

  return (
    <div className="flex items-center justify-center">
      <div
        className="relative"
        style={{
          width: size,
          height: size,
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={stroke}
          />

          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.35s ease" }}
          />
        </svg>

        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-gray-700">
          {safeValue.toFixed(0)}%
        </div>
      </div>
    </div>
  );
};

const BreakdownRowTooltip = ({ formula }) => (
  <div
    className="
      hidden sm:block
      pointer-events-none
      opacity-0 group-hover/label:opacity-100
      translate-y-1 group-hover/label:translate-y-0
      transition-all duration-200
      absolute
      left-1/2 -translate-x-1/2
      bottom-full mb-2
      w-[min(270px,calc(100vw-32px))]
      bg-white
      border border-gray-200
      rounded-xl
      shadow-lg
      p-3
      z-[80]
    "
  >
    <p className="text-[11px] font-mono text-green-800 leading-relaxed tracking-wide">
      {formula}
    </p>

    <div
      className="
        absolute left-1/2 -translate-x-1/2
        top-full w-3 h-3 bg-white rotate-45 -mt-[6px]
      "
    />
  </div>
);

const AnalyticsSkeleton = () => (
  <div
    className={`
      flex bg-[#f1f1f1] min-h-screen w-full max-w-full overflow-x-hidden
      ${isOpen ? "overflow-hidden" : ""}
    `}
  >
    <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

    <div
      className={`
        flex-1 min-w-0 w-full max-w-full
        ${lang === "AR" ? "" : "lg:ml-64"}
        transition-all duration-300 overflow-x-hidden
      `}
    >
      <AdminHeader setIsOpen={setIsOpen} />

      <main className="w-full max-w-full min-w-0 p-2 sm:p-4 lg:p-6 overflow-x-hidden">
        {/* HEADER SKELETON */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gray-200 animate-pulse" />
            <div className="w-44 h-6 rounded-md bg-gray-200 animate-pulse" />
          </div>

          <div className="w-24 h-8 rounded-lg bg-gray-300 animate-pulse" />
        </div>

        {/* FILTERS SKELETON */}
        <div className="flex items-center gap-1.5 sm:gap-3 mb-6">
          <div className="flex-1 sm:flex-none sm:w-28 h-8 rounded-lg bg-gray-200 animate-pulse" />
          <div className="flex-1 sm:flex-none sm:w-40 h-8 rounded-lg bg-gray-200 animate-pulse" />
          <div className="flex-1 sm:flex-none sm:w-24 h-8 rounded-lg bg-gray-200 animate-pulse" />
        </div>

        {/* TOP CARDS SKELETON */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
            >
              <div className="w-28 h-4 rounded bg-gray-200 animate-pulse mb-3" />
              <div className="flex items-center justify-between gap-3">
                <div className="w-20 h-5 rounded bg-gray-200 animate-pulse" />
                <div className="w-20 h-8 rounded bg-gray-100 animate-pulse" />
              </div>
            </div>
          ))}
        </div>

        {/* MAIN CARDS SKELETON */}
        <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-3 sm:gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 h-[360px] md:h-[420px]">
            <div className="w-40 h-4 rounded bg-gray-200 animate-pulse mb-4" />
            <div className="w-28 h-6 rounded bg-gray-200 animate-pulse mb-6" />
            <div className="h-[250px] rounded-xl bg-gray-100 animate-pulse" />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 h-[360px] md:h-[420px]">
            <div className="w-40 h-4 rounded bg-gray-200 animate-pulse mb-5" />

            <div className="space-y-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between gap-4">
                  <div className="w-28 h-4 rounded bg-gray-200 animate-pulse" />
                  <div className="w-20 h-4 rounded bg-gray-200 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SECONDARY CARDS SKELETON */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 h-[250px] sm:h-[300px]"
            >
              <div className="w-40 h-4 rounded bg-gray-200 animate-pulse mb-5" />
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, j) => (
                  <div key={j}>
                    <div className="w-24 h-3 rounded bg-gray-200 animate-pulse mb-2" />
                    <div className="flex items-center gap-2">
                      <div className="h-4 flex-1 rounded bg-gray-100 animate-pulse" />
                      <div className="w-14 h-3 rounded bg-gray-200 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  </div>
);

  if (loading || !data) {
  return <AnalyticsSkeleton />;
}

  const maxValue = Math.max(...(data.daily?.sales || [0]));
  
const dayComparison = data.dayComparison || {
  today: 0,
  yesterday: 0,
  percent: 0,
};

const monthComparison = data.monthComparison || {
  thisMonth: 0,
  lastMonth: 0,
  percent: 0,
};

const yearComparison = data.yearComparison || {
  thisYear: 0,
  lastYear: 0,
  percent: 0,
};


  // ✅ SAFE TOTALS (data is guaranteed here)
const dailyTotal = formatTotal(
  metric === "orders"
    ? calculateTotal(data.daily?.orders || [])
    : calculateTotal(resolveValues(data.daily))
);

const weeklyTotal = formatTotal(
  metric === "orders"
    ? calculateTotal(data.weekly?.orders || [])
    : calculateTotal(resolveValues(data.weekly))
);

const monthlyTotal = formatTotal(
  metric === "orders"
    ? calculateTotal(data.monthly?.orders || [])
    : calculateTotal(resolveValues(data.monthly))
);


const yearlyTotal = formatTotal(
  calculateTotal(resolveValues(data.yearly))
);

const yearlyLabels =
  data.yearly?.labels?.map((m) =>
    new Date(2000, Number(m) - 1, 1).toLocaleString(
      lang === "AR" ? "ar" : "en",
      { month: "short" }
    )
  ) || [];

   
   const dailyLabels = data.daily?.labels || [];
const dailySales = data.daily?.sales || [];

const comparison = data.comparison || {
  thisWeek: 0,
  lastWeek: 0,
  percent: 0,
};

  // ✅ SAFE labels (data is guaranteed to exist here)
const weeklyLabel = (() => {
  if (!data.weekly?.startDate) return "";
  const d = new Date(data.weekly.startDate);
  const month = d.toLocaleString(lang === "AR" ? "ar" : "en", { month: "long" });
  const weekOfMonth = Math.ceil(d.getDate() / 7);
  return lang === "AR"
    ? `الأسبوع ${weekOfMonth} من ${month}`
    : `Week ${weekOfMonth} of ${month}`;
})();

const monthlyLabel = (() => {
  if (!data.monthly?.monthStart) return "";
  const d = new Date(data.monthly.monthStart);
  return d.toLocaleString(lang === "AR" ? "ar" : "en", {
    month: "long",
    year: "numeric",
  });
})();


  const weeklySales = data.weekly?.sales || [0,0,0,0,0,0,0];
  const monthlyLabels = data.monthly?.labels || [];
  const monthlySales = data.monthly?.sales || [];

  const topLabels = (data.topProducts || []).map((p) =>
    lang === "AR" ? p.name_ar : p.name_en
  );

  const topValues = (data.topProducts || []).map((p) => p.sales);

const smallChartTooltip = {
   enabled: false,
  external: function (context) {
    let tooltipEl = document.getElementById("chart-tooltip");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "chart-tooltip";
      document.body.appendChild(tooltipEl);
    }

    const tooltip = context.tooltip;

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    const value = tooltip.dataPoints?.[0]?.raw ?? 0;

    tooltipEl.innerHTML = `
      <div style="
        background:white;
        padding:10px 14px;
        border-radius:12px;
        box-shadow:0 10px 25px rgba(0,0,0,0.15);
        border:1px solid #e5e7eb;
        width:180px;
      ">
        <div style="font-weight:600;margin-bottom:6px;">${TEXT.value}</div>
        <div style="
          display:inline-block;
          background:#f3f4f6;
          padding:4px 8px;
          border-radius:6px;
          font-weight:600;
        ">
          ${value}
        </div>
      </div>
    `;

    const position = context.chart.canvas.getBoundingClientRect();

    tooltipEl.style.opacity = 1;
    tooltipEl.style.position = "absolute";
    tooltipEl.style.left =
      position.left + window.pageXOffset + context.tooltip.x + 15 + "px";
    tooltipEl.style.top =
      position.top + window.pageYOffset + context.tooltip.y - 40 + "px";
    tooltipEl.style.pointerEvents = "none";
  },
};

const handleProductSalesSearch = async (e) => {
  e.preventDefault();

  const cleanQuery = productSalesSearchQuery.trim();

  if (!cleanQuery) {
    setProductSalesSearchResults([]);
    setProductSalesSearchError(
      lang === "AR" ? "أدخل اسم المنتج أو الرقم" : "Enter product name or ID"
    );
    return;
  }

  try {
    setProductSalesSearchLoading(true);
    setProductSalesSearchError("");

    const res = await searchProductOverallSales(cleanQuery);

    setProductSalesSearchResults(res.results || []);
  } catch (err) {
    console.error(err);
    setProductSalesSearchError(
      lang === "AR"
        ? "تعذر البحث عن المنتج"
        : "Could not search product"
    );
  } finally {
    setProductSalesSearchLoading(false);
  }
};

const smallChartVerticalLine = {
  id: "smallChartVerticalLine",
  afterDraw: (chart) => {
    const active = chart.tooltip?._active; // ✅ FIX HERE

    if (active && active.length) {
      const ctx = chart.ctx;
      const { x, y } = active[0].element;

      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;

      ctx.save();

      // vertical line
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#d1d5db";
      ctx.stroke();

      // dot
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#0ea5e9";
      ctx.fill();

      ctx.lineWidth = 2;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();

      ctx.restore();
    }
  },
};

const goToReport = (reportKey) => {
  const today = new Date();

  const start = mainStart || today;
  const end = mainEnd || start;

  navigate("/admin/reports", {
    state: {
      fromAnalytics: true,
      reportKey,
      mainStart: formatLocalDate(start),
      mainEnd: formatLocalDate(end),
      compareStart: compareRange?.start ? formatLocalDate(compareRange.start) : null,
      compareEnd: compareRange?.end ? formatLocalDate(compareRange.end) : null,
      compareType,
      currency,
      exchangeRate,
    },
  });
};

return (
  <div
    id="admin-analytics-page"
    className={`flex bg-[#f1f1f1] min-h-screen w-full max-w-full overflow-x-hidden ${isOpen ? "overflow-hidden" : ""}`}
  >
    <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

    <div
  className={`
    flex-1 min-w-0 w-full max-w-full overflow-x-hidden lg:overflow-visible
    ${lang === "AR" ? "" : "lg:ml-64"}
    transition-all duration-300
  `}
>
      <AdminHeader setIsOpen={setIsOpen} />

      <main className="w-full max-w-full min-w-0 p-2 sm:p-4 lg:p-6 overflow-x-hidden lg:overflow-visible">

<div className="block w-full max-w-full min-w-0 overflow-x-hidden lg:overflow-visible">

  {!canAccess ? (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "غير مصرح لك بعرض نشاط المستخدمين"
          : "You are not allowed to view user activity"}
      </p>
    </div>
  ) : (
    <>
{/* PAGE HEADER (SHOPIFY STYLE) */}
<div className="flex flex-row items-center justify-between gap-3 mb-5 w-full">

  {/* LEFT */}
  <div className="mx-1 flex items-center gap-2">

    <div className="flex items-center justify-center w-6 h-6">
      <ChartSolid className="w-[20px] h-[20px] text-gray-900" />
    </div>

    <h1 className="text-[20px] font-semibold text-gray-900">
      {TEXT.title}
    </h1>

  </div>

  {/* RIGHT */}
  <button
  type="button"
  onClick={() => setShowExport(true)}
  className="
  analytics-export-no-print
  h-8 px-2.5 sm:px-3
  shrink-0
  bg-[#1f1f1f]
  text-white
  rounded-lg
  shadow-[0_1px_0_rgba(0,0,0,0.08),0_3px_8px_rgba(0,0,0,0.2)]
  text-[12px] sm:text-sm font-medium
  hover:bg-black
  active:shadow-[inset_0_2px_6px_rgba(0,0,0,0.3)]
  transition
"
>
  {TEXT.export}
</button>

</div>

{/* FILTER BUTTONS (SHOPIFY STYLE) */}
<div className="flex flex-row sm:flex-wrap items-center gap-1.5 sm:gap-3 mb-6 w-full max-w-full min-w-0">

  {/* TODAY */}
  <button
  type="button"
  onClick={() => {
  setShowCompareCalendar(true);
  setDateView("main");
  setDateTab("custom");

  if (mainRange) {
  // ✅ restore last applied range
  setMainStart(mainRange.start);
  setMainEnd(mainRange.end);
  setSelectedDate(mainRange.end);
  setCalendarBaseDate(mainRange.end);
} else {
  // fallback → today
  const today = new Date();
  setMainStart(today);
  setMainEnd(today);
  setSelectedDate(today);
  setCalendarBaseDate(today);
}
}}
  className="
  flex-1 sm:flex-none min-w-0
  flex items-center justify-between gap-1 sm:gap-2
  h-8 px-2 sm:px-3 w-full sm:w-auto max-w-full
  bg-white
  border border-gray-200
  rounded-lg
  shadow-[0_1px_0_rgba(0,0,0,0.04)]
  text-[11px] sm:text-[12px] font-medium text-gray-900
  hover:bg-gray-50
  transition
"
>
  
  <FaCalendarAlt className="text-gray-900 text-sm" />

<span className="truncate min-w-0">{getMainButtonLabel()}</span>

{/* ❌ RESET BUTTON */}
{mainRange && (
  <span
    role="button"
    tabIndex={0}
    onClick={(e) => {
      e.stopPropagation();
      resetToToday();
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        resetToToday();
      }
    }}
    className="ml-1 text-gray-400 hover:text-black text-sm cursor-pointer"
  >
    ✕
  </span>
)}

<FiChevronDown className="text-gray-900 text-sm" />
</button>

<div className="relative compare-dropdown flex-1 sm:flex-none min-w-0 sm:w-auto">

  {/* DATE */}
    <button
  type="button"
  onClick={() => setShowCompareDropdown((p) => !p)}
  className="flex items-center justify-between gap-1 sm:gap-2 h-8 px-2 sm:px-3 w-full sm:w-auto max-w-full bg-white border border-gray-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04)] text-[11px] sm:text-[12px] font-medium text-gray-900 hover:bg-gray-50 transition"
>
  <FaCalendarAlt className="text-gray-900 text-sm" />

  <span className="truncate min-w-0">{getCompareButtonLabel()}</span>

  {/* ✅ RESET CHIP (same as Today button) */}
  {compareRange && (
  <span
    role="button"
    tabIndex={0}
    onClick={(e) => {
      e.stopPropagation();

      const start = mainStart || new Date();
      const end = mainEnd || start;

      setCompareRange(null);
      setCompareType("none");
      loadCards(start, end, null);
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();

        const start = mainStart || new Date();
        const end = mainEnd || start;

        setCompareRange(null);
        setCompareType("none");
        loadCards(start, end, null);
      }
    }}
    className="ml-1 text-gray-400 hover:text-black text-sm cursor-pointer"
  >
    ✕
  </span>
)}

  <FiChevronDown className="text-gray-900 text-sm" />
</button>

{showCompareDropdown && (
  <div
    className={`
      fixed sm:absolute
      left-3 right-3
      ${lang === "AR" ? "sm:right-0 sm:left-auto" : "sm:left-0 sm:right-auto"}
      top-[124px] sm:top-auto
      mt-0 sm:mt-2
      w-auto sm:w-[260px]
      bg-white border border-gray-200
      rounded-xl shadow-lg
      py-1 z-[999]
      overflow-hidden
    `}
    dir={lang === "AR" ? "rtl" : "ltr"}
  >

    {[
  { key: "none", label: TEXT.compareNone },
  { key: "yesterday", label: TEXT.compareYesterday },
  { key: "prevYear", label: TEXT.comparePrevYear },
  { key: "prevYearMatch", label: TEXT.comparePrevYearMatch },
  { key: "custom", label: TEXT.compareCustom },
].map((item) => (
      <button
  type="button"
        key={item.key}
        onClick={() => {
  // 🔹 Reset when "No comparison"
  if (item.key === "none") {
    setCompareRange(null);
    setCompareType("none");
    setShowCompareDropdown(false);
    return;
  }

  setCompareType(item.key);
setShowCompareDropdown(false);

if (item.key === "custom") {
  setShowCustomCompareModal(true);

  const today = new Date();

  setCompareStartDate(null);
  setCompareEndDate(null);
  setSelectedDate(today);
  setCalendarBaseDate(today);
  setCalendarOffset(0);

  return;
}

const start = mainStart || new Date();
const end = mainEnd || start;
const nextCompare = buildCompareRange(item.key, start, end);

setCompareRange(nextCompare);
loadCards(start, end, nextCompare);
}}
    className={`
  w-full
  ${lang === "AR" ? "text-right" : "text-left"}
  px-4 py-2.5
  text-[11px] sm:text-[13px]
  leading-none
  whitespace-nowrap
  overflow-hidden
  hover:bg-gray-100 transition
  ${compareType === item.key ? "bg-gray-100 font-medium" : ""}
`}
      >
        <span className="block truncate">
  {item.label}
</span>
      </button>
    ))}

  </div>
)}
</div>

  {/* CURRENCY */}
  <button
  type="button"
  onClick={() => {
    if (currency === "USD") {
      // USD → open modal
      setShowCurrencyModal(true);
    } else {
      // LBP → switch back to USD instantly
      setCurrency("USD");
    }
  }}
  className="
  flex-1 sm:flex-none min-w-0
  flex items-center justify-center sm:justify-start gap-1 sm:gap-2
  h-8 px-2 sm:px-3 w-full sm:w-auto
  bg-white
  border border-gray-200
  rounded-lg
  shadow-[0_1px_0_rgba(0,0,0,0.04)]
  text-[11px] sm:text-[12px] font-medium text-gray-900
  hover:bg-gray-50
  transition
"
>
  <FaExchangeAlt className="text-gray-900 text-sm" />

  <span className="truncate min-w-0">
  {currency === "USD"
    ? "USD"
    : `LBP ${exchangeRate.toLocaleString()} ل.ل`}
</span>
</button>

</div>

{/* SHOPIFY MINI CARDS */}
<div className="relative z-20 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6 w-full max-w-full min-w-0 overflow-visible">
  {[
  {
    label: TEXT.grossSales,
    reportKey: "grossSales",
    value: cardsData
      ? currency === "LBP"
        ? `${Math.round(cardsData.grossSales * exchangeRate).toLocaleString()} L.L`
        : `$${cardsData.grossSales.toFixed(2)}`
      : "—",
    rawValue: cardsData?.grossSales || 0,
    compareValue: cardsData?.comparison?.grossSales || 0,
    tooltipTitle: TEXT.grossSalesTooltip,
    tooltipDesc: TEXT.grossSalesDesc,
    trendKey: "sales",
  },
  {
    label: TEXT.returningRate,
    reportKey: "returningRate",
    value: cardsData ? `${cardsData.returningRate}%` : "—",
    rawValue: cardsData?.returningRate || 0,
    compareValue: cardsData?.comparison?.returningRate || 0,
    tooltipTitle: TEXT.returningTooltip,
    tooltipDesc: TEXT.returningDesc,
    trendKey: "customers",
  },
  {
    label: TEXT.ordersFulfilled,
    reportKey: "ordersFulfilled",
    value: cardsData ? `${cardsData.fulfilledOrders}` : "—",
    rawValue: cardsData?.fulfilledOrders || 0,
    compareValue: cardsData?.comparison?.fulfilledOrders || 0,
    tooltipTitle: TEXT.fulfilledTooltip,
    tooltipDesc: TEXT.fulfilledDesc,
    trendKey: "fulfilledOrders",
  },
  {
    label: TEXT.orders,
    reportKey: "totalOrders",
    value: cardsData ? `${cardsData.totalOrders}` : "—",
    rawValue: cardsData?.totalOrders || 0,
    compareValue: cardsData?.comparison?.totalOrders || 0,
    tooltipTitle: TEXT.ordersTooltip,
    tooltipDesc: TEXT.ordersDesc,
    trendKey: "orders",
  },
].map((card, i) => {
  const spark = buildSparkline(card.trendKey);
  const delta = getDelta(
  card.rawValue,
  card.compareValue,
  {
    // only for returning customer rate
    hideZeroComparison: card.label === TEXT.returningRate,
  }
);
  const compareLabel = getCompareLabel();

  return (
    <div key={i} className="relative analytics-card group">

    {/* CARD */}
    <div
      className="
        bg-white
        border border-gray-200
        rounded-xl
        px-4 py-2
        shadow-[0_1px_0_rgba(0,0,0,0.04)]
        hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)]
        transition
        cursor-pointer
      "
    >

      {/* TITLE */}
      <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();
    goToReport(card.reportKey);
  }}
  className={`
    inline-block text-start group/title
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  <p className="text-[13px] font-semibold text-gray-900 leading-tight hover:text-blue-700 transition">
    {card.label}
  </p>

  <div
    className="mt-[2px] group-hover/title:border-blue-500 transition"
    style={{
      borderBottom: "2px dotted #9CA3AF",
      width: "100%",
    }}
  />
</button>
     
<div className="flex items-center justify-between gap-2 mt-2 min-w-0 max-w-full overflow-hidden">

  {/* VALUE + COMPARISON */}
<div className="flex items-center gap-2 min-w-0">
  <p className="text-[13px] font-semibold text-gray-900 truncate min-w-0 max-w-[150px] sm:max-w-none">
  {card.value}
</p>

  {cardsData?.comparison && (
    <div className="text-[10px] leading-none">
     {delta.fromZero ? (
  <span className="inline-flex items-center gap-[3px] font-semibold">
    <svg
      viewBox="0 0 20 20"
      className="w-[12px] h-[12px] text-[#5aa36b] rotate-45"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 15V5" />
      <path d="M5 10l5-5 5 5" />
    </svg>
  </span>
) : delta.hideComparison || delta.percent === null ? (
  <span className="text-gray-400 font-semibold">
    -
  </span>
) : delta.direction === "up" ? (
  <span className="inline-flex items-center gap-[3px] font-semibold">
    <svg
      viewBox="0 0 20 20"
      className="w-[12px] h-[12px] text-[#5aa36b] rotate-45"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 15V5" />
      <path d="M5 10l5-5 5 5" />
    </svg>

    <span className="text-gray-700">{delta.percent}%</span>
  </span>
) : delta.direction === "down" ? (
  <span className="inline-flex items-center gap-[3px] font-semibold">
    <svg
      viewBox="0 0 20 20"
      className="w-[12px] h-[12px] text-[#dc6868] -rotate-45"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 5v10" />
      <path d="M5 10l5 5 5-5" />
    </svg>

    <span className="text-gray-700">{delta.percent}%</span>
  </span>
) : (
  <span className="text-gray-400 font-semibold">
    -
  </span>
)}
    </div>
  )}
</div>

  {/* SMALL PROGRESS LINE */}
  <div className="w-[58px] sm:w-[80px] h-[30px] opacity-80 shrink-0 overflow-hidden">
  <Line
  data={{
    labels: spark.labels,
    datasets: [
  ...(spark.compareValues
    ? [
        {
  data: spark.compareValues,
  borderColor: "rgba(107,114,128,0.45)",
  backgroundColor: "transparent",
  tension: 0.45,
  borderWidth: 1.5,
  pointRadius: 0,
  pointHoverRadius: 0,
  fill: false,
},
      ]
    : []),
  {
    data: spark.values,
    borderColor: "#0ea5e9",
    backgroundColor: "rgba(14,165,233,0.10)",
    tension: 0.45,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 0,
    fill: true,
  },
],
  }}
  options={{
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    elements: {
      line: {
        capBezierPoints: true,
      },
    },
    scales: {
      x: {
        display: false,
        grid: { display: false },
      },
      y: {
        display: false,
        min: spark.min,
        max: spark.max,
        grid: { display: false },
      },
    },
  }}
/>
</div>

</div>

    </div>

    {/* TOOLTIP (DESKTOP ONLY) */}
<div className="
  hidden sm:block
  pointer-events-none
  opacity-0 group-hover:opacity-100
  translate-y-1 group-hover:translate-y-0
  transition-all duration-200
  absolute
  left-1/2 -translate-x-1/2
  bottom-full mb-1
  w-[min(300px,calc(100vw-24px))]
  bg-white
  border border-gray-200
  rounded-xl
  shadow-lg
  p-3
  z-[999]
">

        {/* TITLE */}
        <p className="text-[15px] font-semibold text-gray-900 mb-1">
          {card.tooltipTitle}
        </p>

        {/* DESCRIPTION */}
        <p className="text-[13px] text-gray-900 leading-relaxed">
          {card.tooltipDesc}
        </p>

         {/* ARROW */}
        <div className="
          absolute
          left-1/2 -translate-x-1/2
          top-full
          w-3 h-3
          bg-white
          rotate-45
          -mt-[6px]
        "></div>
      </div>
    

  </div>
 );
})}

</div>

{/* ================= BIG SHOPIFY CARDS ================= */}
<div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-3 sm:gap-4 mb-8 w-full max-w-full min-w-0">

  {/* ================= LEFT: TOTAL SALES OVER TIME ================= */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 h-auto md:h-[420px] flex flex-col shadow-[0_1px_0_rgba(0,0,0,0.04)]">

    {/* TITLE */}
   <div className="relative inline-block w-fit group cursor-pointer">

  {/* TITLE */}
  <button
  type="button"
  onClick={() => goToReport("totalSalesOverTime")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.totalSalesOverTime}
</button>

  <div
    className="mt-[0px] mb-1"
    style={{
      borderBottom: "2px dotted #9CA3AF",
      width: "100%",
    }}
  />

{/* ✅ TOOLTIP DESKTOP ONLY */}
<div
  className={`
    hidden sm:block
    pointer-events-none
    opacity-0 group-hover:opacity-100
    translate-y-1 group-hover:translate-y-0
    transition-all duration-200
    absolute
    bottom-full mb-2
    w-[min(320px,calc(100vw-32px))]
    bg-white
    border border-gray-200
    rounded-xl
    shadow-lg
    p-3
    z-50
    ${lang === "AR" ? "right-0 translate-x-0" : "left-1/2 -translate-x-1/2"}
  `}
  dir={lang === "AR" ? "rtl" : "ltr"}
>

    <p className="text-[15px] font-semibold text-gray-900 mb-1">
      {TEXT.totalSalesOverTime}
    </p>

    <p className="text-[13px] text-gray-700 leading-relaxed mb-2">
      {TEXT.totalSalesDesc}
    </p>

    <p className="text-[12px] font-mono text-green-800 tracking-wide">
      <span className="text-blue-800">{TEXT.totalSalesFormula} </span> =
      {TEXT.netSalesFormula}
      <span className="text-blue-800"> + </span>
      {TEXT.additionalFeesFormula}
      <span className="text-blue-800"> + </span>
      {TEXT.shippingChargeFormula}
    </p>

    <div
  className={`
    absolute top-full w-3 h-3 bg-white rotate-45 -mt-[6px]
    ${lang === "AR" ? "right-4" : "left-1/2 -translate-x-1/2"}
  `}
/>
  </div>
</div>

    {/* VALUE */}
<div className="mb-2 flex items-center justify-between gap-3">
  <p className="text-[20px] font-semibold text-gray-900">
    {formatChartMoney(mainChartTotalSales)}
  </p>

  {cardsData?.comparison && (
    <div className="text-[14px] leading-none">
      {totalSalesDelta.fromZero ? (
        <span className="inline-flex items-center gap-[4px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[16px] h-[16px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>
        </span>
      ) : totalSalesDelta.hideComparison || totalSalesDelta.percent === null ? (
        <span className="text-gray-400 font-semibold text-[14px]">
          -
        </span>
      ) : totalSalesDelta.direction === "up" ? (
        <span className="inline-flex items-center gap-[4px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[16px] h-[16px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>

          <span className="text-gray-700 text-[14px]">
            {totalSalesDelta.percent}%
          </span>
        </span>
      ) : totalSalesDelta.direction === "down" ? (
        <span className="inline-flex items-center gap-[4px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[16px] h-[16px] text-[#dc6868]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 4v12" />
            <path d="M5 11l5 5 5-5" />
          </svg>

          <span className="text-gray-700 text-[14px]">
            {totalSalesDelta.percent}%
          </span>
        </span>
      ) : (
        <span className="text-gray-400 font-semibold text-[14px]">
          -
        </span>
      )}
    </div>
  )}
</div>

    {/* CHART */}
    <div className="h-[220px] sm:h-[300px] cursor-pointer w-full max-w-full min-w-0 overflow-hidden">
    <Line
  data={{
    labels: mainChartLabels,
    datasets: [
 ...(hasCompareChart
  ? [
      {
        label: lang === "AR" ? "المقارنة" : "Comparison",
        data: alignedCompareSales,
        borderColor: "rgba(196,181,253,0.95)",
        backgroundColor: "rgba(196,181,253,0.22)",
        fill: "origin",
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
        order: 0,
      },
    ]
  : []),
  {
    label: TEXT.totalSales,
    data: mainChartSales,
    borderColor: "#0ea5e9",
    backgroundColor: "rgba(14,165,233,0.14)",
    fill: true,
    tension: 0.35,
    pointRadius: 0,
    pointHoverRadius: 4,
    borderWidth: 2,
    order: 1,
  },
],
  }}
  options={{
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    hover: {
      mode: "index",
      intersect: false,
    },
    events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById("chart-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chart-tooltip";
            document.body.appendChild(tooltipEl);
          }

          tooltipEl.style.transform = "translateY(-100%)";

          const tooltip = context.tooltip;

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
const point = mainChartTrend[index];
const comparePoint = compareChartTrend[index];

const value = Number(point?.sales || 0);
const compareValue =
  hasCompareChart && comparePoint
    ? Number(comparePoint.sales || 0)
    : null;

          const tooltipTimeLabel =
  mainChartGranularity === "hour"
    ? formatHourLabel(point?.hour)
    : formatChartDate(point?.date);

const mainTooltipDateLabel =
  mainChartGranularity === "hour"
    ? formatChartDate(point?.date)
    : formatChartDate(point?.date);

const compareTooltipDateLabel =
  hasCompareChart && comparePoint
    ? compareChartGranularity === "hour"
      ? formatChartDate(comparePoint.date)
      : formatChartDate(comparePoint.date)
    : "";

const fullTooltipLabel =
  mainChartGranularity === "hour"
    ? formatChartDateTime(point)
    : formatChartDate(point?.date);

        tooltipEl.innerHTML = hasCompareChart
  ? `
    <div style="
      background:white;
      padding:10px 12px;
      border-radius:8px;
      box-shadow:0 10px 25px rgba(0,0,0,0.15);
      border:1px solid #e5e7eb;
      width:240px;
    ">
      <div style="
        font-size:12px;
        font-weight:700;
        color:#111827;
        margin-bottom:8px;
      ">
        ${tooltipTimeLabel}
      </div>

      <div style="display:flex;flex-direction:column;gap:6px;">
        <div style="
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          background:#f3f4f6;
          padding:5px 8px;
          border-radius:6px;
          font-size:11px;
          font-weight:600;
        ">
          <span style="display:flex;align-items:center;gap:6px;color:#374151;">
            <span style="width:7px;height:7px;background:#0ea5e9;border-radius:50%;display:inline-block;"></span>
            ${mainTooltipDateLabel}
          </span>
          <span>${formatChartMoney(value)}</span>
        </div>

        <div style="
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          background:#f9fafb;
          padding:5px 8px;
          border-radius:6px;
          font-size:11px;
          font-weight:600;
          color:#4b5563;
        ">
          <span style="display:flex;align-items:center;gap:6px;">
  <span style="width:7px;height:7px;background:#c4b5fd;border-radius:50%;display:inline-block;"></span>
  ${compareTooltipDateLabel}
</span>
          <span>${formatChartMoney(compareValue)}</span>
        </div>
      </div>
    </div>
  `
 : `
    <div style="
      background:white;
      padding:8px 10px;
      border-radius:8px;
      box-shadow:0 8px 22px rgba(0,0,0,0.14);
      border:1px solid #e5e7eb;
      width:175px;
    ">
      <div style="
        font-size:11px;
        font-weight:700;
        color:#111827;
        margin-bottom:5px;
        line-height:1.15;
      ">
        ${TEXT.totalSales}
      </div>

      <div style="
        display:flex;
        align-items:center;
        gap:5px;
        font-size:10px;
        color:#6b7280;
        margin-bottom:6px;
        line-height:1.15;
        white-space:nowrap;
      ">
        <span style="
          width:6px;
          height:6px;
          background:#0ea5e9;
          border-radius:50%;
          display:inline-block;
          flex-shrink:0;
        "></span>
        <span>${fullTooltipLabel}</span>
      </div>

      <div style="
        display:flex;
        align-items:center;
        justify-content:flex-start;
        background:#f3f4f6;
        padding:4px 6px;
        border-radius:5px;
        font-size:10px;
        font-weight:700;
        color:#111827;
        line-height:1.15;
      ">
        <span>${formatChartMoney(value)}</span>
      </div>
    </div>
  `;

          const position = context.chart.canvas.getBoundingClientRect();

          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left =
            position.left + window.pageXOffset + context.tooltip.x + 15 + "px";
          tooltipEl.style.top =
            position.top + window.pageYOffset + context.tooltip.y - 40 + "px";
          tooltipEl.style.pointerEvents = "none";
        },
      },
    },
    elements: {
      line: {
        capBezierPoints: true,
      },
    },
    scales: {
      x: {
  grid: { display: false },
  ticks: {
    color: "#878e97",
    font: {
      size: mainChartRangeDays > 30 ? 9 : 10,
      weight: "300",
    },
    maxRotation: 0,
    minRotation: 0,
    autoSkip: false,
  },
},
      y: {
  min: 0,
  max: mainChartSuggestedMax,
  ticks: {
    stepSize: mainChartStep,
    color: "#878e97",
    callback: (value) => formatMoneyAxis(value),
    font: {
      size: 13,
      weight: "300",
    },
  },
        grid: {
          drawBorder: false,
          drawTicks: false,
          color: "#f1f5f9",
        },
        border: {
          display: false,
        },
      },
    },
  }}
    plugins={[
    {
      id: "comparisonLineGlow",
      beforeDatasetDraw: (chart, args) => {
        const dataset = chart.data.datasets[args.index];

        if (!dataset?.comparisonGlow) return;

        const ctx = chart.ctx;

        ctx.save();
        ctx.shadowColor = "transparent";
ctx.shadowBlur = 0;
ctx.shadowOffsetX = 0;
ctx.shadowOffsetY = 0;
      },
      afterDatasetDraw: (chart, args) => {
        const dataset = chart.data.datasets[args.index];

        if (!dataset?.comparisonGlow) return;

        chart.ctx.restore();
      },
    },
    {
      id: "verticalLine",
      afterDraw: (chart) => {
        const active = chart.tooltip?._active;

        if (active && active.length) {
          const ctx = chart.ctx;
          const { x, y } = active[0].element;

          const topY = chart.scales.y.top;
          const bottomY = chart.scales.y.bottom;

          ctx.save();

          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#d1d5db";
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fillStyle = "#0ea5e9";
          ctx.fill();

          ctx.lineWidth = 2;
          ctx.strokeStyle = "#ffffff";
          ctx.stroke();

          ctx.restore();
        }
      },
    },
  ]}
/>
    </div>
<div className="flex items-center justify-center gap-4 mt-3 text-[12px] text-gray-500">
  <div className="flex items-center gap-2">
    <span className="w-2.5 h-2.5 bg-cyan-500 rounded-[3px]"></span>
    <span>{mainChartFooterLabel}</span>
  </div>

  {hasCompareChart && (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 bg-purple-300 rounded-[3px]"></span>
      <span>{getCompareLabel()}</span>
    </div>
  )}
</div>
  </div>

  {/* ================= RIGHT: SALES BREAKDOWN ================= */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 h-auto md:h-[420px] flex flex-col justify-start shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-visible">

    {/* TITLE */}
  <div className="relative group inline-block w-fit cursor-pointer">

  <button
  type="button"
  onClick={() => goToReport("totalSalesBreakdown")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.totalSalesBreakdown}
</button>

  <div
    className="mt-[0px] mb-1"
    style={{
      borderBottom: "2px dotted #9CA3AF",
      width: "100%",
    }}
  />

  {/* TOOLTIP DESKTOP ONLY */}
<div className="
  hidden sm:block
  cursor-pointer
  opacity-0 group-hover:opacity-100
    translate-y-1 group-hover:translate-y-0
    transition-all duration-200
    absolute
    left-1/2 -translate-x-1/2
    bottom-full mb-2
    w-[260px]
    bg-white
    border border-gray-200
    rounded-xl
    shadow-lg
    p-3
    z-50
  ">

    <p className="text-[15px] font-semibold text-gray-900">
      {TEXT.totalSalesBreakdown}
    </p>

    {/* ARROW */}
    <div className="
      absolute
      left-1/2 -translate-x-1/2
      top-full
      w-3 h-3
      bg-white
      rotate-45
      -mt-[6px]
    "></div>

  </div>
</div>

    {/* LIST */}
    <div className="space-y-4 text-[13px] pb-2 pt-1">

    {[
  {
    label: TEXT.grossSales,
    value: salesBreakdown.grossSales,
    compareValue: compareSalesBreakdown?.grossSales || 0,
    formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">إجمالي الإيرادات</span>{" "}
      = مجموع subtotal_amount للطلبات غير الملغاة وغير المستردة
    </>
  ) : (
    <>
      <span className="text-blue-800">Gross sales</span>{" "}
      = sum of subtotal_amount from non-cancelled and non-refunded orders
    </>
  ),
  },
  {
    label: TEXT.discounts,
    value: salesBreakdown.discounts,
    compareValue: compareSalesBreakdown?.discounts || 0,
    formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">الخصومات</span>{" "}
      = مجموع discount_amount للطلبات غير الملغاة وغير المستردة
    </>
  ) : (
    <>
      <span className="text-blue-800">Discounts</span>{" "}
      = sum of discount_amount from non-cancelled and non-refunded orders
    </>
  ),
  },
  {
    label: TEXT.returns,
    value: salesBreakdown.returns,
    compareValue: compareSalesBreakdown?.returns || 0,
    formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">المرتجعات</span>{" "}
      = مجموع total_amount للطلبات الملغاة أو المستردة
    </>
  ) : (
    <>
      <span className="text-blue-800">Returns</span>{" "}
      = sum of total_amount from cancelled or refunded orders
    </>
  ),
  },
  {
    label: TEXT.netSales,
    value: salesBreakdown.netSales,
    compareValue: compareSalesBreakdown?.netSales || 0,
    formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">صافي المبيعات</span>{" "}
      = إجمالي الإيرادات <span className="text-blue-800">-</span> الخصومات
    </>
  ) : (
    <>
      <span className="text-blue-800">Net sales</span>{" "}
      = gross sales <span className="text-blue-800">-</span> discounts
    </>
  ),
  },
  {
    label: TEXT.shippingCharges,
    value: salesBreakdown.shippingCharges,
    compareValue: compareSalesBreakdown?.shippingCharges || 0,
   formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">رسوم الشحن</span>{" "}
      = مجموع shipping_fee للطلبات غير الملغاة وغير المستردة
    </>
  ) : (
    <>
      <span className="text-blue-800">Shipping charges</span>{" "}
      = sum of shipping_fee from non-cancelled and non-refunded orders
    </>
  ),
  },
  {
    label: TEXT.additionalFeesFormula,
    value: salesBreakdown.additionalFees,
    compareValue: compareSalesBreakdown?.additionalFees || 0,
    formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">الرسوم الإضافية</span>{" "}
      = مجموع extra_fee_amount للطلبات غير الملغاة وغير المستردة
    </>
  ) : (
    <>
      <span className="text-blue-800">Extra fees</span>{" "}
      = sum of extra_fee_amount from non-cancelled and non-refunded orders
    </>
  ),
  },
  {
    label: TEXT.totalSales,
    value: salesBreakdown.totalSales,
    compareValue: compareSalesBreakdown?.totalSales || 0,
    formula:
  lang === "AR" ? (
    <>
      <span className="text-blue-800">إجمالي المبيعات</span>{" "}
      = صافي المبيعات <span className="text-blue-800">+</span> رسوم الشحن{" "}
      <span className="text-blue-800">+</span> الرسوم الإضافية
    </>
  ) : (
    <>
      <span className="text-blue-800">Total sales</span>{" "}
      = net sales <span className="text-blue-800">+</span> shipping charges{" "}
      <span className="text-blue-800">+</span> extra fees
    </>
  ),
  },
].map((item, i) => (
  <div
  key={i}
  className={`
    group 
    flex justify-between items-center px-3 py-2 rounded-md
    ${i % 2 === 1 ? "bg-gray-100" : ""}
    hover:bg-gray-200 transition
  `}
>

  <div className="relative group/label inline-flex items-center min-w-0">
  <span className="text-blue-600 group-hover/label:text-blue-800 group-hover/label:underline transition cursor-pointer truncate">
    {item.label}
  </span>

  <BreakdownRowTooltip formula={item.formula} />
</div>

<div className="flex items-center justify-end gap-2 min-w-0">
  <span className="text-gray-900 font-medium whitespace-nowrap">
    {formatChartMoney(item.value)}
  </span>

  <BreakdownDelta
    current={item.value}
    previous={item.compareValue}
  />
</div>

  </div>
))}

    </div>

  </div>

</div>

{/* ================= SECONDARY ANALYTICS (6 CARDS) ================= */}
<div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 mb-6 w-full max-w-full min-w-0">

  {/* ================= SMALL CARDS ================= */}

  {/* CARD 1 */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 h-auto sm:h-[250px] overflow-visible shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("salesByChannel")}
  className={`
    text-[13px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.salesByChannel}
</button>
      <div className="mt-[2px]" style={{ borderBottom: "2px dotted #9CA3AF" }} />
      <TooltipBlock
    title={TEXT.salesByChannel}
    subtitle={TEXT.salesByChannelDesc}
    equation={
      <>
        <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = {TEXT.netSalesFormula}{" "}
        <span className="text-blue-800">+</span> {TEXT.additionalFeesFormula}{" "}
        <span className="text-blue-800">+</span> {TEXT.shippingChargeFormula}
      </>
    }
  />
    </div>

   <div className="mt-4 space-y-2.5">
  {salesByChannelTotal <= 0 ? (
    <div className="h-[160px] flex flex-col justify-center space-y-3">
      {salesByChannel.map((item) => (
        <div
          key={item.channel}
          className="group relative rounded-md px-1.5 py-1 transition-colors hover:bg-gray-50"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
  <p className="text-[12px] font-medium text-gray-800 leading-tight">
    {item.label}
  </p>

  <SalesChannelDelta
    current={item.totalSales}
    previous={item.compareTotalSales}
  />
</div>

          <div className="flex items-center gap-2 w-full">
  <div
    className="h-[18px] bg-cyan-500 transition-all duration-300 shrink-0"
    style={{
      width: "4%",
      borderRadius: "0 2px 2px 0",
    }}
  />

  <p className="shrink-0 text-left text-[12px] font-semibold text-gray-600 whitespace-nowrap">
    {formatChartMoney(0)}
  </p>
</div>
        </div>
      ))}
    </div>
  ) : (
    salesByChannel.map((item) => {
      return (
        <div
          key={item.channel}
          className="group relative rounded-md px-1.5 py-1 transition-colors hover:bg-gray-50"
        >
          {/* row title */}
          <div className="flex items-center gap-1.5 mb-1.5">
  <p className="text-[12px] font-medium text-gray-800 leading-tight">
    {item.label}
  </p>

  <SalesChannelDelta
    current={item.totalSales}
    previous={item.compareTotalSales}
  />
</div>

          {/* progress row + amount */}
<div className="flex items-center gap-2 w-full">
  <div
    className="h-[18px] bg-cyan-500 transition-all duration-300 shrink-0"
    style={{
      width: getSalesChannelBarWidth(item.totalSales),
      borderRadius: "0 2px 2px 0",
    }}
  />

  <p className="shrink-0 text-left text-[12px] font-semibold text-gray-600 whitespace-nowrap">
    {formatChartMoney(item.totalSales)}
  </p>
</div>

          {/* hover tooltip */}
          <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[230px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {salesChannelTooltipText.date}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {salesChannelDateLabel}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {salesChannelTooltipText.source}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {item.label}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {salesChannelTooltipText.totalOrders}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {item.orders}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-cyan-600">
                  {salesChannelTooltipText.amount}
                </span>
                <span className="font-semibold text-cyan-700 text-right">
                  {formatChartMoney(item.totalSales)}
                </span>
              </div>
              {cardsData?.comparison && (
  <div className="flex items-start justify-between gap-3">
    <span className="text-gray-500">
      {lang === "AR" ? "المقارنة" : "Comparison"}
    </span>
    <span className="font-semibold text-gray-600 text-right">
      {formatChartMoney(item.compareTotalSales)}
    </span>
  </div>
)}
            </div>

            <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
          </div>
        </div>
      );
    })
  )}
</div>
  </div>


  {/* CARD 2 */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 h-auto sm:h-[250px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">

  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("avgOrder")}
  className={`
    text-[13px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.avgOrder}
</button>
    <div className="mt-[2px]" style={{ borderBottom: "2px dotted #9CA3AF" }} />
    <TooltipBlock
      title={TEXT.avgOrder}
      subtitle={TEXT.avgOrderDesc}
      equation={
        <>
          <span className="text-blue-800">{TEXT.averageOrderValueFormula}</span> = ({TEXT.grossSalesFormula}{" "}
          <span className="text-blue-800">-</span> {TEXT.discountFormula}) / {TEXT.orders}
        </>
      }
    />
  </div>

  <div className="mt-2 flex items-center justify-between gap-3">
  <p className="text-[16px] font-semibold text-gray-900">
    {formatChartMoney(averageOrderValue)}
  </p>

  {cardsData?.comparison && (
    <div className="text-[12px] leading-none">
      {averageOrderValueDelta.fromZero ? (
        <span className="inline-flex items-center gap-[3px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[13px] h-[13px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>
        </span>
      ) : averageOrderValueDelta.hideComparison ||
        averageOrderValueDelta.percent === null ? (
        <span className="text-gray-400 font-semibold">-</span>
      ) : averageOrderValueDelta.direction === "up" ? (
        <span className="inline-flex items-center gap-[3px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[13px] h-[13px] text-[#5aa36b]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 16V4" />
            <path d="M5 9l5-5 5 5" />
          </svg>

          <span className="text-gray-700">
            {averageOrderValueDelta.percent}%
          </span>
        </span>
      ) : averageOrderValueDelta.direction === "down" ? (
        <span className="inline-flex items-center gap-[3px] font-semibold">
          <svg
            viewBox="0 0 20 20"
            className="w-[13px] h-[13px] text-[#dc6868]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 4v12" />
            <path d="M5 11l5 5 5-5" />
          </svg>

          <span className="text-gray-700">
            {averageOrderValueDelta.percent}%
          </span>
        </span>
      ) : (
        <span className="text-gray-400 font-semibold">-</span>
      )}
    </div>
  )}
</div>

  {/* CHART */}
  <div className="h-[130px] sm:h-[140px] mt-1 cursor-pointer w-full max-w-full min-w-0 overflow-hidden">
<Line
  data={{
    labels: averageOrderValueLabels,
    datasets: [
      ...(canOverlayAverageOrderValueCompare
        ? [
            {
  label: lang === "AR" ? "المقارنة" : "Comparison",
  data: alignedCompareAverageOrderValue,
  borderColor: "rgba(196,181,253,0.95)",
  backgroundColor: "rgba(196,181,253,0.22)",
  fill: "origin",
  tension: 0.35,
  pointRadius: 0,
  pointHoverRadius: 4,
  borderWidth: 2,
  order: 0,
},
          ]
        : []),
      {
  label: TEXT.avgOrder,
  data: averageOrderValueValues,
  borderColor: "#0ea5e9",
  backgroundColor: "rgba(14,165,233,0.10)",
  fill: true,
  tension: 0.35,
  pointRadius: 0,
  pointHoverRadius: 4,
  borderWidth: 2,
  order: 1,
},
    ],
  }}
  options={{
    responsive: true,
    maintainAspectRatio: false,
    animation: false,

    interaction: {
      mode: "index",
      intersect: false,
    },
    hover: {
      mode: "index",
      intersect: false,
    },
    events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],

    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById("chart-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chart-tooltip";
            document.body.appendChild(tooltipEl);
          }

          tooltipEl.style.transform = "translateY(-100%)";

          const tooltip = context.tooltip;

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
          const point = averageOrderValueTrend[index];
          const comparePoint = compareAverageOrderValueTrend[index];

          const value = Number(point?.averageOrderValue || 0);
          const compareValue =
            canOverlayAverageOrderValueCompare && comparePoint
              ? Number(comparePoint.averageOrderValue || 0)
              : null;

          const tooltipTimeLabel =
            averageOrderValueGranularity === "hour"
              ? formatHourLabel(point?.hour)
              : formatChartDate(point?.date);

          const mainTooltipDateLabel = formatChartDate(point?.date);

          const compareTooltipDateLabel =
            canOverlayAverageOrderValueCompare && comparePoint
              ? formatChartDate(comparePoint.date)
              : "";

          const fullTooltipLabel =
            averageOrderValueGranularity === "hour"
              ? formatChartDateTime(point)
              : formatChartDate(point?.date);

          tooltipEl.innerHTML = canOverlayAverageOrderValueCompare
            ? `
              <div style="
                background:white;
                padding:10px 12px;
                border-radius:8px;
                box-shadow:0 10px 25px rgba(0,0,0,0.15);
                border:1px solid #e5e7eb;
                width:240px;
              ">
                <div style="
                  font-size:12px;
                  font-weight:700;
                  color:#111827;
                  margin-bottom:8px;
                ">
                  ${tooltipTimeLabel}
                </div>

                <div style="display:flex;flex-direction:column;gap:6px;">
                  <div style="
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    gap:10px;
                    background:#f3f4f6;
                    padding:5px 8px;
                    border-radius:6px;
                    font-size:11px;
                    font-weight:600;
                  ">
                    <span style="display:flex;align-items:center;gap:6px;color:#374151;">
                      <span style="width:7px;height:7px;background:#0ea5e9;border-radius:50%;display:inline-block;"></span>
                      ${mainTooltipDateLabel}
                    </span>
                    <span>${formatChartMoney(value)}</span>
                  </div>

                  <div style="
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    gap:10px;
                    background:#f9fafb;
                    padding:5px 8px;
                    border-radius:6px;
                    font-size:11px;
                    font-weight:600;
                    color:#4b5563;
                  ">
                    <span style="display:flex;align-items:center;gap:6px;">
                      <span style="width:7px;height:7px;background:#c4b5fd;border-radius:50%;display:inline-block;"></span>
                      ${compareTooltipDateLabel}
                    </span>
                    <span>${formatChartMoney(compareValue)}</span>
                  </div>
                </div>
              </div>
            `
         : `
  <div style="
    background:white;
    padding:8px 10px;
    border-radius:8px;
    box-shadow:0 8px 22px rgba(0,0,0,0.14);
    border:1px solid #e5e7eb;
    width:155px;
  ">
    <div style="
      font-size:11px;
      font-weight:700;
      color:#111827;
      margin-bottom:5px;
      line-height:1.15;
    ">
      ${TEXT.aovShort}
    </div>

    <div style="
      display:flex;
      align-items:center;
      gap:5px;
      font-size:10px;
      color:#6b7280;
      margin-bottom:6px;
      line-height:1.15;
      white-space:nowrap;
    ">
      <span style="
        width:6px;
        height:6px;
        background:#0ea5e9;
        border-radius:50%;
        display:inline-block;
        flex-shrink:0;
      "></span>
      <span>${fullTooltipLabel}</span>
    </div>

    <div style="
      display:flex;
      align-items:center;
      justify-content:flex-start;
      background:#f3f4f6;
      padding:4px 6px;
      border-radius:5px;
      font-size:10px;
      font-weight:700;
      color:#111827;
      line-height:1.15;
    ">
      <span>${formatChartMoney(value)}</span>
    </div>
  </div>
`;

          const position = context.chart.canvas.getBoundingClientRect();

          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left =
            position.left + window.pageXOffset + context.tooltip.x + 15 + "px";
          tooltipEl.style.top =
            position.top + window.pageYOffset + context.tooltip.y - 40 + "px";
          tooltipEl.style.pointerEvents = "none";
        },
      },
    },

    scales: {
      x: {
  grid: { display: false },
  ticks: {
    color: "#878e97",
    font: {
      size: mainChartRangeDays > 7 ? 8 : 9,
      weight: "300",
    },
    maxRotation: 0,
    minRotation: 0,
    autoSkip: false,
  },
},
      y: {
        min: 0,
        max: averageOrderValueSuggestedMax,
        ticks: {
          stepSize: averageOrderValueStep,
          color: "#878e97",
          callback: (value) => formatMoneyAxis(value),
          font: {
            size: 10,
            weight: "300",
          },
        },
        grid: {
          drawBorder: false,
          drawTicks: false,
          color: "#f1f5f9",
        },
        border: {
          display: false,
        },
      },
    },
  }}
  plugins={[smallChartVerticalLine]}
/>
  </div>

    {/* FOOTER */}
<div className="flex items-center justify-center gap-2 mt-2 text-[9px] text-gray-500 max-w-full overflow-hidden">
  <div className="flex items-center gap-1.5 min-w-0 max-w-[48%]">
    <span className="w-2 h-2 bg-cyan-500 rounded-[2px] shrink-0"></span>
    <span className="truncate whitespace-nowrap">
      {mainChartFooterLabel}
    </span>
  </div>

  {canOverlayAverageOrderValueCompare && (
    <div className="flex items-center gap-1.5 min-w-0 max-w-[48%]">
      <span className="w-2 h-2 bg-purple-300 rounded-[2px] shrink-0"></span>
      <span className="truncate whitespace-nowrap">
        {getCompareLabel()}
      </span>
    </div>
  )}
</div>

</div>

{/* CARD 3 */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 h-auto sm:h-[250px] overflow-visible shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("salesByProduct")}
  className={`
    text-[13px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.salesByProduct}
</button>
      <div className="mt-[2px]" style={{ borderBottom: "2px dotted #9CA3AF" }} />
     <TooltipBlock
  title={TEXT.salesByProduct}
  subtitle={TEXT.salesByProductDesc}
  equation={
  lang === "AR" ? (
    <>
      <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = مجموع line_total من عناصر الطلب
    </>
  ) : (
    <>
      <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = sum of order_items.line_total
    </>
  )
}
/>
    </div>

    <div className="mt-3 space-y-1.5">
  {salesByProductTotal <= 0 ? (
    <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    visibleSalesByProduct.map((item) => (
      <div
        key={getProductKey(item)}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        {/* row title */}
        <div className="flex items-center gap-1.5 mb-1 min-w-0">
          <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[150px] sm:max-w-[190px]">
            {item.label}
          </p>

          <SalesChannelDelta
            current={item.totalSales}
            previous={item.compareTotalSales}
          />
        </div>

        {/* progress row + amount */}
        <div className="flex items-center gap-2 w-full">
          <div
           className="h-[14px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getSalesProductBarWidth(item.totalSales),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            {formatChartMoney(item.totalSales)}
          </p>
        </div>

        {/* hover tooltip */}
        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[250px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesProductTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {salesChannelDateLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesProductTooltipText.product}
              </span>
              <span className="font-medium text-gray-900 text-right max-w-[150px]">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesProductTooltipText.totalOrders}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.orders}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesProductTooltipText.unitsSold}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.unitsSold}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {salesProductTooltipText.amount}
              </span>
              <span className="font-semibold text-cyan-700 text-right">
                {formatChartMoney(item.totalSales)}
              </span>
            </div>

            {cardsData?.comparison && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {lang === "AR" ? "المقارنة" : "Comparison"}
                </span>
                <span className="font-semibold text-gray-600 text-right">
                  {formatChartMoney(item.compareTotalSales)}
                </span>
              </div>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))
  )}
</div>
  </div>


  {/* ================= BIG CARDS ================= */}

{/* CARD - TOP SELLING PRODUCTS */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] overflow-visible shadow-[0_1px_0_rgba(0,0,0,0.04)]">

  <div className="flex items-start justify-between gap-3">
  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("topProducts")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.topProducts}
</button>

    <div
      className="mt-[0px] mb-1"
      style={{
        borderBottom: "2px dotted #9CA3AF",
        width: "100%",
      }}
    />

    <TooltipBlock
      title={TEXT.topProducts}
      subtitle={TEXT.topSellingProductsDesc}
      equation={
        lang === "AR" ? (
          <>
            <span className="text-blue-800">{TEXT.topProducts}</span> = مجموع qty من عناصر الطلب، مرتبة حسب الكمية
          </>
        ) : (
          <>
            <span className="text-blue-800">{TEXT.topProducts}</span> = sum of order_items.qty, sorted by quantity
          </>
        )
      }
    />
  </div>

  <button
    type="button"
    onClick={() => {
      setShowProductSalesSearch(true);
      setProductSalesSearchQuery("");
      setProductSalesSearchResults([]);
      setProductSalesSearchError("");
    }}
    className="
      w-7 h-7
      rounded-lg
      border border-gray-200
      bg-white
      text-gray-600
      flex items-center justify-center
      hover:bg-gray-50
      hover:text-gray-900
      transition
      shadow-[0_1px_0_rgba(0,0,0,0.04)]
    "
    title={lang === "AR" ? "بحث عن مبيعات منتج" : "Search product sales"}
  >
    <FaSearch className="text-[12px]" />
  </button>
</div>

  <div className="mt-3 space-y-2">
    {topSellingProductsTotal <= 0 ? (
      <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
        {TEXT.noData}
      </div>
    ) : (
      topSellingProducts.map((item) => (
        <div
          key={getProductKey(item)}
          className="group relative flex items-center justify-between gap-3 bg-gray-50 hover:bg-gray-200 transition rounded-md px-2.5 py-2"
        >
          {/* PRODUCT INFO */}
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={item.image || "/images/placeholder-product.jpg"}
              alt=""
              onError={(e) => {
                e.currentTarget.src = "/images/placeholder-product.jpg";
              }}
              className="w-9 h-9 rounded-md object-cover bg-white border border-gray-200 shrink-0"
            />

            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-gray-900 truncate max-w-[150px] sm:max-w-[180px] leading-tight">
                {item.label}
              </p>

              <div className="flex items-center gap-1.5 mt-[2px]">
                <p className="text-[11px] text-gray-500 whitespace-nowrap">
                  {item.orders} {TEXT.orders}
                </p>

                <SalesChannelDelta
                  current={item.unitsSold}
                  previous={item.compareUnitsSold}
                />
              </div>
            </div>
          </div>

          {/* QUANTITY */}
          <div className="shrink-0 text-[13px] font-semibold text-gray-900 mx-2">
            {item.unitsSold}
          </div>

          {/* HOVER TOOLTIP */}
          <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[245px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {topSellingTooltipText.date}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {salesChannelDateLabel}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {topSellingTooltipText.product}
                </span>
                <span className="font-medium text-gray-900 text-right max-w-[150px]">
                  {item.label}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {topSellingTooltipText.totalOrders}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {item.orders}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-cyan-600">
                  {topSellingTooltipText.unitsSold}
                </span>
                <span className="font-semibold text-cyan-700 text-right">
                  {item.unitsSold}
                </span>
              </div>

              {cardsData?.comparison && (
                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-500">
                    {topSellingTooltipText.comparison}
                  </span>
                  <span className="font-semibold text-gray-600 text-right">
                    {item.compareUnitsSold}
                  </span>
                </div>
              )}
            </div>

            <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
          </div>
        </div>
      ))
    )}
  </div>

</div>

{/* CARD 4 - SESSIONS OVER TIME */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">

  <div className="relative group inline-block w-fit cursor-pointer">
   <button
  type="button"
  onClick={() => goToReport("sessionsOverTime")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.sessions}
</button>
<div style={{ borderBottom: "2px dotted #9CA3AF" }} />
    <TooltipBlock
      title={TEXT.sessionsOverTime}
      subtitle={TEXT.sessionsDesc}
    />
  </div>

  <div className="mt-2 flex items-center justify-between gap-3">
  <p className="text-[18px] font-semibold text-gray-900">
    {sessionsTotal}
  </p>

    {cardsData?.comparison && (
      <div className="text-[12px] leading-none">
        {sessionsDelta.fromZero ? (
          <span className="inline-flex items-center gap-[3px] font-semibold">
            <svg
              viewBox="0 0 20 20"
              className="w-[13px] h-[13px] text-[#5aa36b]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 16V4" />
              <path d="M5 9l5-5 5 5" />
            </svg>
          </span>
        ) : sessionsDelta.hideComparison ||
          sessionsDelta.percent === null ? (
          <span className="text-gray-400 font-semibold">-</span>
        ) : sessionsDelta.direction === "up" ? (
          <span className="inline-flex items-center gap-[3px] font-semibold">
            <svg
              viewBox="0 0 20 20"
              className="w-[13px] h-[13px] text-[#5aa36b]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 16V4" />
              <path d="M5 9l5-5 5 5" />
            </svg>

            <span className="text-gray-700">
              {sessionsDelta.percent}%
            </span>
          </span>
        ) : sessionsDelta.direction === "down" ? (
          <span className="inline-flex items-center gap-[3px] font-semibold">
            <svg
              viewBox="0 0 20 20"
              className="w-[13px] h-[13px] text-[#dc6868]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 4v12" />
              <path d="M5 11l5 5 5-5" />
            </svg>

            <span className="text-gray-700">
              {sessionsDelta.percent}%
            </span>
          </span>
        ) : (
          <span className="text-gray-400 font-semibold">-</span>
        )}
      </div>
    )}
  </div>

  {/* CHART */}
  <div className="h-[180px] sm:h-[220px] mt-2 cursor-pointer w-full max-w-full min-w-0 overflow-hidden">
    <Line
      data={{
        labels: sessionsLabels,
        datasets: [
          ...(canOverlaySessionsCompare
            ? [
                {
                  label: lang === "AR" ? "المقارنة" : "Comparison",
                  data: alignedCompareSessions,
                  borderColor: "rgba(196,181,253,0.95)",
                  backgroundColor: "rgba(196,181,253,0.22)",
                  fill: "origin",
                  tension: 0.35,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                  order: 0,
                },
              ]
            : []),
          {
            label: TEXT.sessions,
            data: sessionsValues,
            borderColor: "#0ea5e9",
            backgroundColor: "rgba(14,165,233,0.10)",
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
            order: 1,
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        animation: false,

        interaction: {
          mode: "index",
          intersect: false,
        },
        hover: {
          mode: "index",
          intersect: false,
        },
        events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],

        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: function (context) {
              let tooltipEl = document.getElementById("chart-tooltip");

              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chart-tooltip";
                document.body.appendChild(tooltipEl);
              }

              tooltipEl.style.transform = "translateY(-100%)";

              const tooltip = context.tooltip;

              if (tooltip.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }

              const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
              const point = sessionsTrend[index];
              const comparePoint = compareSessionsTrend[index];

              const value = Number(point?.sessions || 0);
              const compareValue =
                canOverlaySessionsCompare && comparePoint
                  ? Number(comparePoint.sessions || 0)
                  : null;

              const tooltipTimeLabel =
                sessionsGranularity === "hour"
                  ? formatHourLabel(point?.hour)
                  : formatChartDate(point?.date);

              const mainTooltipDateLabel = formatChartDate(point?.date);

              const compareTooltipDateLabel =
                canOverlaySessionsCompare && comparePoint
                  ? formatChartDate(comparePoint.date)
                  : "";

              const fullTooltipLabel =
                sessionsGranularity === "hour"
                  ? formatChartDateTime(point)
                  : formatChartDate(point?.date);

              tooltipEl.innerHTML = canOverlaySessionsCompare
                ? `
                  <div style="
                    background:white;
                    padding:10px 12px;
                    border-radius:8px;
                    box-shadow:0 10px 25px rgba(0,0,0,0.15);
                    border:1px solid #e5e7eb;
                    width:240px;
                  ">
                    <div style="
                      font-size:12px;
                      font-weight:700;
                      color:#111827;
                      margin-bottom:8px;
                    ">
                      ${tooltipTimeLabel}
                    </div>

                    <div style="display:flex;flex-direction:column;gap:6px;">
                      <div style="
                        display:flex;
                        align-items:center;
                        justify-content:space-between;
                        gap:10px;
                        background:#f3f4f6;
                        padding:5px 8px;
                        border-radius:6px;
                        font-size:11px;
                        font-weight:600;
                      ">
                        <span style="display:flex;align-items:center;gap:6px;color:#374151;">
                          <span style="width:7px;height:7px;background:#0ea5e9;border-radius:50%;display:inline-block;"></span>
                          ${mainTooltipDateLabel}
                        </span>
                        <span>${value}</span>
                      </div>

                      <div style="
                        display:flex;
                        align-items:center;
                        justify-content:space-between;
                        gap:10px;
                        background:#f9fafb;
                        padding:5px 8px;
                        border-radius:6px;
                        font-size:11px;
                        font-weight:600;
                        color:#4b5563;
                      ">
                        <span style="display:flex;align-items:center;gap:6px;">
                          <span style="width:7px;height:7px;background:#c4b5fd;border-radius:50%;display:inline-block;"></span>
                          ${compareTooltipDateLabel}
                        </span>
                        <span>${compareValue}</span>
                      </div>
                    </div>
                  </div>
                `
                : `
                  <div style="
                    background:white;
                    padding:8px 10px;
                    border-radius:8px;
                    box-shadow:0 8px 22px rgba(0,0,0,0.14);
                    border:1px solid #e5e7eb;
                    width:155px;
                  ">
                    <div style="
                      font-size:11px;
                      font-weight:700;
                      color:#111827;
                      margin-bottom:5px;
                      line-height:1.15;
                    ">
                      ${TEXT.sessions}
                    </div>

                    <div style="
                      display:flex;
                      align-items:center;
                      gap:5px;
                      font-size:10px;
                      color:#6b7280;
                      margin-bottom:6px;
                      line-height:1.15;
                      white-space:nowrap;
                    ">
                      <span style="
                        width:6px;
                        height:6px;
                        background:#0ea5e9;
                        border-radius:50%;
                        display:inline-block;
                        flex-shrink:0;
                      "></span>
                      <span>${fullTooltipLabel}</span>
                    </div>

                    <div style="
                      display:flex;
                      align-items:center;
                      justify-content:flex-start;
                      background:#f3f4f6;
                      padding:4px 6px;
                      border-radius:5px;
                      font-size:10px;
                      font-weight:700;
                      color:#111827;
                      line-height:1.15;
                    ">
                      <span>${value}</span>
                    </div>
                  </div>
                `;

              const position = context.chart.canvas.getBoundingClientRect();

              tooltipEl.style.opacity = 1;
              tooltipEl.style.position = "absolute";
              tooltipEl.style.left =
                position.left + window.pageXOffset + context.tooltip.x + 15 + "px";
              tooltipEl.style.top =
                position.top + window.pageYOffset + context.tooltip.y - 40 + "px";
              tooltipEl.style.pointerEvents = "none";
            },
          },
        },

        scales: {
         x: {
  grid: { display: false },
  ticks: {
    color: "#878e97",
    font: {
      size: mainChartRangeDays > 7 ? 8 : 9,
      weight: "300",
    },
    maxRotation: 0,
    minRotation: 0,
    autoSkip: false,
  },
},
          y: {
            min: 0,
            max: sessionsSuggestedMax,
            ticks: {
              stepSize: sessionsStep,
              color: "#878e97",
              callback: (value) => value,
              font: {
                size: 10,
                weight: "300",
              },
            },
            grid: {
              drawBorder: false,
              drawTicks: false,
              color: "#f1f5f9",
            },
            border: {
              display: false,
            },
          },
        },
      }}
      plugins={[smallChartVerticalLine]}
    />
  </div>

  {/* FOOTER */}
  <div className="flex items-center justify-center gap-2 mt-2 text-[9px] text-gray-500 max-w-full overflow-hidden">
    <div className="flex items-center gap-1.5 min-w-0 max-w-[48%]">
      <span className="w-2 h-2 bg-cyan-500 rounded-[2px] shrink-0"></span>
      <span className="truncate whitespace-nowrap">
        {mainChartFooterLabel}
      </span>
    </div>

    {canOverlaySessionsCompare && (
      <div className="flex items-center gap-1.5 min-w-0 max-w-[48%]">
        <span className="w-2 h-2 bg-purple-300 rounded-[2px] shrink-0"></span>
        <span className="truncate whitespace-nowrap">
          {getCompareLabel()}
        </span>
      </div>
    )}
  </div>

</div>

  {/* CARD 5 - CONVERSION RATE */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">

  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("conversion")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.conversion}
</button>

    <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

    <TooltipBlock
      title={TEXT.conversion}
      subtitle={TEXT.conversionDesc}
      equation={
        <>
          <span className="text-blue-800">{TEXT.conversionRateFormula}</span> = {TEXT.completed}{" "}
          <span className="text-blue-800">/</span> {TEXT.sessions}
        </>
      }
    />
  </div>

  <div className="mt-2 flex items-center justify-between gap-3">
    <p className="text-[18px] font-semibold text-gray-900">
      {conversionRate.toFixed(2)}%
    </p>

    {cardsData?.comparison && (
      <div className="text-[12px] leading-none">
        {conversionRateDelta.fromZero ? (
          <span className="inline-flex items-center gap-[3px] font-semibold">
            <svg
              viewBox="0 0 20 20"
              className="w-[13px] h-[13px] text-[#5aa36b]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 16V4" />
              <path d="M5 9l5-5 5 5" />
            </svg>
          </span>
        ) : conversionRateDelta.hideComparison ||
          conversionRateDelta.percent === null ? (
          <span className="text-gray-400 font-semibold">-</span>
        ) : conversionRateDelta.direction === "up" ? (
          <span className="inline-flex items-center gap-[3px] font-semibold">
            <svg
              viewBox="0 0 20 20"
              className="w-[13px] h-[13px] text-[#5aa36b]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 16V4" />
              <path d="M5 9l5-5 5 5" />
            </svg>

            <span className="text-gray-700">
              {conversionRateDelta.percent}%
            </span>
          </span>
        ) : conversionRateDelta.direction === "down" ? (
          <span className="inline-flex items-center gap-[3px] font-semibold">
            <svg
              viewBox="0 0 20 20"
              className="w-[13px] h-[13px] text-[#dc6868]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 4v12" />
              <path d="M5 11l5 5 5-5" />
            </svg>

            <span className="text-gray-700">
              {conversionRateDelta.percent}%
            </span>
          </span>
        ) : (
          <span className="text-gray-400 font-semibold">-</span>
        )}
      </div>
    )}
  </div>

  {/* CHART */}
  <div className="h-[180px] sm:h-[220px] mt-2 cursor-pointer w-full max-w-full min-w-0 overflow-hidden">
    <Line
      data={{
        labels: conversionRateLabels,
        datasets: [
          ...(canOverlayConversionRateCompare
            ? [
                {
                  label: lang === "AR" ? "المقارنة" : "Comparison",
                  data: alignedCompareConversionRate,
                  borderColor: "rgba(196,181,253,0.95)",
                  backgroundColor: "rgba(196,181,253,0.22)",
                  fill: "origin",
                  tension: 0.35,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                  order: 0,
                },
              ]
            : []),
          {
            label: TEXT.conversion,
            data: conversionRateValues,
            borderColor: "#0ea5e9",
            backgroundColor: "rgba(14,165,233,0.10)",
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
            order: 1,
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        animation: false,

        interaction: {
          mode: "index",
          intersect: false,
        },
        hover: {
          mode: "index",
          intersect: false,
        },
        events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],

        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: function (context) {
              let tooltipEl = document.getElementById("chart-tooltip");

              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chart-tooltip";
                document.body.appendChild(tooltipEl);
              }

              tooltipEl.style.transform = "translateY(-100%)";

              const tooltip = context.tooltip;

              if (tooltip.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }

              const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
              const point = conversionRateTrend[index];
              const comparePoint = compareConversionRateTrend[index];

              const value = Number(point?.conversionRate || 0);
              const activeSessions = Number(point?.activeSessions || 0);
              const convertedSessions = Number(point?.convertedSessions || 0);

              const compareValue =
                canOverlayConversionRateCompare && comparePoint
                  ? Number(comparePoint.conversionRate || 0)
                  : null;

              const compareActiveSessions =
                canOverlayConversionRateCompare && comparePoint
                  ? Number(comparePoint.activeSessions || 0)
                  : null;

              const compareConvertedSessions =
                canOverlayConversionRateCompare && comparePoint
                  ? Number(comparePoint.convertedSessions || 0)
                  : null;

              const tooltipTimeLabel =
                conversionRateGranularity === "hour"
                  ? formatHourLabel(point?.hour)
                  : formatChartDate(point?.date);

              const mainTooltipDateLabel = formatChartDate(point?.date);

              const compareTooltipDateLabel =
                canOverlayConversionRateCompare && comparePoint
                  ? formatChartDate(comparePoint.date)
                  : "";

              const fullTooltipLabel =
                conversionRateGranularity === "hour"
                  ? formatChartDateTime(point)
                  : formatChartDate(point?.date);

              tooltipEl.innerHTML = canOverlayConversionRateCompare
                ? `
                  <div style="
                    background:white;
                    padding:10px 12px;
                    border-radius:8px;
                    box-shadow:0 10px 25px rgba(0,0,0,0.15);
                    border:1px solid #e5e7eb;
                    width:260px;
                  ">
                    <div style="
                      font-size:12px;
                      font-weight:700;
                      color:#111827;
                      margin-bottom:8px;
                    ">
                      ${tooltipTimeLabel}
                    </div>

                    <div style="display:flex;flex-direction:column;gap:6px;">
                      <div style="
                        background:#f3f4f6;
                        padding:6px 8px;
                        border-radius:6px;
                        font-size:11px;
                        font-weight:600;
                      ">
                        <div style="
                          display:flex;
                          align-items:center;
                          justify-content:space-between;
                          gap:10px;
                        ">
                          <span style="display:flex;align-items:center;gap:6px;color:#374151;">
                            <span style="width:7px;height:7px;background:#0ea5e9;border-radius:50%;display:inline-block;"></span>
                            ${mainTooltipDateLabel}
                          </span>
                          <span>${value.toFixed(2)}%</span>
                        </div>

                        <div style="
                          margin-top:4px;
                          color:#6b7280;
                          font-size:10px;
                          font-weight:500;
                        ">
                          ${convertedSessions} / ${activeSessions} ${TEXT.sessionsLower}
                        </div>
                      </div>

                      <div style="
                        background:#f9fafb;
                        padding:6px 8px;
                        border-radius:6px;
                        font-size:11px;
                        font-weight:600;
                        color:#4b5563;
                      ">
                        <div style="
                          display:flex;
                          align-items:center;
                          justify-content:space-between;
                          gap:10px;
                        ">
                          <span style="display:flex;align-items:center;gap:6px;">
                            <span style="width:7px;height:7px;background:#c4b5fd;border-radius:50%;display:inline-block;"></span>
                            ${compareTooltipDateLabel}
                          </span>
                          <span>${Number(compareValue || 0).toFixed(2)}%</span>
                        </div>

                        <div style="
                          margin-top:4px;
                          color:#6b7280;
                          font-size:10px;
                          font-weight:500;
                        ">
                          ${compareConvertedSessions} / ${compareActiveSessions} ${TEXT.sessionsLower}
                        </div>
                      </div>
                    </div>
                  </div>
                `
                : `
                  <div style="
                    background:white;
                    padding:8px 10px;
                    border-radius:8px;
                    box-shadow:0 8px 22px rgba(0,0,0,0.14);
                    border:1px solid #e5e7eb;
                    width:175px;
                  ">
                    <div style="
                      font-size:11px;
                      font-weight:700;
                      color:#111827;
                      margin-bottom:5px;
                      line-height:1.15;
                    ">
                      ${TEXT.conversion}
                    </div>

                    <div style="
                      display:flex;
                      align-items:center;
                      gap:5px;
                      font-size:10px;
                      color:#6b7280;
                      margin-bottom:6px;
                      line-height:1.15;
                      white-space:nowrap;
                    ">
                      <span style="
                        width:6px;
                        height:6px;
                        background:#0ea5e9;
                        border-radius:50%;
                        display:inline-block;
                        flex-shrink:0;
                      "></span>
                      <span>${fullTooltipLabel}</span>
                    </div>

                    <div style="
                      display:flex;
                      align-items:center;
                      justify-content:flex-start;
                      background:#f3f4f6;
                      padding:4px 6px;
                      border-radius:5px;
                      font-size:10px;
                      font-weight:700;
                      color:#111827;
                      line-height:1.15;
                    ">
                      <span>${value.toFixed(2)}%</span>
                    </div>

                    <div style="
                      margin-top:5px;
                      color:#6b7280;
                      font-size:10px;
                      font-weight:500;
                    ">
                      ${convertedSessions} / ${activeSessions} sessions
                    </div>
                  </div>
                `;

              const position = context.chart.canvas.getBoundingClientRect();

              tooltipEl.style.opacity = 1;
              tooltipEl.style.position = "absolute";
              tooltipEl.style.left =
                position.left + window.pageXOffset + context.tooltip.x + 15 + "px";
              tooltipEl.style.top =
                position.top + window.pageYOffset + context.tooltip.y - 40 + "px";
              tooltipEl.style.pointerEvents = "none";
            },
          },
        },

        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "#878e97",
              font: {
                size: mainChartRangeDays > 7 ? 8 : 9,
                weight: "300",
              },
              maxRotation: 0,
              minRotation: 0,
              autoSkip: false,
            },
          },
          y: {
            min: 0,
            max: conversionRateSuggestedMax,
            ticks: {
              stepSize: conversionRateStep,
              color: "#878e97",
              callback: (value) => `${value}%`,
              font: {
                size: 10,
                weight: "300",
              },
            },
            grid: {
              drawBorder: false,
              drawTicks: false,
              color: "#f1f5f9",
            },
            border: {
              display: false,
            },
          },
        },
      }}
      plugins={[smallChartVerticalLine]}
    />
  </div>

  {/* FOOTER */}
  <div className="flex items-center justify-center gap-2 mt-2 text-[9px] text-gray-500 max-w-full overflow-hidden">
    <div className="flex items-center gap-1.5 min-w-0 max-w-[48%]">
      <span className="w-2 h-2 bg-cyan-500 rounded-[2px] shrink-0"></span>
      <span className="truncate whitespace-nowrap">
        {mainChartFooterLabel}
      </span>
    </div>

    {canOverlayConversionRateCompare && (
      <div className="flex items-center gap-1.5 min-w-0 max-w-[48%]">
        <span className="w-2 h-2 bg-purple-300 rounded-[2px] shrink-0"></span>
        <span className="truncate whitespace-nowrap">
          {getCompareLabel()}
        </span>
      </div>
    )}
  </div>

</div>


  {/* CARD 6 - CONVERSION RATE BREAKDOWN */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-visible">

  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("conversionBreakdown")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.conversionBreakdown}
</button>

    <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

    <TooltipBlock
      title={TEXT.conversionBreakdown}
      subtitle={
        lang === "AR"
          ? "يعرض مراحل التحويل من الجلسات إلى الإضافة للسلة ثم الوصول للدفع ثم إكمال الطلب"
          : "Shows the funnel from sessions to cart additions, checkout, and completed orders"
      }
      equation={
        <>
          <span className="text-blue-800">{TEXT.conversionRateFormula}</span>{" "}
          = {TEXT.completed} <span className="text-blue-800">/</span>{" "}
          {TEXT.sessions}
        </>
      }
    />
  </div>

  <div className="mt-2 flex items-center justify-between gap-3">
    <p className="text-[18px] font-semibold text-gray-900">
      {Number(conversionBreakdown.completedRate || 0).toFixed(2)}%
    </p>

    {cardsData?.comparison && (
      <FunnelDelta
        current={conversionBreakdown.completedRate}
        previous={compareConversionBreakdown?.completedRate || 0}
      />
    )}
  </div>

  <div className="grid grid-cols-4 mt-6 text-center text-sm text-gray-900 h-[235px] overflow-hidden">
  {[
    {
      label: TEXT.sessionsShort,
      count: conversionBreakdown.sessions,
      rate: conversionBreakdown.sessionsRate,
      compareCount: compareConversionBreakdown?.sessions || 0,
      compareRate: compareConversionBreakdown?.sessionsRate || 0,
    },
    {
      label: TEXT.addedToCart,
      count: conversionBreakdown.addedToCart,
      rate: conversionBreakdown.addedToCartRate,
      compareCount: compareConversionBreakdown?.addedToCart || 0,
      compareRate: compareConversionBreakdown?.addedToCartRate || 0,
    },
    {
      label: TEXT.reachedCheckout,
      count: conversionBreakdown.reachedCheckout,
      rate: conversionBreakdown.reachedCheckoutRate,
      compareCount: compareConversionBreakdown?.reachedCheckout || 0,
      compareRate: compareConversionBreakdown?.reachedCheckoutRate || 0,
    },
    {
      label: TEXT.completed,
      count: conversionBreakdown.completed,
      rate: conversionBreakdown.completedRate,
      compareCount: compareConversionBreakdown?.completed || 0,
      compareRate: compareConversionBreakdown?.completedRate || 0,
    },
  ].map((item, i) => (
    <div key={item.label} className="relative px-2 h-full">
      {i !== 0 && (
        <div className="absolute left-0 top-1 bottom-4 w-px bg-gradient-to-b from-gray-300 to-transparent"></div>
      )}

      <p className="text-gray-900 text-xs mb-2 leading-tight min-h-[30px] flex items-center justify-center">
        {item.label}
      </p>

      {/* CURRENT VALUE */}
      <div className="mt-2">
        <ProgressRing value={item.rate} color="#06B6D4" />
      </div>

      <p className="text-[11px] text-gray-500 mt-2">
        {item.count} {TEXT.sessionsShort.toLowerCase()}
      </p>

      {/* COMPARISON VALUE */}
      {cardsData?.comparison && compareConversionBreakdown && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <ProgressRing
            value={item.compareRate}
            color="#DC6868"
            size={38}
            stroke={4}
          />

          <p className="text-[10px] text-gray-400 mt-1">
            {item.compareCount} {TEXT.sessionsShort.toLowerCase()}
          </p>

          <div className="mt-1">
            <FunnelDelta
              current={item.rate}
              previous={item.compareRate}
            />
          </div>
        </div>
      )}
    </div>
  ))}
</div>

</div>

 {/* CARD 7 - SESSIONS BY DEVICE */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] overflow-visible shadow-[0_1px_0_rgba(0,0,0,0.04)]">
  
  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("sessionsByDevice")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.sessionsByDevice}
</button>
    <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
    
    <TooltipBlock
      title={TEXT.sessionsByDevice}
      subtitle={TEXT.sessionsByDeviceDesc}
    />
  </div>

  {sessionsByDeviceTotal <= 0 ? (
    <div className="h-[270px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    <div className="mt-4 flex flex-col items-center">
      {/* DONUTS */}
      <div
        className={`
          flex items-center justify-center gap-6 w-full
          ${cardsData?.comparison ? "" : "mt-2"}
        `}
      >
        {/* CURRENT */}
        <div className="flex flex-col items-center">
          <DeviceDonutChart
            items={sessionsByDevice}
            total={sessionsByDeviceTotal}
            valueKey="sessions"
            percentKey="percent"
            size={cardsData?.comparison ? 130 : 155}
            stroke={cardsData?.comparison ? 22 : 27}
          />

          <p className="mt-2 text-[10px] text-gray-500 text-center max-w-[120px] truncate">
            {mainChartFooterLabel}
          </p>
        </div>

        {/* COMPARISON */}
        {cardsData?.comparison && (
          <div className="flex flex-col items-center opacity-80">
            <DeviceDonutChart
              items={sessionsByDevice}
              total={compareSessionsByDeviceTotal}
              valueKey="compareSessions"
              percentKey="comparePercent"
              size={130}
              stroke={22}
            />

            <p className="mt-2 text-[10px] text-gray-500 text-center max-w-[120px] truncate">
              {getCompareLabel()}
            </p>
          </div>
        )}
      </div>

      {/* LEGEND */}
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 w-full">
        {sessionsByDevice.map((item) => (
          <div
            key={item.device}
            onMouseEnter={() => setActiveDeviceTooltip(item.device)}
            onMouseLeave={() => setActiveDeviceTooltip(null)}
            className="relative flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50 transition cursor-pointer"
          >
            <span
              className="w-3 h-3 rounded-[3px] shrink-0"
              style={{
                backgroundColor: deviceColorMap[item.device] || "#9CA3AF",
              }}
            />

            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-gray-800 truncate">
                {item.label}
              </p>

              <p className="text-[10px] text-gray-500">
                {item.percent.toFixed(0)}%
                {cardsData?.comparison && (
                  <span className="text-gray-400">
                    {" "}
                    / {item.comparePercent.toFixed(0)}%
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
  <p className="text-[11px] font-semibold text-gray-700">
    {item.sessions}
  </p>

  <DeviceDelta
    current={item.sessions}
    previous={item.compareSessions}
  />
</div>

            {/* CONTROLLED TOOLTIP */}
            {activeDeviceTooltip === item.device && (
              <div className="pointer-events-none absolute left-0 bottom-full z-[999] mb-2 w-[240px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.14)]">
                <div className="space-y-1.5 text-[11px]">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">
                      {lang === "AR" ? "التاريخ" : "Date"}
                    </span>
                    <span className="font-medium text-gray-900 text-right">
                      {mainChartFooterLabel}
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">
                      {lang === "AR" ? "نوع الجهاز" : "Device type"}
                    </span>
                    <span className="font-medium text-gray-900 text-right">
                      {item.label}
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                      <span className="text-gray-500">
                        {TEXT.sessionsShort}
                      </span>

                      <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900 text-right">
                        {item.sessions}

                        <DeviceDelta
                          current={item.sessions}
                          previous={item.compareSessions}
                        />
                      </span>
                    </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">
                      {lang === "AR" ? "النسبة" : "Percent"}
                    </span>
                    <span className="font-semibold text-gray-900 text-right">
                      {item.percent.toFixed(2)}%
                    </span>
                  </div>

                  {cardsData?.comparison && (
                    <>
                      <div className="border-t border-gray-100 my-1" />

                      <div className="flex items-start justify-between gap-3">
                        <span className="text-gray-500">
                          {lang === "AR" ? "تاريخ المقارنة" : "Comparison date"}
                        </span>
                        <span className="font-medium text-gray-700 text-right">
                          {getCompareLabel()}
                        </span>
                      </div>

                      <div className="flex items-start justify-between gap-3">
                        <span className="text-gray-500">
                          {lang === "AR" ? "جلسات المقارنة" : "Comparison sessions"}
                        </span>
                        <span className="font-semibold text-gray-600 text-right">
                          {item.compareSessions}
                        </span>
                      </div>

                      <div className="flex items-start justify-between gap-3">
                        <span className="text-gray-500">
                          {lang === "AR" ? "نسبة المقارنة" : "Comparison percent"}
                        </span>
                        <span className="font-semibold text-gray-600 text-right">
                          {item.comparePercent.toFixed(2)}%
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="absolute left-5 top-full w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45 -mt-[6px]" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )}

</div>

  {/* CARD 8 - SESSIONS BY LOCATION */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] overflow-visible shadow-[0_1px_0_rgba(0,0,0,0.04)]">
  
  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("sessionsByLocation")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.sessionsByLocation}
</button>

    <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

    <TooltipBlock
      title={TEXT.sessionsByLocation}
      subtitle={TEXT.sessionsByLocationDesc}
    />
  </div>

  {sessionsByLocationTotal <= 0 ? (
    <div className="h-[270px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    <div className="mt-4 flex flex-col items-center">
      {/* DONUTS */}
      <div
        className={`
          flex items-center justify-center gap-6 w-full
          ${cardsData?.comparison ? "" : "mt-2"}
        `}
      >
        {/* CURRENT */}
        <div className="relative group/locationChart flex flex-col items-center">
          <LocationDonutChart
            items={sessionsByLocationWithColors}
            total={sessionsByLocationTotal}
            size={cardsData?.comparison ? 130 : 155}
            stroke={cardsData?.comparison ? 22 : 27}
          />

          <p className="mt-2 text-[10px] text-gray-500 text-center max-w-[120px] truncate">
            {mainChartFooterLabel}
          </p>

          {/* CURRENT HOVER TOOLTIP */}
          <div className="pointer-events-none absolute left-1/2 bottom-full z-[999] mb-2 hidden w-[min(270px,calc(100vw-32px))] -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.14)] group-hover/locationChart:block">
            <div className="space-y-2 text-[11px]">
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {locationTooltipText.date}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {mainChartFooterLabel}
                </span>
              </div>

              <div className="pt-1 border-t border-gray-100 space-y-1.5">
                {sessionsByLocationWithColors.map((item) => (
                  <div key={item.country}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="w-2 h-2 rounded-[2px] shrink-0"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-gray-600 truncate max-w-[120px]">
                          {item.label}
                        </span>
                      </span>

                      <span className="font-semibold text-gray-900 whitespace-nowrap">
                        {item.sessions} · {item.percent.toFixed(1)}%
                      </span>
                    </div>

                    {item.country?.toLowerCase() === "lebanon" &&
                      item.cities?.length > 0 && (
                        <div className="mt-1 ml-3 pl-2 border-l border-gray-100 space-y-[2px]">
                          {item.cities.map((city) => (
                            <div
                              key={city.city}
                              className="flex items-center justify-between gap-3 text-[10px]"
                            >
                              <span className="text-gray-400 truncate max-w-[130px]">
                                {city.city}
                              </span>
                              <span className="font-medium text-gray-600">
                                {city.sessions}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute left-1/2 top-full w-3 h-3 -translate-x-1/2 bg-white border-r border-b border-gray-200 rotate-45 -mt-[6px]" />
          </div>
        </div>

        {/* COMPARISON */}
        {cardsData?.comparison && (
          <div className="relative group/locationCompareChart flex flex-col items-center opacity-80">
            <LocationDonutChart
  items={sessionsByLocationWithColors}
  total={sessionsByLocationWithColors.reduce(
    (sum, item) => sum + Number(item.compareSessions || 0),
    0
  )}
  percentKey="comparePercent"
  size={130}
  stroke={22}
/>

            <p className="mt-2 text-[10px] text-gray-500 text-center max-w-[120px] truncate">
              {getCompareLabel()}
            </p>

            {/* COMPARISON HOVER TOOLTIP */}
            <div className="pointer-events-none absolute left-1/2 bottom-full z-[999] mb-2 hidden w-[min(270px,calc(100vw-32px))] -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.14)] group-hover/locationCompareChart:block">
              <div className="space-y-2 text-[11px]">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-500">
                    {locationTooltipText.date}
                  </span>
                  <span className="font-medium text-gray-900 text-right">
                    {getCompareLabel()}
                  </span>
                </div>

                <div className="pt-1 border-t border-gray-100 space-y-1.5">
                  {sessionsByLocationWithColors.map((item) => (
                    <div
                      key={`${item.country}-compare-tooltip`}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="w-2 h-2 rounded-[2px] shrink-0"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-gray-600 truncate max-w-[120px]">
                          {item.label}
                        </span>
                      </span>

                      <span className="font-semibold text-gray-900 whitespace-nowrap">
                        {item.compareSessions} · {item.comparePercent.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute left-1/2 top-full w-3 h-3 -translate-x-1/2 bg-white border-r border-b border-gray-200 rotate-45 -mt-[6px]" />
            </div>
          </div>
        )}
      </div>

      {/* LEGEND */}
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 w-full">
        {sessionsByLocationWithColors.slice(0, 5).map((item) => (
          <div
            key={item.country}
            className="relative flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50 transition cursor-pointer"
          >
            <span
              className="w-3 h-3 rounded-[3px] shrink-0"
              style={{ backgroundColor: item.color }}
            />

            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-gray-800 truncate">
                {item.label}
              </p>

              <p className="text-[10px] text-gray-500">
                {item.percent.toFixed(0)}%
                {cardsData?.comparison && (
                  <span className="text-gray-400">
                    {" "}
                    / {item.comparePercent.toFixed(0)}%
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <p className="text-[11px] font-semibold text-gray-700">
                {item.sessions}
              </p>

              <DeviceDelta
                current={item.sessions}
                previous={item.compareSessions}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

</div>


{/* ================= COHORT + LANDING PAGE ================= */}
<div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-4 mb-6">

  {/* ================= CARD 1: COHORT ================= */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-visible">

    {/* TITLE */}
<div className="flex items-start justify-between gap-4 mb-4">
  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("cohortAnalysis")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.cohortAnalysis}
</button>

    <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

    <TooltipBlock
      title={TEXT.cohortAnalysis}
      subtitle={TEXT.cohortDesc}
    />
  </div>

  <CohortHelpTooltip />
</div>

    {/* TABLE HEADER */}
    <div className="grid grid-cols-[120px_1fr] text-[12px] text-gray-500 mb-2">
  <span>{TEXT.cohortLabel}</span>

  <div className="grid grid-cols-8 text-center w-full">
    <span className="col-span-8 text-center">{TEXT.months}</span>
  </div>
</div>

    {/* ROWS */}
    <div className="overflow-visible">
  <CohortTable />
</div>

  </div>

  {/* ================= CARD 2: LANDING PAGE ================= */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)]">

    <div className="relative group inline-block w-fit cursor-pointer mb-4">
      <button
  type="button"
  onClick={() => goToReport("landingPage")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.landingPage}
</button>

      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

      <TooltipBlock
        title={TEXT.landingPage}
        subtitle={TEXT.landingPageDesc}
      />
    </div>

    {sessionsByLandingPageTotal <= 0 ? (
  <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">
    {TEXT.noData}
  </div>
) : (
  <div className="mt-1 space-y-1.5 h-[260px] overflow-visible">
    {sessionsByLandingPage.map((item) => (
      <div
        key={item.landingPage || item.label}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        {/* PAGE TITLE */}
        <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
          <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[220px]">
            {item.label}
          </p>

          <DeviceDelta
            current={item.sessions}
            previous={item.compareSessions}
          />
        </div>

        {/* BAR + VALUE */}
        <div className="flex items-center gap-2 w-full">
          <div
           className="h-[13px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getLandingPageBarWidth(item.sessions),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            {item.sessions} {TEXT.sessionsShort}
          </p>
        </div>

        {/* URL */}
        <p className="mt-1 text-[10px] text-gray-400 truncate max-w-[260px]">
          {item.landingPage}
        </p>

        {/* HOVER TOOLTIP */}
        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[min(270px,calc(100vw-32px))] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {landingPageTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {mainChartFooterLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {landingPageTooltipText.page}
              </span>
              <span className="font-medium text-gray-900 text-right max-w-[160px]">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {landingPageTooltipText.url}
              </span>
              <span className="font-medium text-gray-900 text-right max-w-[160px] break-all">
                {item.landingPage}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {landingPageTooltipText.sessions}
              </span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-cyan-700 text-right">
                {item.sessions}

                <DeviceDelta
                  current={item.sessions}
                  previous={item.compareSessions}
                />
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {landingPageTooltipText.percent}
              </span>
              <span className="font-semibold text-gray-900 text-right">
                {item.percent.toFixed(2)}%
              </span>
            </div>

            {cardsData?.comparison && (
              <>
                <div className="border-t border-gray-100 my-1" />

                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-500">
                    {landingPageTooltipText.comparison}
                  </span>
                  <span className="font-semibold text-gray-600 text-right">
                    {item.compareSessions} {TEXT.sessionsShort}
                  </span>
                </div>

                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-500">
                    {lang === "AR" ? "نسبة المقارنة" : "Comparison percent"}
                  </span>
                  <span className="font-semibold text-gray-600 text-right">
                    {item.comparePercent.toFixed(2)}%
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))}
  </div>
)}

  </div>

</div>

{/* ================= NEW REFERRER ANALYTICS (3 CARDS) ================= */}
<div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 mb-6 w-full max-w-full min-w-0">

  {/* CARD 10 */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("sessionsBySocialReferrer")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.sessionsBySocialReferrerTitle}
</button>
      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
      
      <TooltipBlock
        title={TEXT.sessionsBySocialReferrerTitle}
        subtitle={TEXT.sessionsBySocialReferrerDesc}
      />
    </div>

   <div className="mt-4 space-y-1.5 overflow-visible">
  {sessionsBySocialReferrerTotal <= 0 ? (
    <div className="h-auto sm:h-[250px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    sessionsBySocialReferrer.map((item) => (
      <div
        key={item.referrer}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[150px] sm:max-w-[180px]">
            {item.label}
          </p>

          <DeviceDelta
            current={item.sessions}
            previous={item.compareSessions}
          />
        </div>

        <div className="flex items-center gap-2 w-full">
          <div
            className="h-[13px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getSocialReferrerBarWidth(item.sessions),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            {item.sessions}
          </p>
        </div>

        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[230px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {socialReferrerTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {salesChannelDateLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {socialReferrerTooltipText.referrer}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {socialReferrerTooltipText.sessions}
              </span>
              <span className="font-semibold text-cyan-700 text-right">
                {item.sessions}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {socialReferrerTooltipText.percent}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.percent.toFixed(2)}%
              </span>
            </div>

            {cardsData?.comparison && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {socialReferrerTooltipText.comparison}
                </span>
                <span className="font-semibold text-gray-600 text-right">
                  {item.compareSessions}
                </span>
              </div>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))
  )}
</div>

  </div>

  {/* CARD 11 */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("salesByReferrer")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.salesByReferrerTitle}
</button>
      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
      
      <TooltipBlock
        title={TEXT.salesByReferrerTitle}
        subtitle={TEXT.salesByReferrerDesc}
        equation={
          <>
            <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = {TEXT.netSalesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.additionalFeesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.shippingChargeFormula}
          </>
        }
      />
    </div>

    <div className="mt-4 space-y-1.5 overflow-visible">
  {salesByReferrerTotal <= 0 ? (
    <div className="h-auto sm:h-[250px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    salesByReferrer.map((item) => (
      <div
        key={item.referrer}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        {/* row title */}
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[220px]">
            {item.label}
          </p>

          <SalesChannelDelta
            current={item.totalSales}
            previous={item.compareTotalSales}
          />
        </div>

        {/* progress row + amount */}
        <div className="flex items-center gap-2 w-full">
          <div
            className="h-[13px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getSalesReferrerBarWidth(item.totalSales),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            {formatChartMoney(item.totalSales)}
          </p>
        </div>

        {/* hover tooltip */}
        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[230px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesReferrerTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {salesChannelDateLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesReferrerTooltipText.referrer}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesReferrerTooltipText.totalOrders}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.orders}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {salesReferrerTooltipText.amount}
              </span>
              <span className="font-semibold text-cyan-700 text-right">
                {formatChartMoney(item.totalSales)}
              </span>
            </div>

            {cardsData?.comparison && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {salesReferrerTooltipText.comparison}
                </span>
                <span className="font-semibold text-gray-600 text-right">
                  {formatChartMoney(item.compareTotalSales)}
                </span>
              </div>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))
  )}
</div>

  </div>

  {/* CARD 12 */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("performanceByReferringChannel")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.performanceByReferringChannel}
</button>
      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
      
      <TooltipBlock
        title={TEXT.performanceByReferringChannel}
        subtitle={TEXT.performanceByReferringChannelDesc}
        equation={
          <>
            <span className="text-blue-800">Total sales last non direct click</span> = {TEXT.netSalesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.additionalFeesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.shippingChargeFormula}
          </>
        }
      />
    </div>

    <div className="mt-4 space-y-1.5 overflow-visible">
  {performanceByReferringChannelTotal <= 0 ? (
    <div className="h-auto sm:h-[250px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    performanceByReferringChannel.map((item) => (
      <div
        key={item.channel}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[150px]">
                {item.label}
              </p>

              <SalesChannelDelta
                current={item.totalSales}
                previous={item.compareTotalSales}
              />
            </div>

            <p className="text-[9px] text-gray-500 mt-[1px]">
              {item.sessions} {TEXT.sessions.toLowerCase()} · {item.orders}{" "}
              {TEXT.orders.toLowerCase()} · {item.conversionRate.toFixed(1)}%
            </p>
          </div>

          <p className="shrink-0 text-[11px] font-semibold text-gray-700 whitespace-nowrap">
            {formatChartMoney(item.totalSales)}
          </p>
        </div>

        <div className="flex items-center gap-2 w-full">
          <div
            className="h-[12px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getPerformanceChannelBarWidth(item.totalSales),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[10px] font-semibold text-gray-500 whitespace-nowrap">
            ${TEXT.aovShort} {formatChartMoney(item.averageOrderValue)}
          </p>
        </div>

        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[250px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {performanceChannelTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {salesChannelDateLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {performanceChannelTooltipText.channel}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {performanceChannelTooltipText.sessions}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.sessions}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {performanceChannelTooltipText.orders}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.orders}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {performanceChannelTooltipText.conversionRate}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.conversionRate.toFixed(2)}%
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {performanceChannelTooltipText.averageOrderValue}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {formatChartMoney(item.averageOrderValue)}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {performanceChannelTooltipText.sales}
              </span>
              <span className="font-semibold text-cyan-700 text-right">
                {formatChartMoney(item.totalSales)}
              </span>
            </div>

            {cardsData?.comparison && (
              <div className="flex items-start justify-between gap-3 pt-1 border-t border-gray-100">
                <span className="text-gray-500">
                  {performanceChannelTooltipText.comparison}
                </span>
                <span className="font-semibold text-gray-600 text-right">
                  {formatChartMoney(item.compareTotalSales)}
                </span>
              </div>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))
  )}
</div>

  </div>

</div>

{/* ================= NEW REFERRER ANALYTICS (3 CARDS) ================= */}
<div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 mb-6 w-full max-w-full min-w-0">

  {/* CARD 1 - SESSIONS BY REFERRER */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("sessionsByReferrer")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.sessionsByReferrer}
</button>
      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
      
      <TooltipBlock
        title={TEXT.sessionsByReferrer}
        subtitle={TEXT.sessionsByReferrerDesc}
      />
    </div>

    <div className="mt-4 space-y-1.5 overflow-visible">
  {sessionsByReferrerTotal <= 0 ? (
    <div className="h-[270px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    sessionsByReferrer.map((item) => (
      <div
        key={item.referrer}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[220px]">
            {item.label}
          </p>

          <DeviceDelta
            current={item.sessions}
            previous={item.compareSessions}
          />
        </div>

        <div className="flex items-center gap-2 w-full">
          <div
            className="h-[13px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getSessionsReferrerBarWidth(item.sessions),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            {item.sessions}
          </p>
        </div>

        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-1 hidden w-[230px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {sessionsReferrerTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {salesChannelDateLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {sessionsReferrerTooltipText.referrer}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {sessionsReferrerTooltipText.sessions}
              </span>
              <span className="font-semibold text-cyan-700 text-right">
                {item.sessions}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {sessionsReferrerTooltipText.percent}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.percent.toFixed(2)}%
              </span>
            </div>

            {cardsData?.comparison && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {sessionsReferrerTooltipText.comparison}
                </span>
                <span className="font-semibold text-gray-600 text-right">
                  {item.compareSessions}
                </span>
              </div>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))
  )}
</div>

  </div>

  {/* CARD 9 - SOCIAL REFERRER */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    
    <div className="relative group inline-block w-fit cursor-pointer">
      <button
  type="button"
  onClick={() => goToReport("salesBySocial")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.salesBySocial}
</button>
      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
      
      <TooltipBlock
        title={TEXT.salesBySocial}
        subtitle={TEXT.salesBySocialDesc}
        equation={
          <>
            <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = {TEXT.netSalesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.additionalFeesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.shippingChargeFormula}
          </>
        }
      />
    </div>

    <div className="mt-4 space-y-1.5 overflow-visible">
  {salesBySocialReferrerTotal <= 0 ? (
    <div className="h-[270px] flex items-center justify-center text-gray-400 text-sm">
      {TEXT.noData}
    </div>
  ) : (
    salesBySocialReferrer.map((item) => (
      <div
        key={item.referrer}
        className="group relative rounded-md px-1.5 py-[2px] transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-[11px] font-medium text-gray-800 leading-tight truncate max-w-[220px]">
            {item.label}
          </p>

          <SalesChannelDelta
            current={item.totalSales}
            previous={item.compareTotalSales}
          />
        </div>

        <div className="flex items-center gap-2 w-full">
          <div
            className="h-[13px] bg-cyan-500 transition-all duration-300 shrink-0"
            style={{
              width: getSalesSocialReferrerBarWidth(item.totalSales),
              borderRadius: "0 2px 2px 0",
            }}
          />

          <p className="shrink-0 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            {formatChartMoney(item.totalSales)}
          </p>
        </div>

        <div className="pointer-events-none absolute left-2 top-full z-[70] mt-1 hidden w-[240px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesSocialReferrerTooltipText.date}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {salesChannelDateLabel}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesSocialReferrerTooltipText.referrer}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.label}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-500">
                {salesSocialReferrerTooltipText.totalOrders}
              </span>
              <span className="font-medium text-gray-900 text-right">
                {item.orders}
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <span className="text-cyan-600">
                {salesSocialReferrerTooltipText.amount}
              </span>
              <span className="font-semibold text-cyan-700 text-right">
                {formatChartMoney(item.totalSales)}
              </span>
            </div>

            {cardsData?.comparison && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {salesSocialReferrerTooltipText.comparison}
                </span>
                <span className="font-semibold text-gray-600 text-right">
                  {formatChartMoney(item.compareTotalSales)}
                </span>
              </div>
            )}
          </div>

          <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
        </div>
      </div>
    ))
  )}
</div>

  </div>


  {/* CARD - PRODUCTS BY SELL-THROUGH RATE */}
<div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] overflow-visible shadow-[0_1px_0_rgba(0,0,0,0.04)]">

  <div className="relative group inline-block w-fit cursor-pointer">
    <button
  type="button"
  onClick={() => goToReport("sellThrough")}
  className={`
    text-[14px] font-semibold text-gray-900 hover:text-blue-700 transition
    ${lang === "AR" ? "text-right" : "text-left"}
  `}
>
  {TEXT.sellThrough}
</button>

    <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

    <TooltipBlock
      title={TEXT.sellThrough}
      subtitle={TEXT.sellThroughDesc}
      equation={
        <>
          <span className="text-blue-800">{TEXT.sellThroughRateFormula}</span>{" "}
          = {TEXT.inventoryUnitsSold} / ({TEXT.inventoryUnitsSold} +{" "}
          {TEXT.endingInventoryUnits})
        </>
      }
    />
  </div>

  <div className="mt-3 space-y-2">
    {productsBySellThroughRateTotal <= 0 ? (
      <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
        {TEXT.noData}
      </div>
    ) : (
      productsBySellThroughRate.map((item) => (
        <div
          key={getProductKey(item)}
          className="group relative flex items-center justify-between gap-3 bg-gray-50 hover:bg-gray-200 transition rounded-md px-2.5 py-2"
        >
          {/* PRODUCT INFO */}
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={item.image || "/images/placeholder-product.jpg"}
              alt=""
              onError={(e) => {
                e.currentTarget.src = "/images/placeholder-product.jpg";
              }}
              className="w-9 h-9 rounded-md object-cover bg-white border border-gray-200 shrink-0"
            />

            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-gray-900 truncate max-w-[150px] sm:max-w-[180px] leading-tight">
                {item.label}
              </p>

              <div className="flex items-center gap-1.5 mt-[2px]">
                <p className="text-[11px] text-gray-500 whitespace-nowrap">
                  {item.unitsSold} {TEXT.unitsSold}
                </p>

                <SalesChannelDelta
                  current={item.sellThroughRate}
                  previous={item.compareSellThroughRate}
                />
              </div>
            </div>
          </div>

          {/* RATE */}
          <div className="shrink-0 text-[13px] font-semibold text-gray-900 mx-2">
            {item.sellThroughRate.toFixed(0)}%
          </div>

          {/* HOVER TOOLTIP */}
          <div className="pointer-events-none absolute left-2 top-full z-[70] mt-2 hidden w-[min(270px,calc(100vw-32px))] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] sm:group-hover:block">
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {sellThroughTooltipText.date}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {salesChannelDateLabel}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {sellThroughTooltipText.product}
                </span>
                <span className="font-medium text-gray-900 text-right max-w-[150px]">
                  {item.label}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-cyan-600">
                  {sellThroughTooltipText.sellThroughRate}
                </span>
                <span className="font-semibold text-cyan-700 text-right">
                  {item.sellThroughRate.toFixed(2)}%
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {sellThroughTooltipText.unitsSold}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {item.unitsSold}
                </span>
              </div>

              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-500">
                  {sellThroughTooltipText.endingInventory}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {item.endingInventory}
                </span>
              </div>

              {cardsData?.comparison && (
                <>
                  <div className="border-t border-gray-100 my-1" />

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">
                      {sellThroughTooltipText.comparison}
                    </span>
                    <span className="font-semibold text-gray-600 text-right">
                      {item.compareSellThroughRate.toFixed(2)}%
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="absolute left-5 top-[-5px] w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
          </div>
        </div>
      ))
    )}
  </div>

</div>

</div>

{/* ================= POS STAFF SALES (SINGLE CARD) ================= */}
<div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 mb-6 w-full max-w-full min-w-0">

  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">

    <div className="relative group inline-block w-fit cursor-pointer">
      <p className="text-[14px] font-semibold text-gray-900">
        {TEXT.posStaffSales}
      </p>

      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />

      <TooltipBlock
        title={TEXT.posStaffSales}
        subtitle={TEXT.posStaffSalesDesc}
        equation={
          <>
            <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = {TEXT.netSalesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.additionalFeesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.deliveryChargesFormula}
          </>
        }
      />
    </div>

    {/* EMPTY STATE */}
    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
      {TEXT.noData}
    </div>

  </div>

{/* CARD 2 - TOTAL SALES BY POS LOCATION */}
  <div className="bg-white border border-gray-200 w-full max-w-full min-w-0  rounded-xl  p-4 sm:p-5 sm:h-[360px] shadow-[0_1px_0_rgba(0,0,0,0.04)]">
    
    <div className="relative group inline-block w-fit cursor-pointer">
      <p className="text-[14px] font-semibold text-gray-900">
        {TEXT.salesByPOS}
      </p>
      <div style={{ borderBottom: "2px dotted #9CA3AF" }} />
      
      <TooltipBlock
        title={TEXT.salesByPOS}
        subtitle={TEXT.salesByPOSDesc}
        equation={
          <>
            <span className="text-blue-800">{TEXT.totalSalesFormula}</span> = {TEXT.netSalesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.additionalFeesFormula}{" "}
            <span className="text-blue-800">+</span> {TEXT.shippingChargeFormula}
          </>
        }
      />
    </div>

    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
      {TEXT.noData}
    </div>

  </div>

</div>
  </>
    )}
  </div>

  {showProductSalesSearch && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
    <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="text-[16px] font-semibold text-gray-900">
            {lang === "AR"
              ? "البحث عن مبيعات منتج"
              : "Search product sales"}
          </h3>

          <p className="text-[12px] text-gray-500 mt-1">
            {lang === "AR"
              ? "يعرض إجمالي مبيعات المنتج منذ البداية بدون نطاق التاريخ"
              : "Shows total product sales overall, ignoring date range"}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowProductSalesSearch(false)}
          className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleProductSalesSearch} className="px-5 py-4 border-b border-gray-100">
        <label className="block text-[12px] font-medium text-gray-700 mb-2">
          {lang === "AR"
            ? "ابحث باسم المنتج أو الرقم"
            : "Search by product name or ID"}
        </label>

        <div className="flex items-center gap-2">
          <input
            value={productSalesSearchQuery}
            onChange={(e) => setProductSalesSearchQuery(e.target.value)}
            placeholder={
              lang === "AR"
                ? "مثال: 101 أو اسم المنتج"
                : "Example: 101 or product name"
            }
            className="
              flex-1 h-9
              border border-gray-300
              rounded-lg
              px-3
              text-[13px]
              outline-none
              focus:ring-2 focus:ring-gray-900/10
              focus:border-gray-400
            "
            autoFocus
          />

          <button
            type="submit"
            disabled={productSalesSearchLoading}
            className="
              h-9 px-4
              bg-gray-900
              text-white
              rounded-lg
              text-[13px] font-medium
              hover:bg-black
              disabled:opacity-60
              transition
            "
          >
            {productSalesSearchLoading
              ? lang === "AR"
                ? "جاري البحث..."
                : "Searching..."
              : lang === "AR"
              ? "بحث"
              : "Search"}
          </button>
        </div>

        {productSalesSearchError && (
          <p className="text-[12px] text-red-600 mt-2">
            {productSalesSearchError}
          </p>
        )}
      </form>

      <div className="px-5 py-4 max-h-[430px] overflow-y-auto">
        {productSalesSearchResults.length === 0 ? (
          <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm">
            {productSalesSearchLoading
              ? lang === "AR"
                ? "جاري التحميل..."
                : "Loading..."
              : TEXT.noData}
          </div>
        ) : (
          <div className="space-y-2">
            {productSalesSearchResults.map((item) => (
              <div
                key={item.productId}
                className="
                  flex items-center gap-3
                  border border-gray-100
                  rounded-xl
                  p-3
                  hover:bg-gray-50
                  transition
                "
              >
                <img
                  src={item.image || "/images/placeholder-product.jpg"}
                  alt={lang === "AR" ? item.name_ar : item.name_en}
                  onError={(e) => {
                    e.currentTarget.src = "/images/placeholder-product.jpg";
                  }}
                  className="w-12 h-12 rounded-lg object-cover bg-gray-100 border border-gray-100"
                />

                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-gray-900 truncate">
                    {lang === "AR" ? item.name_ar : item.name_en}
                  </p>

                  <p className="text-[11px] text-gray-500 mt-0.5">
                    ID: {item.productId}
                    {item.publicId ? ` • ${item.publicId}` : ""}
                  </p>

                  <div className="flex items-center gap-4 mt-1 text-[11px] text-gray-500">
                    <span>
                      {lang === "AR" ? "الطلبات" : "Orders"}:{" "}
                      <strong className="text-gray-800">{item.orders}</strong>
                    </span>

                    <span>
                      {lang === "AR" ? "الوحدات" : "Units"}:{" "}
                      <strong className="text-gray-800">{item.unitsSold}</strong>
                    </span>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <p className="text-[11px] text-gray-500">
                    {lang === "AR" ? "إجمالي المبيعات" : "Total sales"}
                  </p>

                  <p className="text-[14px] font-semibold text-gray-900">
                    {formatChartMoney(item.totalSales)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
)}

        </main>

{showCustomCompareModal && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    
    <div className="bg-white w-[340px] rounded-xl shadow-xl border border-gray-200">


      {/* RANGE INPUTS */}
      <div className="flex items-center justify-center gap-2 px-5 py-4">

        <input
          readOnly
          value={compareStartDate ? compareStartDate.toLocaleDateString() : ""}
          className="
  w-full
  border border-gray-300
  px-2 py-1.5
  text-[13px]
  rounded-md
  bg-gray-100
  text-center
"
        />

        <span className="text-gray-400 text-sm font-medium">VS</span>

        <input
          readOnly
          value={compareEndDate ? compareEndDate.toLocaleDateString() : ""}
          className="
  w-full
  border border-gray-300
  px-2 py-1.5
  text-[13px]
  rounded-md
  bg-gray-100
  text-center
"
        />
      </div>

     <div className="px-4 pb-4">

  {/* MONTH HEADER WITH ARROWS */}
  <div className="flex items-center justify-between mb-3">

    <button
      type="button"
      onClick={() => setCalendarOffset((p) => p - 1)}
      className="
  w-7 h-7 flex items-center justify-center
  border border-gray-300
  rounded-md
  bg-white
  hover:bg-gray-100
  shadow-sm
"
    >
      ←
    </button>

    <div className="text-sm font-medium">
  {new Date(
    (calendarBaseDate || new Date()).getFullYear(),
    (calendarBaseDate || new Date()).getMonth() + calendarOffset,
    1
  ).toLocaleString(lang === "AR" ? "ar" : "en", {
    month: "long",
    year: "numeric",
  })}
</div>

    <button
      type="button"
      onClick={() => setCalendarOffset((p) => p + 1)}
      className="
  w-7 h-7 flex items-center justify-center
  border border-gray-300
  rounded-md
  bg-white
  hover:bg-gray-100
  shadow-sm
"
    >
      →
    </button>

  </div>

  <Calendar
  monthOffset={calendarOffset}
  selectedDate={selectedDate}
  calendarBaseDate={calendarBaseDate}
  setSelectedDate={setSelectedDate}
  rangeStart={compareStartDate}
  rangeEnd={compareEndDate}
  setRangeStart={setCompareStartDate}
  setRangeEnd={setCompareEndDate}
  setCalendarOffset={setCalendarOffset}
  isCompareCalendar={true}
/>

</div>

      {/* FOOTER */}
      <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
        
        <button
          onClick={() => setShowCustomCompareModal(false)}
          className="
  px-3 py-1.5
  text-[13px]
  border border-gray-300
  rounded-md
  hover:bg-gray-100
"
        >
          {TEXT.cancelBtn}
        </button>

        <button
onClick={() => {
  if (compareStartDate && compareEndDate) {
    const nextCompare = {
      start: compareStartDate,
      end: compareEndDate,
    };

    const start = mainStart || new Date();
    const end = mainEnd || start;

    // ✅ Prevent the cards useEffect from immediately reloading
    // with the old compareRange after selecting custom comparison.
    skipNextCardsAutoLoadRef.current = true;

    setCompareRange(nextCompare);
    setCompareType("custom");

    // ✅ This is the only load needed for custom comparison.
    loadCards(start, end, nextCompare);
  }

  setShowCustomCompareModal(false);
}}
          className="
  px-4 py-1.5
  text-[13px]
  bg-black
  text-white
  rounded-md
  hover:bg-gray-800
"
        >
          {TEXT.applyBtn}
        </button>

      </div>

    </div>
  </div>
)}

{showCurrencyModal && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    
    <div className="bg-white rounded-xl w-[360px] shadow-xl border border-gray-200">

      {/* HEADER */}
      <div className="px-5 py-4 border-b">
        <h2 className="text-[16px] font-semibold text-gray-900">
          {TEXT.setExchangeRate}
        </h2>
        <p className="text-[12px] text-gray-500 mt-1">
          {TEXT.usdToLbp}
        </p>
      </div>

      {/* BODY */}
      <div className="p-5">
        <input
          type="number"
          value={tempRate}
          onChange={(e) => setTempRate(e.target.value)}
          placeholder={TEXT.exchangePlaceholder}
          className="
            w-full
            border border-gray-300
            rounded-lg
            px-3 py-2
            text-sm
            focus:outline-none
            focus:ring-2
            focus:ring-black
          "
        />

        {/* helper */}
        <p className="text-[11px] text-gray-400 mt-2">
          {TEXT.exchangeHelper}
        </p>
      </div>

      {/* FOOTER */}
      <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
        
        <button
          onClick={() => {
            setShowCurrencyModal(false);
            setTempRate("");
          }}
          className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-100"
        >
          {TEXT.cancelBtn}
        </button>

        <button
          onClick={() => {
            if (!tempRate || Number(tempRate) <= 0) return;

            setExchangeRate(Number(tempRate));
            setCurrency("LBP"); // 👈 switch AFTER setting
            setShowCurrencyModal(false);
            setTempRate("");
          }}
          className="
            px-4 py-1.5
            text-sm
            bg-black
            text-white
            rounded-md
            hover:bg-gray-800
            transition
          "
        >
          {TEXT.saveBtn}
        </button>

      </div>

    </div>

  </div>
)}

{showCompareCalendar && (
  <div className="fixed inset-0 z-50 bg-black/30 sm:bg-transparent">
    <div className="flex min-h-[100dvh] items-end justify-center p-2 sm:min-h-0 sm:items-start sm:pt-20 sm:p-0">

      <div
        ref={modalRef}
        className="
          bg-white
          w-full max-w-[360px] sm:w-[720px] sm:max-w-none
          max-h-[calc(100dvh-16px)] sm:max-h-none
          rounded-t-2xl sm:rounded-lg
          border border-gray-200
          shadow-[0_8px_30px_rgba(0,0,0,0.12)]
          flex flex-col sm:flex-row
          overflow-hidden
        "
      >

      {/* LEFT PANEL */} 
      <div className="w-full sm:w-[220px] border-b sm:border-b-0 sm:border-r bg-[#ECECED] p-2 relative">

  {/* BACK BUTTON */}
  {dateView !== "main" && (
    <button
  type="button"
      onClick={() => setDateView("main")}
      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#E7E7E7] active:bg-[#DCDCDC] rounded-md transition"
    >
      ←
    </button>
  )}

  {/* MAIN MENU */}
  {dateView === "main" && (
    <>
    {[
  { key: "today", label: TEXT.todayLabel },
  { key: "yesterday", label: TEXT.yesterdayLabel },
  { type: "separator" },
  { key: "last", label: TEXT.lastLabel, next: "last" },
  { key: "quarters", label: TEXT.quartersLabel, next: "quarters" },
  { type: "separator" },
  { key: "custom", label:TEXT.customRangeLabel},
].map((item, index) => {
  if (item.type === "separator") {
    return (
      <div
        key={`sep-${index}`}
        className="my-2 border-t border-gray-300/70"
      />
    );
  }

  return (
    <button
  type="button"
      key={item.key}
      onClick={() => {
  if (item.next) {
    setDateView(item.next);
    return;
  }

  setDateTab(item.key);
  setActiveQuickRange(item.key); // ✅ ADD THIS

  if (item.key === "today") {
    const today = new Date();
    applyRange(today, today);
  }

  if (item.key === "yesterday") {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    applyRange(d, d);
  }

  if (item.key === "custom") {
  setMainStart(null);
  setMainEnd(null);

  setRangeStart(null); // ✅ ADD
  setRangeEnd(null);   // ✅ ADD

  setSelectedDate(null);
}
}}
      className={`
  group w-full flex justify-between items-center
  px-3 py-1.5 text-[13px]
  rounded-md
  transition-all duration-150
  whitespace-nowrap
  ${
    (item.key === "today" && activeQuickRange === "today") ||
    (item.key === "yesterday" && activeQuickRange === "yesterday") ||
    (item.key === "custom" && activeQuickRange === "custom") ||
    (item.key === "last" && activeQuickRange === "last") ||
    (item.key === "quarters" && activeQuickRange === "quarter")
      ? "bg-white border border-gray-300 shadow-sm font-medium"
      : "hover:bg-[#E7E7E7]"
  }
`}
    >
      {item.label}

      {item.next && (
        <span className="opacity-0 group-hover:opacity-100 transition">
          ›
        </span>
      )}
    </button>
  );
})}
    </>
  )}

  {/* LAST MENU */}
  {dateView === "last" && (
    <>
     {[
  { label: TEXT.last24h, days: 1 },
  { label: TEXT.last7d, days: 7 },
  { label: TEXT.last30d, days: 30 },
  { label: TEXT.last90d, days: 90 },
  { label: TEXT.lastYear, days: 365 },
].map((item, i) => (
  <button
  type="button"
    key={i}
    onClick={() => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - item.days + 1);

      applyRange(start, end);
setActiveQuickRange("last"); // ✅ IMPORTANT
    }}
    className="w-full text-left px-3 py-1.5 text-[12px] rounded-md hover:bg-[#E7E7E7] active:bg-[#DCDCDC] transition"
  >
    {item.label}
  </button>
))}
    </>
  )}

  {/* QUARTERS MENU */}
  {dateView === "quarters" && (
    <>
      {getLastQuarters().map((q, i) => (
  <button
    type="button"
    key={i}
    onClick={() => {
      applyRange(q.start, q.end);
      setActiveQuickRange("quarter");
    }}
    className="w-full text-left px-3 py-1.5 text-[12px] rounded-md hover:bg-[#E7E7E7] active:bg-[#DCDCDC] transition"
  >
    {q.label}
  </button>
))}
    </>
  )}

<div className="static sm:absolute sm:bottom-2 sm:left-0 w-full px-2 pt-3 mt-2 sm:mt-0 border-t bg-[#ECECED]">
  <button
    type="button"
    onClick={() => {
      resetDateModal();
    }}
    className="
  w-full text-left px-3 py-2
  text-[13px]
  text-gray-700
  rounded-md
  hover:bg-[#E7E7E7]
"
  >
   {TEXT.reset}
  </button>
</div>

</div>

      {/* RIGHT PANEL */}
      <div className="flex-1 p-3 sm:p-4 overflow-y-auto">

{/* DATE RANGE DISPLAY (ALWAYS VISIBLE) */}
<div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-4 w-full">


  {isSameDay ? (
  <div className="flex justify-center w-full">
    <input
      className="border px-3 py-2 rounded-md w-[150px] sm:w-[160px] text-center bg-gray-50 cursor-default"
      value={
        mainStart
          ? mainStart.toLocaleDateString(lang === "AR" ? "ar" : "en", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : ""
      }
      readOnly
    />
  </div>
  ) : (
    <>
      {/* FROM */}
      <input
        className="
  border border-gray-300
  px-2 py-1.5
  text-[13px]
  rounded-md
  bg-gray-50
  text-center
  w-[120px] sm:w-[140px]
"
        value={
          mainStart
            ? mainStart.toLocaleDateString(lang === "AR" ? "ar" : "en", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : ""
        }
        readOnly
      />

      <span className="text-gray-400">→</span>

      {/* TO */}
      <input
        className="
  border border-gray-300
  px-2 py-1.5
  text-[13px]
  rounded-md
  bg-gray-50
  text-center
  w-[120px] sm:w-[140px]
"
        value={
          mainEnd
            ? mainEnd.toLocaleDateString(lang === "AR" ? "ar" : "en", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : ""
        }
        readOnly
      />
    </>
  )}

</div>

<div className="flex justify-between items-center mb-3 px-2">

  {/* LEFT ARROW */}
  <button
  type="button"
    onClick={() => setCalendarOffset((p) => p - 1)}
    className="
  w-7 h-7
flex items-center justify-center
border border-gray-300
rounded-md
bg-white
hover:bg-gray-100
text-sm
"
  >
    ←
  </button>


  {/* RIGHT ARROW */}
  <button
  type="button"
    onClick={() => setCalendarOffset((p) => p + 1)}
    className="
  w-7 h-7
flex items-center justify-center
border border-gray-300
rounded-md
bg-white
hover:bg-gray-100
text-sm
"
  >
    →
  </button>

</div>

  <div className="flex gap-6 flex-col">

  {/* MONTH LABELS */}
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-2">

    {/* LEFT MONTH */}
<div className="hidden sm:block w-[220px] text-center text-sm font-medium mx-auto">
  {new Date(
    (calendarBaseDate || new Date()).getFullYear(),
    (calendarBaseDate || new Date()).getMonth() + (calendarOffset - 1),
    1
  ).toLocaleString(lang === "AR" ? "ar" : "en", {
    month: "long",
    year: "numeric",
  })}
</div>

    {/* RIGHT MONTH */}
<div className="w-[220px] text-center text-sm font-medium mx-auto">
  {new Date(
    (calendarBaseDate || new Date()).getFullYear(),
    (calendarBaseDate || new Date()).getMonth() + calendarOffset,
    1
  ).toLocaleString(lang === "AR" ? "ar" : "en", {
    month: "long",
    year: "numeric",
  })}
</div>

  </div>

  {/* CALENDARS */}
<div className="grid grid-cols-1 sm:grid-cols-[220px_220px] gap-4 justify-center justify-items-center">
  <div className="hidden sm:block w-[220px]">
  <Calendar
  compact
  monthOffset={calendarOffset - 1}
  selectedDate={selectedDate}
  calendarBaseDate={calendarBaseDate}
  setSelectedDate={setSelectedDate}
  rangeStart={mainStart}
  rangeEnd={mainEnd}
  setRangeStart={setMainStart}
  setRangeEnd={setMainEnd}
  setCalendarOffset={setCalendarOffset}
  activeQuickRange={activeQuickRange}
  setActiveQuickRange={setActiveQuickRange}
/>
</div>

    <div className="w-[220px]">
  <Calendar
  compact
  monthOffset={calendarOffset}
  selectedDate={selectedDate}
  calendarBaseDate={calendarBaseDate}
  setSelectedDate={setSelectedDate}
  rangeStart={mainStart}
  rangeEnd={mainEnd}
  setRangeStart={setMainStart}
  setRangeEnd={setMainEnd}
  setCalendarOffset={setCalendarOffset}
  activeQuickRange={activeQuickRange}
  setActiveQuickRange={setActiveQuickRange}
/>
</div>
  </div>

</div>

  {/* ACTIONS */}
  <div className="mt-5 pt-4 border-t border-gray-200">
  <div className="flex justify-end gap-2">
    <button
  type="button"
  onClick={() => {
    setShowCompareCalendar(false); // ✅ ONLY CLOSE
  }}
      className="
  px-3 py-1.5
  text-[13px]
  border border-gray-300
  rounded-md
  hover:bg-gray-100
"
    >
      {TEXT.cancelBtn}
    </button>

    <button
  type="button"
      onClick={() => {
  if (mainStart && mainEnd) {
  setMainRange({
    start: mainStart,
    end: mainEnd,
  });
}

  setShowCompareCalendar(false);
}}
      className="
  px-4 py-1.5
  text-[13px]
  bg-black
  text-white
  rounded-md
  hover:bg-gray-800
"
    >
      {TEXT.applyBtn}
    </button>
  </div>
</div>
</div>
</div>
    </div>
  </div>
)}

   {showExport && (
  <div
    onMouseDown={() => setShowExport(false)}
    className="analytics-export-no-print fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4"
  >
    <div
      onMouseDown={(e) => e.stopPropagation()}
      className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="px-5 py-4 border-b border-gray-100 bg-[#f6f6f7]">
        <h3 className="text-[15px] font-semibold text-gray-900">
          {TEXT.chooseExportFormat}
        </h3>

        <p className="text-[12px] text-gray-500 mt-1">
          {TEXT.exportFullPageDescription}
        </p>
      </div>

      <div className="p-3 space-y-2">
        <button
          type="button"
          onClick={() => handleAnalyticsFullPageExport("pdf")}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition text-left"
        >
          <div>
            <p className="text-[14px] font-semibold text-gray-900">
              {TEXT.pdf}
            </p>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {lang === "AR"
                ? "فتح الصفحة كاملة لحفظها كملف PDF"
                : "Open the full page and save it as PDF"}
            </p>
          </div>

          <span className="text-gray-400">›</span>
        </button>

        <button
          type="button"
          onClick={() => handleAnalyticsFullPageExport("print")}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition text-left"
        >
          <div>
            <p className="text-[14px] font-semibold text-gray-900">
              {TEXT.print}
            </p>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {lang === "AR"
                ? "طباعة صفحة التحليلات كاملة"
                : "Print the full analytics page"}
            </p>
          </div>

          <span className="text-gray-400">›</span>
        </button>
      </div>

      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
        <button
          type="button"
          onClick={() => setShowExport(false)}
          className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm hover:bg-gray-50"
        >
          {TEXT.cancel}
        </button>
      </div>
    </div>
  </div>
)}


      </div>
      
    </div>
  );
}

export default AdminAnalytics;
